import './surfaces/sales-surface.css';
import type {
  OrgMetadata,
  ObjectMeta,
  FieldMeta,
  ValueOption,
  ListViewMeta,
  ListViewFilter,
  ListViewDerivedColumn,
  PageMeta,
  RelatedListMeta,
  RecordActionMeta,
  StandardVerb,
  LayoutSection,
} from './metadata-source.js';
import {
  richTextToolbarFor,
  bareFieldName,
  objectForRecord,
  projectOneListView,
  type ActionRequirementMeta,
} from './metadata-source.js';
// CAOS-1861 — the client for the Engine's `listViews` noun, which has answered these six verbs
// since CAOS-1579 and until now had no caller in the host at all.
import {
  listViewsFor,
  saveView,
  cloneView,
  deleteView,
  viewKeyFor,
} from './list-views.js';
import { makeIcon, isIconName, richTextToPlainText } from '@caos/ui';
import { fillPath, rowMatches } from './predicate.js';
import { componentDrawFailure, pageNotice, type PageRenderOptions } from './surfaces/page-surface.js';
import { stageFieldFor, groupFieldsFor } from './surfaces/list-modes.js';
import { derivedFromId } from './surfaces/submission-ref.js';
import {
  renderCollection,
  type CollectionRow,
  type CollectionSpec,
  type CollectionView,
} from './surfaces/collection-surface.js';
import { renderPageSurface, renderInstalledComponent } from './surfaces/page-surface.js';
import { renderRosterDestination } from './surfaces/roster-destination.js';
import { OPEN_RECORD_EVENT, announceOpenRecord } from './open-record-event.js';
import { parseTemplate, renderTemplate, SHORT_ID_LENGTH } from './name-template.js';
import type { ShellFramePlan, ShellNavTab } from './kernel-contract.js';
import { recordAddress, listAddress, recordDestination, recordHomesOf, type Destination } from './record-address.js';
import { landingTabKey } from './shell-wiring.js';
import {
  NO_WRITE,
  controlValue,
  createRecord,
  editableFields,
  isEditable,
  loadObjectPermissions,
  recordVersion,
  updateRecord,
  wireValue,
  type ObjectPermissions,
} from './record-write.js';
// CAOS-366 — the tag surface reads and writes entirely through the public Data API; see tags.ts.
import {
  loadRecordTags,
  loadTaggedRecords,
  findOrCreateTag,
  attachTag,
  type RecordTag,
  type ResolvedTags,
} from './tags.js';

/**
 * CAOS-306 — the record-page + list-view CONTENT source for the shell frame.
 *
 * The boot decision + `enrichShellPlan` give the host the installed apps and their object tabs, and
 * `metadata-source` gives the OBJECT CATALOG (fields, layout sections, list columns, value sets).
 * This module joins that metadata with the org's REAL DATA (the kernel Data API) and renders the two
 * content surfaces a Sales app needs:
 *   • a LIST view — `/sales/accounts` → a `<caos-data-table>` of the object's real rows, the name
 *     column linking into the record, reference columns linking to their target record, and Select
 *     columns rendered as `<caos-status-pill>` badges;
 *   • a RECORD page — `/sales/accounts/:id` → a `<caos-record-header>` + the page-layout sections as
 *     `<caos-detail-section>`s of `<caos-key-value-row>`s carrying the record's real, formatted field
 *     values (currency/date/number formatted by the server display block, lookups resolved to names).
 *
 * Everything is same-origin and fail-soft: a Data API hiccup renders an honest empty/So-far state,
 * never a crash. The rendered node is handed to the shell frame as its `content` (the frame appends
 * a `Node` content region verbatim), so this module owns the pixels of both surfaces.
 */

/** A record as the Data API serializes it: `id` + one bare-keyed canonical value per field, + optional display. */
export type ApiRecord = Record<string, unknown> & { id: string; display?: Record<string, unknown> };

/** A money value on the wire (a currency Number field). */
interface MoneyValue {
  amount: string;
  currency: string | null;
}

const DATA_QUERY_PATH = '/api/data/query';
const DATA_GET_PATH = '/api/data/get';
// CAOS-367 — the merged read of what people DID about a record (note/task/call log).
const ACTIVITY_TIMELINE_PATH = '/api/activity/timeline';
// CAOS-402 — the record's own field-change history. The capture (CAOS-1658), both visibility
// planes (CAOS-515) and retention (CAOS-1662) already exist, and the route has been mounted all
// along; nothing in the workspace consumed it, so no screen had ever shown a person one row.
const RECORD_HISTORY_PATH = '/api/history/list';

/** The injectable fetch seam (production = the real same-origin `fetch`; tests supply a stub). */
export type FetchImpl = typeof fetch;

/**
 * The outcome of a list read: the rows on success, or an explicit `error`. CAOS-331 — a list tab must
 * tell an empty object (render the columns-present empty table) APART from a failed read (render an
 * honest error state); a fail-soft `[]` collapses those two, so the streaming surface reads the
 * outcome rather than the swallowed array.
 */
export type QueryOutcome =
  | {
      status: 'ok';
      records: ApiRecord[];
      /**
       * CAOS-363 — where the NEXT page starts, or null when this was the last of them. Without it a
       * list stops at a fixed number of rows and says nothing, which reads exactly like "that is all
       * there is" — the most confident way a screen can be wrong.
       */
      nextCursor: string | null;
    }
  | { status: 'error' };

/**
 * CAOS-349 — the narrowing + ordering a saved list view asks for, as the query carries it. Absent
 * ⇒ every visible row in the default order, exactly as before.
 */
/**
 * CAOS-1565 — one condition on a read, as the Data API takes it: a single `{ field, op, value }`, or
 * an `any` GROUP matching the rows that satisfy at least one of several. The entries of a `filter`
 * array AND together, so a search over two fields cannot be two entries — that asks for the rows
 * where BOTH contain the term. The group is what expresses one question over several fields, and the
 * Engine resolves it server-side: the rows on screen are one page of the set, so narrowing them here
 * would search two hundred records and call the answer "no matches".
 */
export type QueryCondition = ListViewFilter | { readonly any: readonly ListViewFilter[] };

/**
 * CAOS-1877 — can a list offer to order by a field of this storage type? One definition, because
 * two generators of the same control already answered it two different ways and both were wrong.
 *
 * ── WHY THE HOST DECIDES THIS AT ALL ────────────────────────────────────────────────────────────
 * The Engine is the authority: `sortColumn()` in `kernel/src/physical/physical-record-store.ts` is
 * what actually refuses. The host cannot import it, so the rule is restated here — and the deploy
 * gate `listViewOrderingFindings` in `kernel/src/deploy/validate.ts` restates it a third time for
 * an AUTHORED list. A type added to any one of the three has to be added to the others; each of
 * them names the other two so the next reader finds all three rather than one.
 *
 * ── THE THREE THAT HAVE NO ORDER ────────────────────────────────────────────────────────────────
 *   Formula      — computed on read. No stored column exists, so `physicalModel` never gives it a
 *                  `ColumnInfo` at all and a sort over one is refused upstream of everything else.
 *   Reference    — its stored column holds the RELATED record's id, so ordering by it orders by a
 *                  `gen_random_uuid()`. It used to run and return that order silently; CAOS-1877
 *                  refuses it.
 *   Geolocation  — refused in the store since CAOS-620, whose own comment is the argument: PostGIS
 *                  gives geography a btree opclass for bookkeeping, so the ordering would be
 *                  derived from a bounding box — "stable, meaningless, and impossible for a person
 *                  to predict".
 *
 * ── AND THE ONE THAT DOES, WHICH IS THE EASIER MISTAKE ──────────────────────────────────────────
 * A ROLLUP IS ORDERABLE. It has no plain stored column either, so a rule written as "only order by
 * real columns" drops it — and one of the two loops did exactly that. `sortColumn()` carries a
 * dedicated branch for a rollup (a correlated subquery over `caos.rollup_value`, CAOS-576), the
 * keyset pager carries its boundary value, and 20 of the 159 orderings authored across the package
 * shelf are over one. Refusing it hides a working ordering, which is the mirror of the defect this
 * ticket is about rather than a safe direction to err in.
 *
 * Everything not named here is orderable, including the system stamps — "newest first" is the
 * ordering people ask for most, and `created_at` / `updated_at` are legal sort columns.
 *
 * ── LEFT OPEN DELIBERATELY, AND SAID SO RATHER THAN LEFT TO BE FOUND ───────────────────────────
 * `JSON` and `File` are admitted here because the Engine admits them: `sortColumn()` refuses only
 * the three above, so a jsonb or file column orders by whatever btree order Postgres gives it.
 * Whether that is meaningful to a person is the same question this ticket asked about a reference,
 * and the honest answer is that nobody has measured it. It is NOT changed here, because the
 * corpus authors no ordering over either (0 of 159) so the change would be unmeasurable either
 * way, and because quietly widening a refusal past what the ticket investigated is how a fix
 * becomes a regression. `Overridable` is admitted for a different reason entirely: by the time a
 * storage type reaches a `ColumnInfo` the physical model has already replaced it with the
 * computed side's LEAF type, so this function never sees the literal string.
 */
export function canOrderBy(storageType: string): boolean {
  return storageType !== 'Formula' && storageType !== 'Reference' && storageType !== 'Geolocation';
}

export interface QueryView {
  readonly filters?: readonly QueryCondition[];
  readonly sort?: { readonly field: string; readonly dir: 'asc' | 'desc' };
  /** CAOS-363 — continue from where the previous page ended. */
  readonly cursor?: string;
}

/** Query all readable rows, distinguishing a genuine empty result from a transport/parse failure. */
export async function queryRecordsOutcome(
  object: string,
  fetchImpl: FetchImpl = fetch,
  limit = 200,
  view?: QueryView,
): Promise<QueryOutcome> {
  try {
    const narrowing =
      view?.filters && view.filters.length > 0
        ? `&filter=${encodeURIComponent(JSON.stringify(view.filters))}`
        : '';
    const ordering = view?.sort
      ? `&sort=${encodeURIComponent(view.sort.field)}&dir=${view.sort.dir}`
      : '';
    const from = view?.cursor ? `&cursor=${encodeURIComponent(view.cursor)}` : '';
    const res = await fetchImpl(
      `${DATA_QUERY_PATH}?object=${encodeURIComponent(object)}&limit=${limit}&display=true${narrowing}${ordering}${from}`,
      { method: 'GET', headers: { accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' },
    );
    const env = (await res.json()) as { ok?: boolean; data?: { records?: ApiRecord[]; nextCursor?: string | null } };
    if (env?.ok === true && Array.isArray(env.data?.records)) {
      return {
        status: 'ok',
        records: env.data!.records!,
        nextCursor: typeof env.data!.nextCursor === 'string' ? env.data!.nextCursor : null,
      };
    }
    return { status: 'error' };
  } catch {
    return { status: 'error' };
  }
}

/** Query all readable rows of an object (fail-soft → []). `display=true` adds server-formatted strings. */
export async function queryRecords(
  object: string,
  fetchImpl: FetchImpl = fetch,
  limit = 200,
): Promise<ApiRecord[]> {
  const outcome = await queryRecordsOutcome(object, fetchImpl, limit);
  return outcome.status === 'ok' ? outcome.records : [];
}

/** The outcome of a single-record read: the record (or `null` when genuinely absent), or an explicit `error`. */
export type GetOutcome = { status: 'ok'; record: ApiRecord | null } | { status: 'error' };

/** Fetch ONE record by id, distinguishing a not-found record from a failed read. */
export async function getRecordOutcome(
  object: string,
  id: string,
  fetchImpl: FetchImpl = fetch,
): Promise<GetOutcome> {
  try {
    const res = await fetchImpl(
      `${DATA_GET_PATH}?object=${encodeURIComponent(object)}&id=${encodeURIComponent(id)}&display=true`,
      { method: 'GET', headers: { accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' },
    );
    const env = (await res.json()) as { ok?: boolean; data?: { record?: ApiRecord } };
    if (env?.ok === true) return { status: 'ok', record: env.data?.record ?? null };
    return { status: 'error' };
  } catch {
    return { status: 'error' };
  }
}

/** Fetch ONE record by id (fail-soft → null). */
export async function getRecord(
  object: string,
  id: string,
  fetchImpl: FetchImpl = fetch,
): Promise<ApiRecord | null> {
  const outcome = await getRecordOutcome(object, id, fetchImpl);
  return outcome.status === 'ok' ? outcome.record : null;
}

/**
 * Evaluate a text Formula expression against a record, client-side. Handles the common template form
 * `` `${record.first_name} ${record.last_name}` `` — substituting `${record.<field>}` / `${<field>}`
 * tokens with the record's values (blanks dropped). The Data API does not compute Formula fields on
 * read, so a name field like `contact.name` arrives empty; this resolves it from its real parts.
 */
export function evalTextFormula(expr: string, record: ApiRecord): string {
  const body = expr.trim().replace(/^`|`$/g, '');
  const out = body.replace(/\$\{\s*(?:record\.)?([a-zA-Z_][\w]*)\s*\}/g, (_m, field: string) => {
    const v = record[field];
    return v == null ? '' : String(v);
  });
  return out.replace(/\s+/g, ' ').trim();
}

/** The value of a field, resolving a text Formula from its parts (the Data API leaves Formulas empty). */
function fieldRawValue(field: FieldMeta, record: ApiRecord): unknown {
  const raw = record[field.apiName];
  if ((raw == null || raw === '') && field.storageType === 'Formula' && field.formula) {
    const computed = evalTextFormula(field.formula, record);
    return computed === '' ? null : computed;
  }
  return raw;
}

/**
 * The leading characters of a record id, which is how a record identifies itself when it has no
 * readable name (CAOS-1585). One function because three places show it — the blank-name fallback,
 * an open tab's identifier, and the `{id}` template token — and they are only interchangeable while
 * they agree. They were written separately and already disagreed on a null id.
 */
function shortId(record: ApiRecord): string {
  return String(record.id ?? '').slice(0, SHORT_ID_LENGTH);
}

/**
 * The value one `nameTemplate` token resolves to on this record (CAOS-1585).
 *
 * The reserved names are answered first and deliberately: `{id}` is the record's short id and
 * `{label}` the object's translated label, neither of which is a field, and an object that also
 * declares a field called `label` is refused at deploy rather than arbitrated here.
 *
 * A field token goes through {@link fieldRawValue}, the same reader the default uses, so a text
 * Formula naming this field resolves from its parts instead of arriving empty — a title built from
 * a Formula field would otherwise be blank on every record for a reason nothing in the template
 * explains. A token naming a field the object does not have yields nothing; the deploy gate
 * refuses that template, so reaching here means something got past it, and a missing piece of a
 * title is a better answer than a heading with `{nonesuch}` printed in it.
 */
function nameTemplateValue(meta: ObjectMeta, record: ApiRecord, token: string): string {
  if (token === 'id') return shortId(record);
  if (token === 'label') return meta.label;
  const field = meta.fields.get(token);
  const raw = field ? fieldRawValue(field, record) : record[token];
  return raw == null ? '' : String(raw).trim();
}

/**
 * CAOS-1585 — the title a record carries: what its object SAYS it is called, or the default that
 * says the same thing for an object that has not said anything.
 *
 * The object may declare a `nameTemplate` over its own fields. When it does, that is the answer.
 * When it does not — which is every object authored before this existed — the two rules below
 * apply, unchanged, and they are exactly what a template expresses:
 *
 *     an auto-number name field   ⇒  `{label} {<nameField>}`   ("Order 13502" for a stored 13502)
 *     a name that reads blank     ⇒  `{label} {id}`            ("Account 3f9c1a02")
 *     anything else               ⇒  `{<nameField>}`
 *
 * Until this ticket those three were decisions the RENDERER made, and an org could neither decline
 * them, reorder them, nor translate them: they assume English word order and assume the label reads
 * well in front of a number. Keeping them as the default rather than writing a template onto every
 * object is what makes this safe to ship — nothing in any org moves until somebody authors one.
 *
 * A template that resolves ENTIRELY blank falls back to the default rather than returning nothing.
 * Something has to be drawn at the top of a record, and an author whose every token happened to be
 * empty on one record has not asked for a heading-less record; they have hit a row the template did
 * not anticipate, which is the case the id fallback already exists to cover.
 */
export function recordDisplayName(meta: ObjectMeta, record: ApiRecord): string {
  if (meta.nameTemplate !== undefined) {
    const parts = parseTemplate(meta.nameTemplate);
    if (parts) {
      const titled = renderTemplate(parts, (token) => nameTemplateValue(meta, record, token));
      if (titled !== null) return titled;
    }
  }
  const nameField = meta.fields.get(meta.nameField);
  const raw = nameField ? fieldRawValue(nameField, record) : record[meta.nameField];
  const text = raw == null ? '' : String(raw).trim();
  if (text === '') return `${meta.label} ${shortId(record)}`;
  // Auto-number name fields read as a bare number — prefix the object label ("Order 6", "Case 4").
  if (nameField?.storageType === 'AutoNumber') return `${meta.label} ${text}`;
  return text;
}

/**
 * CAOS-386 — the record's BARE identifying value, never re-prefixed with the object label. This is
 * what an open-record tab pairs with the object's short code ("ORD · 13502"): {@link recordDisplayName}
 * deliberately spells "Order 13502" out for record headings/related lists/reference chips, which would
 * double up with the tab's own short code ("ORD · Order 13502") — exactly the bug CAOS-386 retires.
 *
 * CAOS-1585 — `nameTemplate` DELIBERATELY DOES NOT APPLY HERE, and this is a decision rather than a
 * place the wiring stopped. A tab already states the object, in the short code beside this value;
 * the heading does not, which is why the heading spells it out. Running a template here would let
 * an ordinary, correct template — one that names the object, which is exactly what a heading should
 * do — reintroduce "ORD · Order 13502" through the front door. The tab's job is to identify one
 * record among several open ones of the same kind, and the name field is what distinguishes them.
 *
 * An object whose records are genuinely identified by something other than `nameField` says so by
 * naming that field as its `nameField`, which this already reads. What this does not offer is a
 * SEPARATE tab template, and if one is ever wanted it is its own field with its own default, not a
 * second reading of this one.
 */
export function recordTabIdentifier(meta: ObjectMeta, record: ApiRecord): string {
  const nameField = meta.fields.get(meta.nameField);
  const raw = nameField ? fieldRawValue(nameField, record) : record[meta.nameField];
  const text = raw == null ? '' : String(raw).trim();
  return text === '' ? shortId(record) : text;
}

/**
 * CAOS-386 — a short, readable tab code for an object that has not been authored one, so a record
 * tab never regresses to spelling the object out. Multi-word labels ("Purchase Order") take initials
 * ("PO"); a single word takes its first letter plus up to three following consonants ("Project" →
 * "PRJC"), which reads closer to a hand-picked abbreviation than a blunt substring would. Always
 * within the same 8-character ceiling `tabCode` itself is authored under.
 */
export function fallbackTabCode(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) {
    return words
      .map((w) => w[0]!.toUpperCase())
      .join('')
      .slice(0, 8);
  }
  const word = words[0] ?? '';
  const first = word.slice(0, 1).toUpperCase();
  const rest = word
    .slice(1)
    .replace(/[aeiouAEIOU]/g, '')
    .toUpperCase()
    .slice(0, 3);
  return (first + rest) || 'REC';
}

/** id → { name, object } for every record referenced by `object`'s Reference fields (one query per target object). */
export async function buildReferenceIndex(
  meta: ObjectMeta,
  catalog: ReadonlyMap<string, ObjectMeta>,
  fetchImpl: FetchImpl = fetch,
): Promise<Map<string, { name: string; object: string }>> {
  const targets = new Set<string>();
  for (const f of meta.fields.values()) {
    if (f.storageType === 'Reference' && f.referenceTo) targets.add(f.referenceTo);
  }
  const index = new Map<string, { name: string; object: string }>();
  await Promise.all(
    [...targets].map(async (targetKey) => {
      const targetMeta = catalog.get(targetKey);
      if (!targetMeta) return;
      const rows = await queryRecords(targetKey, fetchImpl);
      for (const r of rows) index.set(String(r.id), { name: recordDisplayName(targetMeta, r), object: targetKey });
    }),
  );
  return index;
}

/** Map a target object key → the active app's tab key that opens it (for reference links). */
/**
 * Where each object's records can be reached from HERE: the authored home when there is one, and
 * otherwise the current app's tab for the object.
 *
 * The fallback keeps the incumbent guess exactly, LAST matching tab included — an org that has
 * authored no home keeps the links it has today. The authored entries are applied afterwards so
 * they win, which is the whole point of authoring one.
 */
export function objectHomeMap(
  tabs: readonly ShellNavTab[],
  appKey: string,
  homes?: ReadonlyMap<string, Destination>,
): Map<string, Destination> {
  const map = new Map<string, Destination>();
  for (const t of tabs) if (t.object) map.set(t.object, { appKey, tabKey: t.key });
  if (homes) for (const [object, dest] of homes) map.set(object, dest);
  return map;
}

/** A resolved, formatted field value: display text, and (for a reference) an optional record href. */
export interface FormattedValue {
  text: string;
  href?: string;
  /** A Select value's badge intent, when the field is value-set backed. */
  intent?: string;
}

/**
 * Format one field's value for display. Prefers the server-rendered `display` string for
 * currency/date/number; resolves a Reference to the target record's name (+ an in-app href); reads a
 * Select value's badge intent from the value set. An empty value yields empty text (→ an em dash).
 */
export function formatValue(
  field: FieldMeta,
  record: ApiRecord,
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  valueSets: OrgMetadata['valueSets'],
  objectHome: ReadonlyMap<string, Destination>,
): FormattedValue {
  const raw = fieldRawValue(field, record);
  if (raw == null || raw === '') return { text: '' };

  if (field.storageType === 'Reference') {
    const ref = refIndex.get(String(raw));
    const name = ref?.name ?? '';
    const tab = ref ? objectHome.get(ref.object) : field.referenceTo ? objectHome.get(field.referenceTo) : undefined;
    const href = tab === undefined ? undefined : recordAddress(tab, String(raw));
    return { text: name || String(raw), href };
  }

  if (field.valueSet) {
    const option = valueSets.get(field.valueSet)?.get(String(raw));
    return { text: option?.label ?? String(raw), intent: option?.intent };
  }

  // CAOS-595 — a rich value in a place with room for one line shows as its words, never as its markup.
  if (field.storageType === 'Text' && field.format === 'rich') {
    return { text: richTextToPlainText(raw).replace(/\s*\n\s*/g, ' ') };
  }

  // Server-formatted display string (currency, date, grouped number) when present.
  const disp = record.display?.[field.apiName];
  if (typeof disp === 'string' && disp !== '') {
    return { text: field.format === 'percent' ? `${disp}%` : disp };
  }

  // Currency object with no ISO code (no display) → the bare amount.
  if (field.storageType === 'Number' && typeof raw === 'object') {
    const money = raw as MoneyValue;
    if (typeof money.amount === 'string') return { text: money.amount };
  }

  return { text: field.format === 'percent' ? `${String(raw)}%` : String(raw) };
}

/** Build a status badge for a value-set value with a known intent (or plain text otherwise). */
function badge(value: FormattedValue): Node {
  if (!value.intent) return document.createTextNode(value.text);
  const pill = document.createElement('caos-status-pill');
  pill.setAttribute('intent', value.intent);
  pill.textContent = value.text;
  return pill;
}

/** An in-app link that routes through the navigate seam (and keeps a real href for a11y / new-tab). */
function recordLink(text: string, href: string, navigate?: (href: string) => void): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'sa-link';
  a.href = href;
  a.textContent = text;
  a.addEventListener('click', (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigate?.(href);
  });
  return a;
}

/** What a rendered surface needs from its context (metadata + route + navigate seam). */
export interface SurfaceContext {
  readonly meta: OrgMetadata;
  /**
   * CAOS-561 — how a piece is DRAWN here, when this surface has anything to say about it: the same
   * two injection points {@link PageRenderOptions} already carries, forwarded rather than re-
   * invented. Absent ⇒ the browser's own element registry and a real dynamic import, as in
   * production.
   *
   * They are on the context and not passed at the call site because TWO things read them and must
   * agree: `openComponentAction`, which draws the piece, and `brokenReplacement`, which asks in
   * advance whether it could be drawn. A surface that answered the question with one registry and
   * drew with another would decide a verb was replaceable on evidence the drawing never saw.
   */
  readonly componentDrawing?: Pick<PageRenderOptions, 'isTagRegistered' | 'loadComponentModule'>;
  /**
   * CAOS-1809 — who is READING this surface, keyed as a placed component's inputs name them, so an
   * input declaring `source: "userContext"` can be answered. Forwarded to every `renderPage` call
   * this module makes and to the record-action draw, for the reason `componentDrawing` above is on
   * the context rather than the call site: a piece placed on a page and the same piece opened by a
   * record action must not disagree about who is signed in.
   *
   * Built by `viewerInputContext` from the shell runtime's own viewer and passed down; absent ⇒ the
   * inputs are left off the element, which is the no-session state.
   */
  readonly viewer?: Readonly<Record<string, string>>;
  readonly appKey: string;
  readonly appLabel: string;
  readonly tabKey: string;
  readonly tabs: readonly ShellNavTab[];
  /**
   * CAOS-1586 — the AUTHORED record homes, across every installed app.
   *
   * `tabs` above is only the current app's, which is why the host could not previously produce a
   * cross-app answer: reading an account from the service app addressed it through whatever service
   * tab happened to name `account`. This is built from the whole plan, so a tab that declared
   * `recordHome` decides the address from anywhere. Absent ⇒ nothing is authored and every address
   * falls back to the current app's tabs, exactly as before.
   */
  readonly recordHomes?: ReadonlyMap<string, Destination>;
  readonly navigate?: (href: string) => void;
  /**
   * CAOS-345 — the SAVED LIST VIEW the surface opens on, when one is asked for or the tab declares a
   * default. It supplies the columns and the sort; absent ⇒ the object's own default columns stand,
   * exactly as before.
   */
  readonly listView?: ListViewMeta;
  /**
   * CAOS-1767 — TRUE when this surface must not offer the saved-view chooser. Absent ⇒ offered,
   * which is every caller that existed before this.
   *
   * It is a separate fact from {@link tabKey} and that is the whole reason it exists. A review of
   * CAOS-1767 caught the first attempt gating the chooser on `ctx.tabKey`, reasoning that a page is
   * drawn in no tab — but a `list` page resolves `tabKey` from the listed object's AUTHORED RECORD
   * HOME so its rows can link to records, so the tab key is populated for exactly the common case.
   * The chooser then appeared and, because picking a view is a NAVIGATION to `listAddress`, took the
   * reader off the page into that object's home tab in another app. That is worse than the `//?view=`
   * address the gate was added to prevent: it is a plausible-looking screen change nobody asked for.
   *
   * The two questions are "where do this list's RECORDS live" and "does THIS SURFACE have a list
   * address of its own", and only the caller can answer the second. A page has none — it has no
   * `view` parameter — so it withholds the chooser and says so here rather than being inferred from
   * a field that means something else.
   */
  readonly savedViewsWithheld?: boolean;
  /**
   * CAOS-360 — what THIS person may do here. Absent ⇒ nothing: a surface that was not told stays
   * read-only rather than guessing, because a control offered and then refused is worse than one
   * never offered.
   */
  readonly perms?: ObjectPermissions;
  /**
   * CAOS-1624 — the system permissions the VIEWER holds, from `auth whoami` (already resolved by
   * the kernel: the `platform.root` capstone expanded, an impersonated session intersected).
   *
   * It is deliberately not folded into `perms`, which is a different question with a different
   * answer: `perms` is what may be WRITTEN to this object, and its own note on `searchFieldsFor`
   * records that it says nothing about what may be read. This is org-wide and about the person.
   *
   * ABSENT ⇒ NOT KNOWN, which keeps every scoped filter applied. See {@link readerFilters}.
   */
  readonly heldPermissions?: readonly string[];
  /**
   * CAOS-560 — what this person may do on the OTHER objects this record's actions reach: the target
   * of a `create`, and every object a `component` action declares it needs. Keyed by object key.
   *
   * Separate from {@link perms}, which is this surface's own object, because an action can point
   * anywhere and "an action that requires something the person cannot do is not offered" cannot be
   * answered from one object's permissions. Resolved before the surface renders, since the action
   * bar is built synchronously and a button that appears and then disappears is worse than one that
   * was never drawn.
   *
   * An object MISSING from the map is not permitted — the same rule `perms` itself follows: a
   * surface that was not told stays closed rather than guessing.
   */
  readonly actionPerms?: ReadonlyMap<string, ObjectPermissions>;
  /** The fetch a write goes through (production = the real same-origin fetch; tests supply a stub). */
  readonly fetchImpl?: FetchImpl;
  /**
   * CAOS-583 — the route this surface is open at, so its header can offer an inline affordance to
   * this screen's own help topic when one is anchored to it. Absent ⇒ falls back to
   * `location.pathname`, which is already correct for a real navigation.
   */
  readonly pathname?: string;
}

/**
 * CAOS-360 — say what happened, once, where the person is looking. A save that succeeds says so
 * briefly; a save that fails says why and stays until it is dismissed, because a failure somebody
 * missed is a change they think they made.
 */
// CAOS-368 — `warning` joins the two that were here, because a lost save is neither of them. It is
// not `success`, and it is not `danger`: nothing is broken and nothing was corrupted — the record
// simply moved on, and the next step is to look at what it now says. `caos-toast` already draws the
// intent (it carries its own accent and icon for it); this is the host agreeing to use it.
export function raiseToast(message: string, intent: 'success' | 'warning' | 'danger'): void {
  let stack = document.querySelector('caos-toast-stack.sa-toasts');
  if (!stack) {
    stack = document.createElement('caos-toast-stack');
    stack.className = 'sa-toasts';
    stack.setAttribute('float', '');
    stack.setAttribute('position', 'bottom-right');
    document.body.append(stack);
  }
  const toast = document.createElement('caos-toast');
  toast.setAttribute('intent', intent);
  // A success is transient; a failure waits to be read.
  if (intent === 'success') toast.setAttribute('duration', '3200');
  toast.textContent = message;
  toast.addEventListener('caos-toast-dismiss', () => toast.remove());
  stack.append(toast);
}

/**
 * The picklist options for a Select field, as the value set declares them. Retired values are still
 * resolved for DISPLAY elsewhere; here they are the OFFER list, so a value nobody should choose
 * again is not offered again.
 */
function selectOptions(
  field: FieldMeta,
  meta: OrgMetadata,
): {
  value: string;
  label: string;
  description?: string;
  icon?: string;
  image?: string;
  amount?: number;
}[] {
  const set = field.valueSet ? meta.valueSets.get(field.valueSet) : undefined;
  if (!set) return [];
  // CAOS-640 — the richer half of a value is passed ON rather than narrowed away here. This layer
  // does not decide whether any of it is drawn; the presentation does, and a value carrying none of
  // it produces exactly the two-field object this returned before.
  return [...set.values()].map((o) => ({
    value: o.value,
    label: o.label,
    ...(o.description ? { description: o.description } : {}),
    ...(o.icon ? { icon: o.icon } : {}),
    ...(o.image ? { image: o.image } : {}),
    // `0` is a real qualifier ("adds nothing"), so this tests presence rather than truth.
    ...(o.amount !== undefined ? { amount: o.amount } : {}),
  }));
}

/**
 * CAOS-640 — the presentation the field's VALUE SET declares. The host reads it rather than
 * deciding it: nothing here inspects the field to guess whether it would look good as cards.
 */
function valueSetPresentation(field: FieldMeta, meta: OrgMetadata): 'dropdown' | 'cards' {
  const declared = field.valueSet ? meta.valueSetPresentations.get(field.valueSet) : undefined;
  return declared === 'cards' ? 'cards' : 'dropdown';
}

/**
 * A live search over the records a Reference field may point at. It reads the SAME query route the
 * lists read, so a record somebody may not see is never offered as a link — the access engine
 * narrows the picker for free rather than the picker needing its own idea of who may see what.
 */
function referenceSearch(
  field: FieldMeta,
  ctx: SurfaceContext,
): ((query: string) => Promise<{ value: string; label: string }[]>) | null {
  const targetKey = field.referenceTo;
  const target = targetKey ? ctx.meta.objects.get(targetKey) : undefined;
  if (!targetKey || !target) return null;
  return async (query: string) => {
    const outcome = await queryRecordsOutcome(targetKey, ctx.fetchImpl ?? fetch, 50);
    if (outcome.status !== 'ok') return [];
    const q = query.trim().toLowerCase();
    return outcome.records
      .map((r) => ({ value: String(r.id), label: recordDisplayName(target, r) }))
      .filter((o) => q === '' || o.label.toLowerCase().includes(q))
      .slice(0, 20);
  };
}

/** The `<caos-field-renderer>` field contract, from our own field metadata. */
function rendererField(field: FieldMeta): Record<string, unknown> {
  return {
    storageType: field.storageType,
    label: field.label,
    required: field.required,
    ...(field.format ? { format: field.format } : {}),
    // CAOS-410 — the field's help text, forwarded so the renderer can draw it beside the control.
    // This is the ONE place a field becomes a renderer contract, for the create form and for the
    // record page's inline editor alike, so forwarding it here is what makes the platform render
    // authored help everywhere a person types into a field.
    ...(field.helpText ? { helpText: field.helpText } : {}),
  };
}

/**
 * One field on a record's Details, as THIS surface offers it: the editor, or the read-only row.
 *
 * CAOS-360 — a field this person may change reads as its value and converts in place when clicked.
 * One they may not stays exactly as it always was: no hover cue, no cursor change, nothing to click.
 * An affordance that leads to a refusal is worse than none. The editable one REPLACES the key-value
 * row rather than sitting inside it, because the field renderer already draws the label above the
 * value — the same key-value pair, drawn once. Two components each printing the label is how a
 * field ends up captioned twice.
 *
 * THREE answers have to agree before an editor is drawn, and they answer different questions.
 * `offeredForEdit` is the SURFACE's: this layout asked for the field to be editable here.
 * `isEditable` is the ENGINE's: this person may actually write it. The surface can decline a field
 * the person could write — that is how an org authors a read-only view — and it can never grant one
 * they cannot, because the write boundary would strip the value silently.
 *
 * The third is CAOS-561's, and it is why this function exists at all rather than the condition
 * sitting inline in the section loop. `edit` is what this object says EDITING MEANS here. When the
 * object replaced Edit, the fields stop being directly editable — because half a replacement is
 * worse than none: a wizard beside fields somebody can still edit around it has not replaced
 * anything, it has added a second way to do the same thing, which is the exact state this ticket
 * describes people living with. Taking the resolution as an argument it cannot compute is the point:
 * a site that draws record fields cannot decide for itself what Edit means, and a new one added
 * later either asks the same resolver or does not compile.
 */
function detailFieldNode(
  object: ObjectMeta,
  record: ApiRecord,
  field: FieldMeta,
  placement: { readonly offeredForEdit?: boolean },
  edit: VerbResolution,
  ctx: SurfaceContext,
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  objectHome: ReadonlyMap<string, Destination>,
  // CAOS-368 - the page's one version cell, handed straight through. A field that read the
  // version off the record here would be a second, unshared snapshot, and an inline edit followed
  // by a row action would be refused for a conflict with nobody but the same person.
  version: RecordVersionHolder,
): HTMLElement {
  // `standard` and `fallback` are the two outcomes that leave the platform's own editing in place —
  // the second because a broken replacement must not take the verb away with it.
  const directlyEditable = edit.outcome === 'standard' || edit.outcome === 'fallback';
  const perms = ctx.perms;
  if (directlyEditable && placement.offeredForEdit && perms && isEditable(field, perms)) {
    return editableField(object, record, field, ctx, refIndex, objectHome, version);
  }
  return readOnlyFieldRow(field, record, refIndex, ctx, objectHome);
}

function editableField(
  object: ObjectMeta,
  record: ApiRecord,
  field: FieldMeta,
  ctx: SurfaceContext,
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  objectHome: ReadonlyMap<string, Destination>,
  version: RecordVersionHolder,
): HTMLElement {
  const el = document.createElement('caos-field-renderer');
  el.setAttribute('mode', 'inline');
  const api = el as unknown as {
    field: unknown;
    value: unknown;
    options: unknown;
    optionsPresentation: string;
    search: unknown;
    refDisplay: string | null;
  };
  api.field = rendererField(field);
  api.value = controlValue(field, record);
  const toolbar = richTextToolbarFor(field, ctx.meta);
  if (toolbar) (el as unknown as { richTextToolbar: unknown }).richTextToolbar = toolbar;
  if (field.storageType === 'Select' || field.storageType === 'MultiSelect') {
    api.options = selectOptions(field, ctx.meta);
    // CAOS-640 — forwarded, not chosen. The value set said how it wants to look.
    api.optionsPresentation = valueSetPresentation(field, ctx.meta);
  }
  if (field.storageType === 'Reference') {
    const search = referenceSearch(field, ctx);
    if (search) api.search = search;
    // CAOS-369 — a reference has to read as WHO it points at. Left with only the stored id, an
    // editable reference showed an em dash where a name belonged: a field holding a real account
    // looked like a field nobody had filled in, which is the one thing a record page must never say.
    const linked = formatValue(field, record, refIndex, ctx.meta.valueSets, objectHome);
    if (linked.text) api.refDisplay = linked.text;
    if (linked.href) (el as unknown as { refHref: string }).refHref = linked.href;
  }

  let committed = api.value;
  el.addEventListener('caos-field-change', (ev) => {
    const next = (ev as CustomEvent<{ value: unknown }>).detail?.value;
    const previous = committed;
    committed = next;
    void (async () => {
      // CAOS-368 — the version is read from the page's SHARED holder at save time, not captured
      // when this control was built, because a successful save moves it. Every editable control on
      // a record page closes over the same holder, so editing two fields in a row works: the second
      // save states the version the first one produced. Snapshotted per control instead, the second
      // save would state a version its own first save had just superseded and conflict with nobody
      // but itself.
      const outcome = await updateRecord(
        object.key,
        String(record.id),
        { [field.apiName]: wireValue(field, next) },
        version.current,
        ctx.fetchImpl ?? fetch,
      );
      if (outcome.status === 'ok') {
        // The record is now at whatever version the save produced. Recording it here is what lets
        // the NEXT edit on this page state a version the Engine will accept.
        version.current = recordVersion(outcome.record) ?? version.current;
        raiseToast(`${field.label} saved.`, 'success');
        return;
      }
      if (outcome.status === 'conflict') {
        // The record moved on under this edit, so the save was refused rather than applied over
        // somebody's work. Two things are owed here and neither is optional: say so, and show what
        // it NOW says — putting `previous` back would leave the screen showing a value that is just
        // as wrong as the rejected one, only more convincingly.
        const fresh = await getRecordOutcome(object.key, String(record.id), ctx.fetchImpl ?? fetch);
        if (fresh.status === 'ok' && fresh.record) {
          // The re-read carries its VERSION too, so a second attempt at this edit states one the
          // Engine can accept and succeeds, instead of being refused for ever against the version
          // the page first loaded at.
          version.current = recordVersion(fresh.record) ?? version.current;
          committed = controlValue(field, fresh.record);
          api.value = committed;
          raiseToast(outcome.message, 'warning');
          return;
        }
        // The re-read failed too, so what it now says is genuinely unknown. Fall back to the old
        // value and say the save did not happen — never present a guess as the record's state.
        committed = previous;
        api.value = previous;
        raiseToast(outcome.message, 'warning');
        return;
      }
      // Put it back. The record did not take this value, so the screen must not keep showing it.
      committed = previous;
      api.value = previous;
      raiseToast(outcome.message, 'danger');
    })();
  });
  return el;
}

/**
 * CAOS-368 — the version a record page believes its record is at, shared by every control on it.
 *
 * ONE MUTABLE CELL, and deliberately not the record itself. The version has to be shared, because
 * a save through one field moves the version the NEXT field must state; but writing it back onto
 * the `ApiRecord` would mutate an object the caller owns and may well be reusing, which is a side
 * effect no reader of `renderRecordSurface` would expect. A cell the page creates and only these
 * controls can see gets the sharing without the reach.
 */
interface RecordVersionHolder {
  current: number | undefined;
}

/**
 * Render the LIST surface for an object tab: a page header + a `<caos-data-table>` of the real rows,
 * the name column linking into the record, references linking to their target, Selects as badges.
 */
export function renderListSurface(
  object: ObjectMeta,
  rows: readonly ApiRecord[],
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  ctx: SurfaceContext,
  nextCursor: string | null = null,
): HTMLElement {
  // CAOS-345 — a saved view chooses the columns; without one the object's defaults stand.
  const viewColumns = ctx.listView?.columns ?? [];
  /*
   * CAOS-1623 — columns projected from the row's identity, which no object field holds.
   *
   * Read here, beside the stored columns, and consumed in `spec()` and `describe()` below. Both of
   * those feed the ONE table builder the collection surface owns, and that builder is what draws
   * the `table` presentation AND the `group` presentation — the grouped view is a table per group.
   * Putting this anywhere closer to the table would therefore have been a choice to serve one of
   * those two and not the other, which is exactly the quiet half-consumer this ticket was filed
   * about. `card`, `split` and `kanban` draw cards and lanes rather than columns, so they have
   * nowhere to put one; that is a property of those presentations, not an omission here.
   */
  const derivedColumns = ctx.listView?.derivedColumns ?? [];
  const columns =
    viewColumns.length > 0
      ? viewColumns
      : object.listColumns.length > 0
        ? object.listColumns
        : [object.nameField];

  /**
   * CAOS-1677 — the columns this grid will actually edit in place.
   *
   * Beside the stored columns and the derived ones for the reason CAOS-1623 states just above: both
   * `spec()` and `describe()` need it, and they feed the one builder that draws the `table`
   * presentation AND the grouped one. Deciding it closer to the table would be a choice to serve
   * one of those and not the other.
   *
   * The declaration is where this starts and not where it ends. Four things have to agree, and each
   * is a different kind of question:
   *
   *   • the VIEW asked — `columnEditable`, which is only ever set from an authored
   *     `uiBehavior: 'editable'`;
   *   • the PERSON may write it — `isEditable`, the same gate the record page's fields pass, which
   *     also refuses a computed or engine-assigned kind and a platform-kept system field. The
   *     declaration was never a grant, and this is where that is enforced on this surface;
   *   • the GRID can serve the kind — see `GRID_EDITABLE_STORAGE_TYPES`;
   *   • the column is not the record's NAME. That cell is the only route into the record, and an
   *     editor in it would take the way out with it. A name is edited on the record page it opens.
   *
   * A column that fails any of them is drawn exactly as it is drawn today. It is NOT drawn as a box
   * that cannot save: the one outcome worse than no editor is an editor that discards what somebody
   * types.
   */
  const gridEditable: ReadonlySet<string> = (() => {
    const declared = ctx.listView?.columnEditable;
    const perms = ctx.perms;
    if (!declared || declared.size === 0 || !perms) return new Set<string>();
    const out = new Set<string>();
    for (const apiName of declared) {
      if (apiName === object.nameField) continue;
      const field = object.fields.get(apiName);
      if (!field) continue;
      if (!isEditable(field, perms)) continue;
      if (!GRID_EDITABLE_STORAGE_TYPES.has(field.storageType)) continue;
      // Money is a Number with a `currency` format and a two-part value on the wire
      // (`{ amount, currency }`). `controlValue` reduces it to the amount, so a committed cell
      // would send a bare number back and drop the currency the record holds.
      if (field.format === 'currency') continue;
      // A rich-text field's editor is a toolbar and a document, not a line. The record page gives
      // it one; a single-line box in a cell would silently flatten the markup it holds.
      if (richTextToolbarFor(field, ctx.meta)) continue;
      out.add(apiName);
    }
    return out;
  })();

  /**
   * CAOS-363 — what the reader has asked of this list, as opposed to what the saved view asks of it.
   * The saved view is the AUTHOR deciding once for everybody; this is the person deciding now. The
   * two AND together: narrowing a view further can only ever show you less, never more.
   */
  const state: ListReadState = {
    search: '',
    sort: ctx.listView?.sort ? { field: bareOf(ctx.listView.sort.field), dir: ctx.listView.sort.dir } : null,
    filters: [],
    rows: [...rows],
    refIndex,
    cursor: nextCursor,
    loading: false,
  };

  const objectHome = objectHomeMap(ctx.tabs, ctx.appKey, ctx.recordHomes);
  const nameOf = (record: ApiRecord): string => recordDisplayName(object, record);
  const hrefOf = (record: ApiRecord): string | undefined =>
    ctx.tabKey ? recordAddress({ appKey: ctx.appKey, tabKey: ctx.tabKey }, String(record.id)) : undefined;
  const format = (field: FieldMeta, record: ApiRecord): { text: string; href?: string } =>
    formatValue(field, record, state.refIndex, ctx.meta.valueSets, objectHome);

  // CAOS-467 — the list view's declared `groupBy` field, when it has one, wins over inference for
  // WHICH field a board stands its columns up from; `stageFieldFor` still refuses to hand back a
  // field that is not actually stage-capable, so a declaration that names the wrong kind of field
  // falls back to the same inference as before rather than breaking the board.
  const stage = stageFieldFor(object, ctx.meta, ctx.listView?.groupBy);
  const groupable = groupFieldsFor(object, ctx.meta);

  /**
   * CAOS-467/CAOS-1565 — the field set the NON-TABLE presentations draw: what a card shows, and what
   * the split view's detail pane lists. A declared `cardFields` is that set; absent ⇒ the columns,
   * which is the inference every list had before and stays exactly as it was.
   *
   * `columns` is untouched by this and remains the table's alone, which is what CAOS-467 separated
   * the two for: a table wants the fields you compare records by, a card wants the one line that
   * says which record this is.
   *
   * A declared name the object does not hold is dropped. Deploy already refuses a listView whose
   * `cardFields` names a field that is not there (it is a field REFERENCE —
   * kernel/src/deploy/reference-positions.ts), so this is a belt against a partial metadata read
   * rather than an allowance for a bad declaration; a card drawn from an empty set would say less
   * than one drawn from the columns.
   */
  const declaredCardFields = (ctx.listView?.cardFields ?? []).filter((key) => object.fields.has(key));
  const cardKeys = declaredCardFields.length > 0 ? declaredCardFields : columns;
  /**
   * The fields that can be a counted figure on a card, in the order they were declared. NOT capped
   * here: the cap is two figures DRAWN, and which ones are drawn depends on the record — a field
   * this record leaves empty is not a figure, and capping the candidates first would let an empty
   * first number cost a card the figure that would have filled the slot. That ordering (format,
   * drop the empties, then take two) is the one this surface has always used, and a review caught
   * an earlier version of this change quietly reversing it.
   */
  const metricKeys = cardKeys.filter((key) => {
    const field = object.fields.get(key);
    return field !== undefined && (field.storageType === 'Number' || field.storageType === 'Rollup');
  });

  /** The quiet line under a card's name: the first shown field that is not the name and not empty. */
  const subtitleOf = (record: ApiRecord, skip?: string): string | undefined => {
    for (const key of cardKeys) {
      if (key === object.nameField) continue;
      // `skip` is the board's stage field: on a board the column heading already says the stage, so
      // inferring it as the sub-line as well would print it twice. A DECLARED set overrules that —
      // an author who listed the stage field asked for it, and second-guessing a declaration is the
      // defect this ticket is about.
      if (declaredCardFields.length === 0 && key === skip) continue;
      // A DECLARED set decides both lines of the card, so a figure already drawn as a metric is not
      // repeated as the sub-line. With nothing declared this skips nothing, which keeps an existing
      // list's sub-line exactly what it was.
      if (declaredCardFields.length > 0 && metricKeys.includes(key)) continue;
      const field = object.fields.get(key);
      if (!field || field.system) continue;
      const text = format(field, record).text;
      if (text) return text;
    }
    return undefined;
  };

  /**
   * CAOS-1860 — the row buttons this view declared, and whether there are any to draw.
   *
   * Read once rather than per row: the column has to be added to the spec IFF the cells are added to
   * the rows, and the two reading the same name separately is how a table grows a heading with no
   * cells under it — the mismatch `ROW_ACTIONS_COLUMN` was named to prevent in the first place.
   *
   * This is the DECLARATION, not the permission. Every button here may still filter out for every
   * row, and that is fine and expected: `recordActionBar` returns an empty bar rather than nothing,
   * so the column stays and the cells stay, because a table whose rows have different numbers of
   * cells is a broken table (the rule `buildRecordTable` states for the same reason).
   */
  const declaredRowActions = ctx.listView?.rowActions ?? [];
  const drawsRowActions = declaredRowActions.length > 0;

  /** One record, described once. The surface draws it five ways. */
  const describe = (record: ApiRecord): CollectionRow => {
    const cells: Record<string, unknown> = {};
    /*
     * CAOS-1623 — the derived cells first, so a derived column that collides with a field api name
     * loses to the field. A stored value is the thing the record actually holds; letting a
     * projection overwrite it would make a column silently stop showing its own data.
     */
    for (const d of derivedColumns) {
      cells[d.key] = derivedCellValue(d, record);
    }
    for (const apiName of columns) {
      const field = object.fields.get(apiName);
      if (!field) {
        cells[apiName] = '';
        continue;
      }
      const value = format(field, record);
      if (gridEditable.has(apiName)) {
        /*
         * CAOS-1677 — an editable cell holds the CONTROL value, not the display one.
         *
         * `formatValue` is for reading: it can put thousands separators and a currency symbol on a
         * number. That string is what the editor would be seeded with and what a commit would send
         * back, so a formatted cell makes the first edit of an untouched number a write of
         * "1,234.00". `controlValue` is what the record page seeds its own editors from, for
         * exactly this reason, and `wireValue` on the way out is its pair.
         *
         * So an editable number column reads as its raw digits rather than its formatted form. That
         * is a visible consequence and the right way round: the alternative is a cell that looks
         * better and corrupts the value.
         *
         * A STRING, never a `{ node }`. The component keeps a composed node in preference to
         * drawing a control — deliberately, so a hand-built editor still wins — which means
         * composing anything here would silently cancel the editor this column asked for.
         */
        const control = controlValue(field, record);
        cells[apiName] = control == null ? '' : String(control);
      } else if (apiName === object.nameField) {
        cells[apiName] = { node: recordLink(nameOf(record), hrefOf(record) ?? '#', ctx.navigate) };
      } else if (field.storageType === 'Reference' && value.href) {
        cells[apiName] = { node: recordLink(value.text, value.href, ctx.navigate) };
      } else {
        const full = formatValue(field, record, state.refIndex, ctx.meta.valueSets, objectHome);
        cells[apiName] = full.intent ? { node: badge(full) } : full.text;
      }
    }
    if (drawsRowActions) {
      /*
       * CAOS-1860 — the same bar the record header draws and the same bar a related list's rows
       * draw, over THIS row's record: same runner, same permission ladder, same `appliesWhen`, same
       * `requires`, same `confirm`, same version the button was offered against, same conflict
       * refusal.
       *
       * Reusing `recordActionBar` is the point rather than a convenience, on the reasoning CAOS-407
       * wrote on its `opts` parameter: a second runner written for a list's rows is how `confirm`
       * comes to mean "ask" in one place and "ask, unless…" in another, and how one of the two
       * quietly stops checking whether the person may edit the field at all.
       *
       * `reread` and not a narrower repaint. An approve button moves the record's status, and this
       * list is very often filtered BY that status — an approval queue is "the ones awaiting
       * approval" — so the row that was just acted on usually has to leave the list. Repainting the
       * row in place would leave an approved record sitting in the pending queue until somebody
       * reloaded the page, which reads as the button not having worked.
       *
       * An empty bar rather than no cell, for the reason stated on `drawsRowActions` above.
       */
      const bar = recordActionBar(
        object,
        record,
        state.refIndex,
        ctx,
        () => void reread(),
        { current: recordVersion(record) },
        { actions: declaredRowActions, placement: 'row' },
      );
      cells[ROW_ACTIONS_COLUMN] = { node: bar ?? document.createElement('span') };
    }
    // The split view's detail pane: the same field set a card draws, because it is the same question
    // — what this record is, apart from the columns you compare records by.
    const detail = cardKeys
      .map((k) => object.fields.get(k))
      .filter((f): f is FieldMeta => f != null && f.apiName !== object.nameField)
      .map((field) => {
        const { text, href } = format(field, record);
        return { label: field.label, value: text || '\u2014', ...(href ? { href } : {}) };
      });
    /*
     * CAOS-1623 — the split view's detail pane is a list of LABELLED VALUES, which is a slot a
     * derived column fits without inventing anything, so it gets them after the stored fields.
     *
     * This is the asymmetry worth closing rather than documenting. A STORED column already reaches
     * a card and a lane without being declared in `cardFields`, because `cardKeys` falls back to
     * `columns` — so without this a derived column would be the one kind of column that vanishes
     * the moment you leave the table, which is the quieter version of the very defect this ticket
     * was filed about.
     *
     * `card` and `kanban` are NOT covered and cannot be: their slots are a title, one subtitle line
     * and numeric metrics, none of which is an arbitrary labelled string. That is a property of
     * those presentations rather than a decision taken here, and it is why this sits beside the
     * detail pane instead of in something shared.
     */
    const derivedDetail = derivedColumns
      .map((d) => ({ label: d.label, value: derivedCellValue(d, record) }))
      .filter((d) => d.value !== '');
    detail.push(...derivedDetail);

    const subtitle = subtitleOf(record, stage?.apiName);
    /*
     * The counted facts along the foot of a card — read as “3 contacts”, a figure says more at a
     * glance than the same number in a table cell.
     */
    // Every figure this record actually has, in declared order. HOW MANY FIT is the presentation's
    // decision and is already taken there — `collection-surface.ts` hands the card the first two —
    // so capping here as well would be a second answer to a settled question, and the one further
    // from the card.
    const metrics = metricKeys
      .map((k) => object.fields.get(k))
      .filter((f): f is FieldMeta => f != null)
      .map((field) => ({ value: format(field, record).text, label: field.label.toLowerCase() }))
      .filter((m) => m.value !== '');
    const href = hrefOf(record);
    const groups: Record<string, string> = {};
    for (const field of groupable) groups[field.apiName] = format(field, record).text;
    return {
      id: String(record.id),
      title: nameOf(record),
      cells,
      detail,
      groups,
      ...(metrics.length > 0 ? { metrics } : {}),
      ...(subtitle ? { subtitle } : {}),
      ...(href ? { href } : {}),
      ...(stage ? { stageValue: String(record[stage.apiName] ?? '') } : {}),
    };
  };

  /**
   * Every way this list can be ordered, offered above it so it applies to whatever body is shown.
   *
   * CAOS-1646 L5 — the view's own list wins outright when it declares one. The generated pair per
   * field below is kept as the default and is unchanged for every list that says nothing: what it
   * cannot express is a SHORT, worded set ("Newest first", "Oldest first", "Type", "Status"), which
   * is what a reporter-facing list offers. Two entries per field also means a five-field object
   * offers ten orderings, all named "<Field> (A–Z)".
   */
  const authoredSorts = ctx.listView?.sortOptions;
  const sortOptions: { value: string; label: string }[] = authoredSorts
    ? authoredSorts.map((o) => ({ value: o.value, label: o.label }))
    : [];
  for (const field of authoredSorts ? [] : object.fields.values()) {
    if (!canOrderBy(field.storageType)) continue;
    sortOptions.push({ value: `${field.apiName}:asc`, label: `${field.label} (A\u2013Z)` });
    sortOptions.push({ value: `${field.apiName}:desc`, label: `${field.label} (Z\u2013A)` });
  }

  const nameField = object.fields.get(object.nameField);
  const actions: HTMLElement[] = [];
  // CAOS-360 — New, when the LIST SAYS it offers creating a record AND this person may create one.
  // Two answers and both are required. `create` is the surface's: a view that authored nothing
  // offers nothing, so an org can lay a read-only list over a writable object — which it could not
  // do while the host took this decision from permission alone. `canCreate` is the Engine's, and it
  // is still the one that can only ever subtract: declaring the control never grants anything.
  const offeredCreate = ctx.listView?.create;

  /** The platform's own New: the control this list has drawn since CAOS-360. */
  const standardCreateButton = (): HTMLElement => {
    const create = document.createElement('caos-button');
    create.setAttribute('variant', 'primary');
    create.textContent = offeredCreate?.label ?? 'New';
    create.addEventListener('click', () => openCreateForm(object, ctx));
    return create;
  };

  /**
   * CAOS-561 — what a REPLACEMENT for New does when its piece turns out not to be drawable. It is
   * passed to the resolver as this surface's `reload`, because that is what `reload` means at every
   * site that has one: put yourself right now that something has changed. A list cannot re-run its
   * whole header cheaply, and it does not need to — the only thing that changed is this control, so
   * only this control is rebuilt, in place, with the sentence saying why.
   *
   * Without it the failure is the exact shape the ticket names: the standard New has already been
   * displaced, the person presses the replacement, reads "this cannot be drawn" in an overlay, and
   * closes it to find no way to create a record at all.
   */
  let displaced: { button: HTMLElement; action: RecordActionMeta } | null = null;
  const putNewBack = (): void => {
    // `isConnected` rather than `parentNode`: a list that has been navigated away from still has a
    // parent for every node in it, so swapping the control there would be work nobody can see.
    if (displaced === null || !displaced.button.isConnected) return;
    const { button, action } = displaced;
    displaced = null;
    const standard = standardCreateButton();
    button.replaceWith(standard);
    standard.after(
      standardFallbackNotice({
        verb: 'create',
        outcome: 'fallback',
        action,
        // Asked again rather than remembered: the ladder has just learned why, and the sentence
        // should be that reason and not a generic one standing in for it.
        why: brokenReplacement(action, object, null, ctx) ?? `“${action.label}” opens a piece that cannot be drawn.`,
      }),
    );
  };

  // ...and what New MEANS here, which the object may have said is something of its own. The
  // permission is resolved first and handed over: a replacement replaces the affordance, never the
  // permission, so a person who may not create one is offered nothing either way.
  const createVerb = resolveStandardVerb(
    'create',
    object,
    null,
    new Map(),
    ctx,
    putNewBack,
    offeredCreate !== undefined && ctx.perms?.canCreate === true,
  );
  if (createVerb.outcome === 'replaced') {
    const replacement = document.createElement('caos-button');
    replacement.setAttribute('variant', actionVariant(createVerb.action.intent) === 'ghost' ? 'primary' : actionVariant(createVerb.action.intent));
    replacement.dataset.actionId = createVerb.action.id;
    replacement.dataset['standardVerb'] = 'create';
    replacement.textContent = createVerb.action.label;
    replacement.addEventListener('click', () => void createVerb.run());
    displaced = { button: replacement, action: createVerb.action };
    actions.push(replacement);
  } else if (createVerb.outcome === 'standard' || createVerb.outcome === 'fallback') {
    actions.push(standardCreateButton());
    // Loudly. An override that quietly did not take is what somebody spends an afternoon on.
    if (createVerb.outcome === 'fallback') actions.push(standardFallbackNotice(createVerb));
  }

  let surface: { root: HTMLElement; update: (next: CollectionSpec) => void };

  /**
   * CAOS-1677 — write one committed cell.
   *
   * The record page's path is the model and the three outcomes are its, read from source rather
   * than from the description of it: `ok` moves this row's version on, `conflict` re-reads so the
   * next attempt can succeed, anything else is a plain refusal.
   *
   * WHERE IT DEPARTS, AND WHY. The record page puts the old value back in the control on a failure
   * and writes the re-read value into it on a conflict. A cell cannot do either. The grid's editor
   * is a persistent input that keeps focus through a commit — Enter commits and does not close — so
   * a save resolving while somebody types the next value would overwrite a caret mid-keystroke. The
   * record page is safe from that because its inline editor has closed by the time the save lands.
   * So nothing here writes into a control, and the cell is left holding what the person typed.
   *
   * That is not silence about a refusal: the unsaved marker is the difference between what the row
   * declares and what the control holds, so an unsettled cell goes on saying, correctly, that it
   * holds an edit the record does not have. A refusal is therefore stated twice — once in the toast
   * that says why, and once by the cell continuing to look unsaved. `settle` is called on success
   * ONLY, which is what makes that marker mean something.
   *
   * It also does not repaint. A repaint would discard every other in-progress edit in the grid and
   * whatever was focused, to show one cell a value it already shows. The row is updated in place
   * instead, so the next natural repaint — a further page, a search, a sort — reads the saved record
   * rather than the one the list loaded with.
   *
   * THE VERSION IS READ OFF THE ROW, and there is no holder beside it. CAOS-368's shared cell
   * exists on a record page because every control there edits one record and nothing else moves the
   * page's idea of its version. Here the row IS the holder: every outcome that learns a newer
   * version replaces `state.rows[i]` with the record that carried it, so the next edit to this row
   * reads the version the last one produced. `selectionActionBar` writes a selection the same way —
   * `recordVersion(record)` per record, no holder — and the rule it states is this one: "Each
   * record is written with the version IT was read at ... a stale one among them is refused on its
   * own, reported on its own, and does not take the other eleven down with it."
   *
   * A Map keyed by row id stood here first and was removed after red-verification showed it could
   * not fail: with the record adopted, a holder rebuilt on every save produced identical results on
   * every path. Two mechanisms for one job, where the weaker one also needed clearing on every
   * re-read to stay correct.
   *
   * An absent version needs nothing special: `recordVersion` answers `undefined` on a substrate
   * that tracks none and `updateRecord` already takes it that way, which is how the record page and
   * the selection bar both work today.
   */
  const saveCell = async (edit: {
    readonly id: string;
    readonly column: string;
    readonly value: string;
    readonly settle: (saved: string) => boolean;
    readonly refuse: (refused: string) => boolean;
  }): Promise<void> => {
    /*
     * Re-checked rather than trusted. The event names a column by key and arrives from the DOM, so
     * this is the boundary where "the grid offered this" is confirmed instead of assumed.
     *
     * Each of these ANSWERS before it returns. Committing records what was reported so the blur
     * after Enter does not report it twice, so a commit that is dropped in silence leaves the cell
     * believing that value is already in flight and it can never be sent again. The last of the
     * three is the reachable one: a re-query, a filter or a sort can drop the row from `state.rows`
     * while its editor is still on screen and still has what somebody typed in it.
     */
    if (!gridEditable.has(edit.column)) {
      edit.refuse(edit.value);
      return;
    }
    const field = object.fields.get(edit.column);
    if (!field) {
      edit.refuse(edit.value);
      return;
    }
    const index = state.rows.findIndex((r) => String(r.id) === edit.id);
    if (index < 0) {
      edit.refuse(edit.value);
      return;
    }

    /*
     * REFUSED HERE RATHER THAN SENT, for the value the wire would silently turn into "clear this
     * field". `wireValue` answers null for a Number whose text is not a finite number, so `12abc`
     * or `1,234` or `$5` would CLEAR the number and the save would succeed and say so. That is
     * data loss wearing a success toast, and the grid's control is a plain text box, so it is the
     * ordinary typing mistake rather than an exotic one.
     *
     * An empty box is deliberately NOT caught: clearing a field is its authored meaning, and
     * `wireValue`'s own docblock says so.
     */
    if (field.storageType === 'Number' && edit.value.trim() !== '' && !Number.isFinite(Number(edit.value))) {
      raiseToast(`${field.label} has to be a number.`, 'warning');
      edit.refuse(edit.value);
      return;
    }

    /*
     * A money value arrives as `{ amount, currency }` and `controlValue` reduces it to the amount
     * alone, so a committed cell would send a bare number for a two-part value and drop the
     * currency. A column declaring `format: 'currency'` is already kept out of `gridEditable`;
     * this is the belt for a field that is money on the wire without having declared the format,
     * which is the only way one could reach here.
     */
    const held = state.rows[index]![field.apiName];
    if (held !== null && typeof held === 'object' && 'amount' in (held as Record<string, unknown>)) {
      raiseToast(`${field.label} cannot be edited here.`, 'warning');
      edit.refuse(edit.value);
      return;
    }

    const outcome = await updateRecord(
      object.key,
      edit.id,
      { [field.apiName]: wireValue(field, edit.value) },
      recordVersion(state.rows[index]!),
      ctx.fetchImpl ?? fetch,
    );
    if (outcome.status === 'ok') {
      // The WHOLE saved record, not just the cell. A save can move more than the field it named —
      // a formula over it, a roll-up on it, the modified stamp — and keeping only the typed value
      // would leave the row disagreeing with the record in the columns beside the edited one.
      const saved = outcome.record;
      const at = state.rows.findIndex((r) => String(r.id) === edit.id);
      if (saved && at >= 0) state.rows[at] = saved;
      /*
       * `false` means the cell is no longer drawn, so a repaint landed while this save was in
       * flight and the table it drew came from `state.rows` as they were BEFORE the line above.
       * The screen is then showing the old value while the toast says saved, so it has to be
       * refreshed. ONLY in that case: a repaint while the cell is still there would discard every
       * other in-progress edit in the grid to show one cell a value it already shows.
       */
      if (!edit.settle(edit.value)) surface.update(spec());
      raiseToast(`${field.label} saved.`, 'success');
      return;
    }
    if (outcome.status === 'conflict') {
      // Re-read for the VERSION, which is what makes a second attempt possible instead of refused
      // for ever against the version this list loaded at. The value is deliberately not pushed into
      // the cell; what the record now says reaches the screen on the next repaint, by which time
      // nobody is typing into it.
      const fresh = await getRecordOutcome(object.key, edit.id, ctx.fetchImpl ?? fetch);
      if (fresh.status === 'ok' && fresh.record) {
        const at = state.rows.findIndex((r) => String(r.id) === edit.id);
        if (at >= 0) state.rows[at] = fresh.record;
      }
      // The refusal has to reach the cell too, or the same value can never be tried again - which
      // would make the re-read above pointless, since re-reading exists so a second attempt can
      // succeed.
      edit.refuse(edit.value);
      raiseToast(outcome.message, 'warning');
      return;
    }
    edit.refuse(edit.value);
    raiseToast(outcome.message, 'danger');
  };

  /**
   * CAOS-1677 - one save at a time per ROW, in the order the commits arrived.
   *
   * Two cells of one row committed in quick succession both read that row's version before either
   * answer came back, so the second stated a version its own first save had just superseded and
   * was refused for a conflict with nobody but the same person. Tabbing from one editable cell to
   * the next and typing is the ordinary way to produce that, not a corner case.
   *
   * Serialised per row rather than globally, because that is exactly where the shared state is:
   * the row's record carries the version, and two different rows have nothing to tell each other.
   * `selectionActionBar` states the same rule for a selection - "One at a time, in the order they
   * appear" - and for the same reason.
   *
   * It also closes a version going BACKWARDS: a conflict's re-read could land after a later save
   * had already adopted a newer record and overwrite it with the older one. Chaining means there
   * is no later save for it to overtake.
   *
   * The `catch` swallows nothing real - `saveCell` reports every outcome itself. It is there so one
   * rejected save cannot poison the chain and silently drop the row's next edit.
   */
  const rowSaves = new Map<string, Promise<void>>();
  const queueCellSave = (edit: Parameters<typeof saveCell>[0]): void => {
    const prior = rowSaves.get(edit.id) ?? Promise.resolve();
    rowSaves.set(
      edit.id,
      prior.then(() => saveCell(edit)).catch(() => undefined),
    );
  };

  const spec = (): CollectionSpec => ({
    key: `object.${object.key}`,
    eyebrow: ctx.appLabel,
    /*
     * CAOS-1861 — the LIVE label for the open view, falling back to the one it was resolved with.
     *
     * `ctx.listView.label` alone was right while a view's name could only change by a deploy. It is
     * wrong the moment a person can rename the view they are looking at: the chooser took the new
     * name and the heading above it kept the old one, so the screen disagreed with itself about what
     * you were reading. Reading the live set here means `applyHeader` on the next `update` fixes
     * both at once, rather than a rename needing a navigation to somewhere it already is.
     */
    heading:
      (ctx.listView ? views.find((v) => v.key === ctx.listView!.key)?.label : undefined) ??
      ctx.listView?.label ??
      object.pluralLabel,
    columns: [
      /*
       * CAOS-1646 L2 — the stored columns and the derived ones, interleaved where the author said.
       *
       * `...columns` then `...derivedColumns` stood here, which is why a derived column could only
       * ever be last. `placeColumns` inserts each derived column at its `at` into the list as
       * built so far and appends the ones declaring none, so a list authored before this gets the
       * byte-identical array it got then.
       *
       * NEVER SORTABLE, and this is a correctness rule rather than a simplification. The heading's
       * sort asks the SERVER to order by that key, and the server orders by columns the object
       * actually has. A derived column is computed here, on the page of rows already fetched, so a
       * sort on it would either 500 or silently order by something else. A formula column is
       * refused a sort just above for the same reason — and now that a derived column can sit in
       * the MIDDLE of the stored ones, beside headings that do sort, this is the first place
       * somebody would reasonably add one.
       */
      ...placeColumns(
        columns.map((apiName) => {
          const f = object.fields.get(apiName);
          return {
            key: apiName,
            // CAOS-1646 L1 — the heading the view named for this column, else the field's own.
            label: ctx.listView?.columnLabels?.[apiName] ?? f?.label ?? apiName,
            align: f?.storageType === 'Number' || f?.storageType === 'Rollup' ? ('end' as const) : ('start' as const),
            sortable: f !== undefined && f.storageType !== 'Formula',
            // CAOS-1677 — already decided, above. Every gate has been applied by the time it is
            // read here, which is why this is a membership test and not a condition.
            editable: gridEditable.has(apiName),
          };
        }),
        derivedColumns,
      ),
      // CAOS-1860 — the actions column, LAST and after `placeColumns`, so a derived column's `at`
      // cannot land a projection to the right of the buttons. Never sortable: there is no value
      // under it to order by, and the heading's sort asks the SERVER to order by the column key,
      // which would 500 on `__rowActions`. Same reasoning the derived columns carry just above.
      ...(drawsRowActions
        ? [{ key: ROW_ACTIONS_COLUMN, label: 'Actions', align: 'end' as const, sortable: false }]
        : []),
    ],
    rows: state.rows.map(describe),
    hasMore: state.cursor != null,
    loading: state.loading,
    searchPlaceholder: searchPromptFor(object, ctx.listView),
    ...(stage
      ? {
          stages: [...(ctx.meta.valueSets.get(stage.valueSet ?? '') ?? new Map())].map(([value, option]) => ({
            value,
            label: option.label,
            ...(option.intent ? { intent: option.intent } : {}),
          })),
        }
      : {}),
    groupBy: groupable.map((f) => ({ key: f.apiName, label: f.label })),
    // CAOS-467 — the list view's declared defaults, read straight through: the surface itself
    // (modesFor / renderCollection) is what decides whether either is actually usable, falling
    // back to its own inference when it is not.
    ...(ctx.listView?.presentation
      ? { defaultMode: ctx.listView.presentation }
      : /*
         * CAOS-1860 — a list that declares row buttons and names no `presentation` OPENS ON THE
         * TABLE, because the table is the one reading that draws them.
         *
         * Without this the whole feature is invisible by default. The platform default is `card`
         * (`list-modes.ts`), a card is built from `title`/`subtitle`/`detail`/`metrics` and never
         * reads `cells`, and the actions live in a cell — so an author who declared Approve and
         * Reject and said nothing about presentation would get a queue with no buttons on it and
         * nothing anywhere saying why. That is the exact failure this ticket was filed about one
         * level up: a declaration the renderer accepts and never draws.
         *
         * A DEFAULT AND NOT AN OVERRIDE. An author who explicitly asks for `card` or `kanban` gets
         * it — their word beats an inference, and `presentation` is read first above for precisely
         * that reason. What they do not get is the buttons, and that is a card-grid limitation
         * recorded on the metatype rather than worked around here: `caos-ui`'s card is a `<button>`
         * that opens the record, so a button inside it is not a thing to nest.
         */
        drawsRowActions
        ? { defaultMode: 'table' as const }
        : {}),
    ...(ctx.listView?.groupBy ? { groupByDefault: ctx.listView.groupBy } : {}),
    sortOptions,
    ...(state.sort ? { sortValue: `${state.sort.field}:${state.sort.dir}` } : {}),
    ...(views.length > 0 ? { views, ...(ctx.listView ? { viewKey: ctx.listView.key } : {}) } : {}),
    // CAOS-1861 — the four view-management handlers, supplied together or not at all. The surface
    // draws the menu only when at least one arrives, so `managesViews` is the single place this is
    // decided; spreading them conditionally here is what makes "the chooser leads nowhere" and "you
    // cannot manage views" the same condition rather than two that could drift.
    ...(managesViews ? { onViewCreate, onViewRename, onViewClone, onViewDelete } : {}),
    actions,
    filters,
    pathname: ctx.pathname,
    emptyLabel: () => describeEmpty(object, state, ctx.listView),
    onOpen: (id) => {
      const href = recordAddress({ appKey: ctx.appKey, tabKey: ctx.tabKey }, String(id));
      if (ctx.navigate) ctx.navigate(href);
      else globalThis.location.assign(href);
    },
    /*
     * CAOS-1677 — offered only when some column actually resolved editable, so a list with none
     * hands the surface no handler and the surface draws no editors. The two halves gate each
     * other in both directions: the surface refuses to draw a control without a listener, and the
     * listener is absent unless a column earned one.
     */
    ...(gridEditable.size > 0 ? { onCellCommit: queueCellSave } : {}),
    onView: (viewKey) => {
      // A saved view decides the columns AND the conditions, so it is a different address, not a
      // different filter on this one — which also means it survives a reload and can be linked to.
      const href = listAddress({ appKey: ctx.appKey, tabKey: ctx.tabKey }, viewKey);
      if (ctx.navigate) ctx.navigate(href);
      else globalThis.location.assign(href);
    },
    query: (asked) => {
      state.search = asked.search;
      state.sort = asked.sortValue === ''
        ? null
        : { field: asked.sortValue.split(':')[0]!, dir: asked.sortValue.endsWith(':desc') ? 'desc' : 'asc' };
      void reread();
    },
    onMore: () => void reread({ append: true }),
  });

  /**
   * Ask the Engine again with what the reader has asked for. It re-READS rather than filtering the
   * rows already on screen, because those are one page of the set: filtering them would search the
   * first two hundred records and call the answer "no matches".
   */
  const reread = async (opts: { append?: boolean } = {}): Promise<void> => {
    state.loading = true;
    const outcome = await queryRecordsOutcome(object.key, ctx.fetchImpl ?? fetch, 200, {
      filters: readerFilters(object, ctx, state),
      ...(state.sort ? { sort: { field: state.sort.field, dir: state.sort.dir } } : {}),
      ...(opts.append && state.cursor ? { cursor: state.cursor } : {}),
    });
    state.loading = false;
    if (outcome.status !== 'ok') {
      raiseToast(`Those ${object.pluralLabel.toLowerCase()} could not be read just now. Try again.`, 'danger');
      return;
    }
    state.rows = opts.append ? [...state.rows, ...outcome.records] : outcome.records;
    state.cursor = outcome.nextCursor;
    // Show the rows NOW. Resolving the names behind any lookup columns means a read per referenced
    // object; waiting for those leaves the OLD rows on screen for a second after somebody searched,
    // which reads as "the search did nothing".
    surface.update(spec());
    const names = await buildReferenceIndex(object, ctx.meta.objects, ctx.fetchImpl ?? fetch);
    // Only repaint if it actually learned something — a repaint that changes nothing throws away
    // whatever the reader had focused or scrolled to.
    if (names.size !== state.refIndex.size) {
      state.refIndex = names;
      surface.update(spec());
    }
  };

  const filters = buildFilterControl(object, state, () => void reread());
  /*
   * CAOS-1767 — THE CHOOSER IS OFFERED ONLY WHERE CHOOSING LEADS SOMEWHERE.
   *
   * Picking a saved view here is a NAVIGATION, not a filter: it goes to `listAddress({appKey,
   * tabKey}, viewKey)`, because a view decides the columns AND the conditions and so deserves its own
   * address. Two different callers have no such address, and they are told apart on purpose:
   *
   *   • one that SAYS SO ({@link SurfaceContext.savedViewsWithheld}) — a `list`-kind page, which has
   *     no `view` parameter of its own. See that field for why this cannot be inferred from the tab
   *     key: a list page populates the tab key from the listed object's authored record home, so
   *     inferring it sent the reader off the page into another app's tab.
   *   • one with no tab key at all — a fail-safe, and inert for everything that ships today. The only
   *     production caller before this was the app-tab list route, which always has one. It is kept
   *     because the alternative failure is a control that navigates to `//?view=…`.
   *
   * What a list page loses is switching view in place, and that is a real limitation rather than a
   * hidden one. The view it OPENS on is the one it names in its data source.
   */
  /*
   * CAOS-1861 — the chooser's content, which starts as the INSTALLED set and is then replaced by the
   * Engine's answer.
   *
   * Two sources, in that order, and the order is the point. `ctx.meta.listViews` is already in hand
   * when this function runs, so the chooser is populated on the first paint exactly as it was before
   * this ticket; `listViews/list` then adds this person's own views a moment later. Doing it the
   * other way round — awaiting the route before drawing anything — would make every list screen on
   * the platform wait for a read that most of them learn nothing new from.
   *
   * `canEdit`/`canDelete` are FALSE for an installed view, which is not a guess: a package's view has
   * no row to edit and the route says the same thing when asked. So the management menu is correct
   * about a shipped view before the route has answered, rather than briefly offering Rename on
   * something that can only refuse it.
   */
  let views: CollectionView[] =
    ctx.savedViewsWithheld === true || !ctx.tabKey
      ? []
      : (ctx.meta.listViews.get(object.key) ?? []).map((v) => ({
          key: v.key,
          label: v.label,
          source: 'package' as const,
          canEdit: false,
          canDelete: false,
        }));

  /*
   * MANAGEMENT IS OFFERED ON THE SAME TEST AS THE CHOOSER, and for the same reason CAOS-1767 gives
   * for the chooser: choosing a view is a NAVIGATION to `listAddress({appKey, tabKey}, viewKey)`, and
   * so is landing on one you have just made. A caller with no `view` address of its own — a
   * `list`-kind page — cannot show you the view it just saved, so offering to save one would be
   * offering a control whose result is invisible.
   *
   * It is NOT gated on there being views already. An object with nothing shipped is exactly where
   * somebody most wants a view of their own, and gating on a non-empty set would mean the first view
   * is the one you can never make.
   */
  const managesViews = ctx.savedViewsWithheld !== true && !!ctx.tabKey;

  /** Go to a view by key — the same navigation the chooser does, for the same reason. */
  const openView = (viewKey: string): void => {
    const href = listAddress({ appKey: ctx.appKey, tabKey: ctx.tabKey }, viewKey);
    if (ctx.navigate) ctx.navigate(href);
    else globalThis.location.assign(href);
  };

  /**
   * Replace the chooser's content with the Engine's answer, which holds the personal views too.
   *
   * Only when the answer is NON-EMPTY. `listViewsFor` is fail-soft to `[]`, so an empty answer means
   * either "this object genuinely has no views" or "the read did not work", and those are
   * indistinguishable from here. Overwriting a populated chooser with `[]` would therefore let a
   * failed read erase the installed views that were already on screen and working.
   */
  const refreshViews = async (): Promise<void> => {
    const live = await listViewsFor(object.key, ctx.fetchImpl ?? fetch);
    if (live.length === 0) return;
    views = live.map((v) => ({
      key: v.key,
      label: v.label,
      source: v.source,
      canEdit: v.canEdit,
      canDelete: v.canDelete,
      ...(v.id ? { id: v.id } : {}),
    }));
    surface.update(spec());
  };

  /**
   * The body a NEW view is saved with: what is on the screen right now.
   *
   * "Save what I am looking at" is the only reading of New that does not need a second screen. The
   * alternative — create an empty view and then edit it — would need a view editor, which is a
   * different ticket and a much larger one; this makes the list itself the editor, which is how a
   * person got to the arrangement they want to keep in the first place.
   *
   * WHAT IT CARRIES is the resolved `columns` (the view's own, or the object's defaults when there is
   * no view — so a brand-new view off an unviewed object is not column-less), the reader's live
   * filters and order, and the presentation and grouping of the view this was made from. What it does
   * NOT carry is `search`: a search is a question you asked once, not an arrangement you keep, and
   * baking it in would make a view that can never show the records it excludes.
   *
   * It does not carry `derivedColumns` either, and that one is a judgement rather than an oversight:
   * a derived column is an authored reading of the view it came from, and this surface holds it
   * PROJECTED rather than as authored. Writing a projection back as a declaration is how a copy
   * comes to differ from its original. Keeping everything is what Copy is for, and it keeps
   * everything properly, because the route reads the source body itself.
   *
   * The identity fields are absent on purpose — `type`, `key` and `object` are set by the Engine from
   * the row's own identity, "so a caller cannot store a view whose body disagrees with the row that
   * holds it". Sending them would be a client asserting something the route is about to overwrite.
   */
  const bodyOnScreenNow = (label: string): Record<string, unknown> => {
    // Every field reference in the body goes through the SAME qualifier — see `authoredRef` for why
    // that is one function rather than four call sites, and what it cost to find out.
    const authored = authoredRef(object);
    return {
      label,
      columns: columns.map(authored),
      ...(state.filters.length > 0
        ? { filters: state.filters.map((f) => ({ ...f, field: authored(f.field) })) }
        : {}),
      ...(state.sort ? { sort: { field: authored(state.sort.field), dir: state.sort.dir } } : {}),
      ...(ctx.listView?.presentation ? { presentation: ctx.listView.presentation } : {}),
      ...(ctx.listView?.groupBy ? { groupBy: authored(ctx.listView.groupBy) } : {}),
    };
  };

  /** Every key already in use on this object, so {@link viewKeyFor} picks one that is free. */
  const takenKeys = (): string[] => views.map((v) => v.key);

  const onViewCreate = async (): Promise<void> => {
    const label = await askForName(
      `Name this view of ${object.pluralLabel.toLowerCase()}. It keeps the columns, filters and order on screen now.`,
      'Save view',
    );
    if (label === null) return;
    const outcome = await saveView(
      object.key,
      viewKeyFor(label, takenKeys()),
      label,
      bodyOnScreenNow(label),
      ctx.fetchImpl ?? fetch,
    );
    if (outcome.status !== 'ok') {
      raiseToast(outcome.message, 'danger');
      return;
    }
    // Go to it. A view you made and cannot see is indistinguishable from one that was not saved,
    // and the address is also what makes it linkable and survive a reload.
    openView(outcome.view.key);
  };

  const onViewRename = async (view: CollectionView): Promise<void> => {
    const label = await askForName(`Rename "${view.label}".`, 'Rename', view.label);
    if (label === null || label === view.label) return;
    /*
     * A RENAME IS A SAVE OF THE SAME KEY, which is what the route is: `save` is create-or-edit keyed
     * by `(object, key)`. The body sent is the body that view already has, read back from the live
     * set rather than rebuilt from the screen — renaming a view must not quietly re-save it with
     * whatever the reader has filtered it down to since.
     *
     * The key is kept, so every link to this view still works. A rename that moved the key would
     * break the address somebody bookmarked, and the label is the only thing being changed.
     */
    const current = (await listViewsFor(object.key, ctx.fetchImpl ?? fetch)).find((v) => v.key === view.key);
    const body = current?.body;
    if (body == null || typeof body !== 'object') {
      raiseToast('That view could not be renamed just now. Try again.', 'danger');
      return;
    }
    const outcome = await saveView(
      object.key,
      view.key,
      label,
      { ...(body as Record<string, unknown>), label },
      ctx.fetchImpl ?? fetch,
    );
    if (outcome.status !== 'ok') {
      raiseToast(outcome.message, 'danger');
      return;
    }
    raiseToast(`Renamed to "${label}".`, 'success');
    await refreshViews();
  };

  const onViewClone = async (view: CollectionView): Promise<void> => {
    const label = await askForName(`Name your copy of "${view.label}".`, 'Copy view', `${view.label} (copy)`);
    if (label === null) return;
    /*
     * `from` is the SOURCE's id for a personal view and its key for a shipped one, which is what the
     * route's `resolveCloneSource` accepts — and the reason the route exists rather than leaving
     * this to a read-and-post: it reads the source body itself, so the copy carries every field the
     * original had, including any this surface does not draw.
     */
    const outcome = await cloneView(
      object.key,
      view.id ?? view.key,
      viewKeyFor(label, takenKeys()),
      label,
      ctx.fetchImpl ?? fetch,
    );
    if (outcome.status !== 'ok') {
      raiseToast(outcome.message, 'danger');
      return;
    }
    openView(outcome.view.key);
  };

  const onViewDelete = async (view: CollectionView): Promise<void> => {
    // A package's view has no row and so cannot be deleted at all; `canDelete` already said so and
    // the menu already disabled the item. This is the belt to that braces: a handler that deleted by
    // key would otherwise be one refactor away from reaching for something that is not there.
    if (!view.id) return;
    const yes = await askToConfirm(`Delete the view "${view.label}"? This cannot be undone.`, 'Delete view');
    if (!yes) return;
    const outcome = await deleteView(view.id, ctx.fetchImpl ?? fetch);
    if (outcome.status !== 'ok') {
      raiseToast(outcome.message, 'danger');
      return;
    }
    // If the view just deleted is the one being LOOKED AT, staying put would leave the screen
    // showing a view that no longer exists — and a reload would then silently fall back to the
    // object's own columns without ever saying why. So go somewhere that still exists.
    if (ctx.listView?.key === view.key) {
      const href = listAddress({ appKey: ctx.appKey, tabKey: ctx.tabKey });
      if (ctx.navigate) ctx.navigate(href);
      else globalThis.location.assign(href);
      return;
    }
    raiseToast(`Deleted "${view.label}".`, 'success');
    await refreshViews();
  };

  surface = renderCollection(spec());
  // The personal views arrive after the first paint, on purpose — see `refreshViews`. Asked for only
  // where the chooser leads somewhere, so a caller that cannot show a view does not read for one.
  if (managesViews) void refreshViews();
  return surface.root;
}

/** What the reader has asked of a list, on top of whatever the saved view already asks. */
export interface ListReadState {
  search: string;
  sort: { field: string; dir: 'asc' | 'desc' } | null;
  filters: { field: string; op: 'eq' | 'contains'; value: string }[];
  rows: ApiRecord[];
  refIndex: ReadonlyMap<string, { name: string; object: string }>;
  cursor: string | null;
  loading: boolean;
}

/**
 * CAOS-1861 — a column as it would be AUTHORED: `object.field`, not the bare name this surface holds.
 *
 * The host's projection bares every field reference, and for reading that is right — the Data API
 * wants bare names. But a view SAVED from the screen is metadata, and the bare form is a liability
 * two steps away: `ListViewSchema` accepts it (a single segment matches `COMPONENT_KEY_PATTERN`), so
 * nothing refuses it on the way in, and then CAOS-1579's `promote` turns that view into shipped
 * metadata — where the deploy reference gate DOES refuse a bare field reference. A person's saved
 * view would have been unpromotable, and the refusal would have arrived at an admin who never typed
 * the thing that caused it.
 *
 * So it is qualified here, at the one place a body is written. A column that already carries a dot is
 * left exactly as it is: it is either already qualified or a cross-object path, and either way this
 * is not the code that gets to decide.
 *
 * `ListViewMeta.columns` is `string[]`, so a CAOS-1646 per-column heading is not reachable from here
 * — the projection has already flattened `{field, label}` to the reference. A view saved from the
 * screen therefore carries the field's own headings, which is what the screen was showing anyway.
 * Copy is the verb that keeps an authored heading, and it keeps it properly: the route copies the
 * source body rather than anything this surface rebuilt.
 *
 * IT IS ONE FUNCTION FOR ALL FOUR AXES, and that is the point rather than tidiness. The first draft
 * qualified `columns`, `sort.field` and `groupBy` at three inline call sites and left
 * `filters[].field` bare — the independent read caught it — and all four are gated identically:
 * `reference-positions.ts` emits `columns`, `filters.field`, `sort.field` and `groupBy` as `field`
 * references on the same terms, so missing any one of them is the same defect. Three call sites and
 * one forgotten is what happens when the rule lives in the call rather than in a function every axis
 * has to go through.
 */
function authoredRef(object: ObjectMeta): (ref: string) => string {
  return (ref) => (ref.includes('.') ? ref : `${object.key}.${ref}`);
}

/** A field key as the Data API wants it: bare, whatever form the metadata used. */
function bareOf(fieldKey: string): string {
  const dot = fieldKey.lastIndexOf('.');
  return dot >= 0 ? fieldKey.slice(dot + 1) : fieldKey;
}

/**
 * Everything narrowing the read: what the saved view asks for, plus what the reader asked for. They
 * AND together, so a reader can only ever narrow a view further — never widen it into records the
 * view was written to keep out.
 */
export function readerFilters(object: ObjectMeta, ctx: SurfaceContext, state: ListReadState): QueryCondition[] {
  // CAOS-1624 — a filter carrying `appliesWhen` is scoped to the VIEWER: it narrows only for
  // somebody who does not hold the named permission. That is what lets one list view be both "my
  // submissions" for a reporter and "the org's" for an administrator, instead of the two readings
  // metadata could express before — always mine (a bolted-on owner_id, which narrows the
  // administrator too) or always everything (which shows every reporter the whole org).
  //
  // LIFTED ONLY BY A POSITIVE ANSWER. The condition skipped is the one whose permission the viewer
  // is KNOWN to hold, so every other state keeps the narrowing: they hold nothing, they hold other
  // things, or the identity read could not be made at all. Failing closed is therefore the shape of
  // the expression rather than a branch somebody has to remember, and the error path narrows the
  // read rather than widening it.
  //
  // It is not a permission check. The Engine still decides what this caller may read; a viewer who
  // holds the permission simply gets the unfiltered question asked, and gets back exactly what the
  // route was already willing to give them.
  //
  // `appliesWhen` is STRIPPED rather than spread through. It is an instruction to this host about
  // whom to narrow, not part of the question the Engine is being asked, and `QueryCondition` is
  // `ListViewFilter` — so spreading the filter unchanged would put a key the Engine never asked for
  // into the request body of every list read that used one.
  const held = ctx.heldPermissions;
  const out: QueryCondition[] = (ctx.listView?.filters ?? [])
    .filter((f) => !(f.appliesWhen && held?.includes(f.appliesWhen.lacksPermission)))
    .map((f) => ({ field: f.field, op: f.op, value: f.value }));
  const q = state.search.trim();
  if (q !== '') {
    // CAOS-467/CAOS-1565 — what the view says its search matches. Several fields are ONE condition:
    // an `any` group, so a record qualifies by matching any one of them. Sent as separate entries
    // they would AND, and a two-field search would return only the records containing the term in
    // both — a search that quietly answers a different question.
    const fields = searchFieldsFor(object, ctx.listView);
    const first = fields[0]!;
    out.push(
      fields.length === 1
        ? { field: first, op: 'contains', value: q }
        : { any: fields.map((field) => ({ field, op: 'contains' as const, value: q })) },
    );
  }
  for (const f of state.filters) {
    if (f.value.trim() === '') continue;
    out.push({ field: f.field, op: f.op, value: f.value });
  }
  return out;
}

/**
 * CAOS-467/CAOS-1565 — which fields a list's search matches. The view's declared `search` decides
 * it; nothing declared ⇒ the object's name field, which is what every list search matched before and
 * what an unmigrated view keeps.
 *
 * A declared name the object does not hold is dropped, and if that leaves nothing the name field
 * stands in: deploy refuses a listView whose `search` names a field that is not there, so this
 * guards a partial metadata read rather than a bad declaration, and sending the Engine a field it
 * cannot resolve would refuse the whole read and take the list down over one name.
 *
 * CAOS-1730 — THE CITATION, not the claim, was wrong, and it is the same mistake twice over. This
 * pointed at `kernel/src/deploy/reference-positions.ts` as the authority for a refusal at DEPLOY.
 * That file is the REVERSE graph: it answers "what would break if this field were deleted", and
 * registering a position there does not make the deploy gate refuse anything. The forward gate is
 * `outboundRefsOf` in `kernel/src/deploy/validate.ts`, and CAOS-1646 is what put `search` on it —
 * at which point this sentence became true, having been false when it was written.
 *
 * That is worth spelling out because the ORIGINAL version of this comment is what CAOS-1646 was
 * filed about: it asserted a refusal that did not exist and used the assertion to justify its own
 * silent fallback. Leaving a true claim resting on the wrong file would let the next reader confirm
 * it against the reverse graph and reach the same false conclusion about `sort.field`, `groupBy`,
 * `filters[].field` or `cardFields` — all four of which were modelled there and gated nowhere until
 * CAOS-1730.
 *
 * A field the VIEWER may not read is a different case and is deliberately not handled here: the
 * Engine refuses to filter on it, exactly as it already refuses an authored `filters` entry naming
 * one. The host cannot tell — `perms` carries what may be WRITTEN, not what may be read — and
 * guessing here would either drop a field somebody can search or claim one they cannot.
 */
export function searchFieldsFor(object: ObjectMeta, listView?: ListViewMeta): string[] {
  const declared = (listView?.search ?? []).filter((key) => object.fields.has(key));
  return declared.length > 0 ? [...declared] : [object.nameField];
}

/**
 * CAOS-1646 L2 — the finished column order: the stored columns, with each derived column inserted
 * where it asked to go.
 *
 * `at` indexes the list AS BUILT SO FAR rather than the final result, which is the only reading
 * that is well-defined when two derived columns name the same index. Taking them in authored order
 * and splicing one at a time makes "first" simply `0` and makes a run of them author left to right.
 *
 * Out of range is CLAMPED here and REFUSED at the deploy front door. That split is deliberate: the
 * gate is where an author finds out, and this is a renderer that must not throw over metadata it
 * has already been handed — a list that disappears is worse than a column one place from where it
 * was asked for.
 */
export function placeColumns<S extends { key: string }, D extends { key: string; label: string; at?: number }>(
  stored: readonly S[],
  derived: readonly D[],
): (S | { key: string; label: string; align: 'start'; sortable: false })[] {
  const out: (S | { key: string; label: string; align: 'start'; sortable: false })[] = [...stored];
  for (const d of derived) {
    const entry = { key: d.key, label: d.label, align: 'start' as const, sortable: false as const };
    if (d.at === undefined) {
      out.push(entry);
      continue;
    }
    out.splice(Math.min(d.at, out.length), 0, entry);
  }
  return out;
}

/**
 * CAOS-1646 — one derived cell's value: the id projection, or the folded word.
 *
 * Both halves live here so the table, the split view's detail pane and anything else reading a
 * derived column cannot disagree about what the column says. That matters more for the cascade than
 * for the projection, because a cascade's answer depends on the record and a second implementation
 * would drift on the edge cases rather than on the ordinary ones.
 */
function derivedCellValue(column: ListViewDerivedColumn, record: Record<string, unknown>): string {
  if (column.cases !== undefined) {
    for (const branch of column.cases) {
      const raw = record[branch.at];
      if (branch.contains !== undefined) {
        // A LIST value matches by MEMBERSHIP and a plain string by substring — the same split
        // `rowMatchesFilter` draws on a roster, so one vocabulary behaves one way on both vehicles.
        const hit = Array.isArray(raw)
          ? raw.some((entry) => String(entry) === branch.contains)
          : String(raw ?? '').includes(branch.contains);
        if (hit) return branch.value;
        continue;
      }
      if (branch.equals !== undefined && String(raw ?? '') === branch.equals) return branch.value;
    }
    return column.otherwise ?? '';
  }
  return derivedFromId(record['id'], column);
}

/**
 * The search box's own promise, worded from the fields it actually matches: "… by name", "… by name
 * or code", "… by name, code or owner". A box that names fields it does not search — or hides the
 * ones it does — teaches people the search is broken, which is the reason this reads off the same
 * list the query is built from.
 */
export function searchPromptFor(object: ObjectMeta, listView?: ListViewMeta): string {
  // CAOS-1646 L4 — the view's own wording wins. The generated sentence below stays the default and
  // stays right for every list that says nothing, which is all of them today; what it could not do
  // is say "Search your feedback…", because it is worded from field labels by construction.
  if (listView?.searchPlaceholder !== undefined) return listView.searchPlaceholder;
  const labels = searchFieldsFor(object, listView).map((key) =>
    (object.fields.get(key)?.label ?? key).toLowerCase(),
  );
  const named =
    labels.length <= 1
      ? (labels[0] ?? 'name')
      : `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
  return `Search ${object.pluralLabel.toLowerCase()} by ${named}…`;
}

/**
 * The empty state, worded for WHY it is empty. "No accounts to display yet" is true of a new
 * workspace and a lie about a search that matched nothing — the second one needs to say that the
 * search is what emptied it, or somebody concludes the records are gone.
 */
export function describeEmpty(object: ObjectMeta, state: ListReadState, listView?: ListViewMeta): string {
  const plural = object.pluralLabel.toLowerCase();
  const typed = state.search.trim();
  /*
   * CAOS-1646 L4 — the authored wording, branch by branch, never one string for all three.
   *
   * The three branches are the reason this function exists, and its own header says why: "No
   * accounts to display yet" is true of a new workspace and a lie about a search that matched
   * nothing. So `emptyLabel` answers only the unsearched, unfiltered case and `searchEmptyLabel`
   * only the search case — an author who sets just the welcoming one still gets a truthful
   * sentence over a failed search, because this falls back to the generated one rather than
   * reusing theirs.
   *
   * The FILTER branch takes no authored word. It is not an omission: a roster has no equivalent to
   * borrow a name from, and "no rows match those conditions" does not vary by surface the way the
   * other two do. Adding a third word with no demonstrated need is how vocabulary grows past what
   * anyone can keep in step.
   */
  if (typed !== '') {
    return listView?.searchEmptyLabel !== undefined
      ? listView.searchEmptyLabel.split('{search}').join(typed)
      : `No ${plural} match “${typed}”.`;
  }
  if (state.filters.some((f) => f.value.trim() !== '')) return `No ${plural} match those conditions.`;
  return listView?.emptyLabel ?? `No ${plural} to display yet.`;
}

/**
 * CAOS-363 — the controls above a list: search it, and narrow it by a field.
 *
 * Sorting is not here — it is on the column headings, where the thing being sorted actually is.
 */
function buildListToolbar(
  object: ObjectMeta,
  ctx: SurfaceContext,
  state: ListReadState,
  onChange: () => void,
): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'sa-list__toolbar';

  /*
   * CAOS-383 - ONE bar, in the reference's order: which saved view, search it, order it, narrow it,
   * read it again. The order lived only on the table's column headings, which meant a person reading
   * cards or a board could not order the list at all - the control has to be here, above the list,
   * where it applies to whatever body is on screen.
   */
  const views = ctx.meta.listViews.get(object.key) ?? [];
  if (views.length > 0) {
    const chooser = document.createElement('select');
    chooser.className = 'sa-list__view';
    chooser.setAttribute('aria-label', `Which ${object.pluralLabel.toLowerCase()} to show`);
    for (const view of views) {
      const option = document.createElement('option');
      option.value = view.key;
      option.textContent = view.label;
      if (ctx.listView?.key === view.key) option.selected = true;
      chooser.append(option);
    }
    chooser.addEventListener('change', () => {
      // A saved view decides the columns AND the conditions, so it is a different address, not a
      // different filter on this one - which also means it survives a reload and can be linked to.
      const href = listAddress({ appKey: ctx.appKey, tabKey: ctx.tabKey }, chooser.value);
      if (ctx.navigate) ctx.navigate(href);
      else globalThis.location.assign(href);
    });
    bar.append(chooser);
  }

  const nameField = object.fields.get(object.nameField);
  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'sa-list__search';
  // It says what it searches. A box labelled "Search accounts" that only matches the name teaches
  // people the search is broken the first time they type a city into it — and a box still saying
  // "by name" while the view declares it searches three fields is the same lesson in reverse.
  search.placeholder = searchPromptFor(object, ctx.listView);
  search.setAttribute('aria-label', search.placeholder);
  let debounce: ReturnType<typeof setTimeout> | null = null;
  search.addEventListener('input', () => {
    state.search = search.value;
    if (debounce) clearTimeout(debounce);
    // A read per keystroke would ask the Engine six times for a word nobody has finished typing.
    debounce = setTimeout(onChange, 250);
  });
  bar.append(search);

  bar.append(buildSortControl(object, state, onChange));

  const filters = buildFilterControl(object, state, onChange);
  // The conditions are folded away until asked for: an empty pair of controls sitting in the bar
  // forever is clutter, and the chips below say when something IS narrowing the list.
  filters.hidden = state.filters.length === 0;

  const narrow = document.createElement('button');
  narrow.type = 'button';
  narrow.className = 'sa-list__tool';
  narrow.setAttribute('aria-expanded', String(!filters.hidden));
  narrow.setAttribute('aria-label', 'Filters');
  narrow.title = 'Filters';
  narrow.append(makeIcon('sliders-horizontal'));
  const word = document.createElement('span');
  word.textContent = 'Filter';
  narrow.append(word);
  narrow.addEventListener('click', () => {
    filters.hidden = !filters.hidden;
    narrow.setAttribute('aria-expanded', String(!filters.hidden));
  });
  bar.append(narrow);

  const refresh = document.createElement('button');
  refresh.type = 'button';
  refresh.className = 'sa-list__tool sa-list__tool--icon';
  refresh.setAttribute('aria-label', 'Refresh');
  refresh.title = 'Refresh';
  refresh.append(makeIcon('refresh-cw'));
  // Records change while somebody is looking at the list. Reloading the whole workspace to find out
  // is a heavy way to ask a light question.
  refresh.addEventListener('click', onChange);
  bar.append(refresh);

  bar.append(filters);
  return bar;
}

/**
 * CAOS-383 - the ORDER, above the list rather than on the table's headings.
 *
 * The headings keep working, and they stay the natural place to reorder a table. But they are the
 * table's, and two of the five ways of reading a list have no headings at all: a person reading
 * cards or a board could not order the list by anything. The control belongs where it applies to
 * whatever is on screen.
 */
function buildSortControl(object: ObjectMeta, state: ListReadState, onChange: () => void): HTMLElement {
  const chooser = document.createElement('select');
  chooser.className = 'sa-list__sort';
  chooser.setAttribute('aria-label', 'Order');

  const blank = document.createElement('option');
  blank.value = '';
  blank.textContent = 'Order by…';
  chooser.append(blank);

  for (const field of object.fields.values()) {
    // CAOS-1877 — through the SAME predicate the generated `sortOptions` use, which is the point.
    // This loop and that one are two independent generators of one control and they disagreed in
    // BOTH directions: this one refused a Rollup the Engine orders by perfectly well (CAOS-576),
    // and offered a Reference and a Geolocation it refuses.
    //
    // NOT A LIVE DEFECT TODAY, and that is worth writing down rather than overstating. The only
    // caller of `buildSortControl` is `buildListToolbar` above it, and `buildListToolbar` has no
    // callers anywhere — the live toolbar is `collection-surface.ts`, fed by the generated
    // `sortOptions`. So nobody can reach this chooser to pick a reference out of it. The first
    // version of this change patched only the other loop, a review flagged this one as a live
    // regression, and the reachability check is what settled that it is not. It is unified anyway,
    // because "two definitions of one rule, currently unreachable" is exactly the shape that comes
    // back the day somebody revives the toolbar, and they would revive the disagreement with it.
    if (!canOrderBy(field.storageType)) continue;
    for (const dir of ['asc', 'desc'] as const) {
      const option = document.createElement('option');
      option.value = `${field.apiName}:${dir}`;
      option.textContent = `${field.label} ${dir === 'asc' ? '(A\u2013Z)' : '(Z\u2013A)'}`;
      if (state.sort?.field === field.apiName && state.sort.dir === dir) option.selected = true;
      chooser.append(option);
    }
  }

  chooser.addEventListener('change', () => {
    if (chooser.value === '') {
      state.sort = null;
    } else {
      const [field, dir] = chooser.value.split(':');
      state.sort = { field: field!, dir: dir === 'desc' ? 'desc' : 'asc' };
    }
    onChange();
  });
  return chooser;
}

/**
 * The narrowing control: pick a field, say what it should be, apply. Conditions AND together, and
 * each one is shown as a chip you can take off again — a narrowing you cannot see is a list that
 * lies about how much there is.
 */
function buildFilterControl(object: ObjectMeta, state: ListReadState, onChange: () => void): HTMLElement {
  const holder = document.createElement('div');
  holder.className = 'sa-list__filters';

  const chips = document.createElement('div');
  chips.className = 'sa-list__chips';

  const field = document.createElement('select');
  field.className = 'sa-list__filter-field';
  field.setAttribute('aria-label', 'Field to narrow by');
  const blank = document.createElement('option');
  blank.value = '';
  blank.textContent = 'Narrow by…';
  field.append(blank);
  // Only fields a person can state a value for. Narrowing by a formula or a rollup would mean
  // matching against something computed on read, which the store cannot do.
  for (const f of object.fields.values()) {
    if (f.storageType === 'Formula' || f.storageType === 'Rollup') continue;
    const opt = document.createElement('option');
    opt.value = f.apiName;
    opt.textContent = f.label;
    field.append(opt);
  }

  const value = document.createElement('input');
  value.type = 'text';
  value.className = 'sa-list__filter-value';
  value.placeholder = 'Value';
  value.setAttribute('aria-label', 'Value to match');

  const add = document.createElement('caos-button');
  add.setAttribute('variant', 'ghost');
  add.textContent = 'Apply';

  const paintChips = (): void => {
    chips.replaceChildren();
    state.filters.forEach((f, i) => {
      const label = object.fields.get(f.field)?.label ?? f.field;
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'sa-list__chip';
      chip.textContent = `${label} ${f.op === 'eq' ? 'is' : 'contains'} “${f.value}” ✕`;
      chip.setAttribute('aria-label', `Remove the condition ${label} ${f.op === 'eq' ? 'is' : 'contains'} ${f.value}`);
      chip.addEventListener('click', () => {
        state.filters.splice(i, 1);
        paintChips();
        onChange();
      });
      chips.append(chip);
    });
  };

  const apply = (): void => {
    const key = field.value;
    const v = value.value.trim();
    if (key === '' || v === '') return;
    const meta = object.fields.get(key);
    // An exact match is right for a fixed choice and wrong for free text, where somebody types part
    // of what they remember.
    const op: 'eq' | 'contains' = meta?.storageType === 'Select' || meta?.storageType === 'Reference' ? 'eq' : 'contains';
    state.filters.push({ field: key, op, value: v });
    field.value = '';
    value.value = '';
    paintChips();
    onChange();
  };
  add.addEventListener('click', apply);
  value.addEventListener('keydown', (ev) => {
    if ((ev as KeyboardEvent).key === 'Enter') {
      ev.preventDefault();
      apply();
    }
  });

  const row = document.createElement('div');
  row.className = 'sa-list__filter-row';
  row.append(field, value, add);
  holder.append(row, chips);
  return holder;
}

/**
 * CAOS-407 — the key of the trailing column a table's row buttons are drawn in. Named rather than
 * written twice, since the column and the cell have to agree or the buttons land nowhere.
 */
const ROW_ACTIONS_COLUMN = '__rowActions';

/**
 * CAOS-1677 — the storage kinds a cell editor in the grid can honestly serve.
 *
 * The control the table draws is one `input type="text"` that commits a string, so this is the set
 * of kinds for which a line of text IS the value. It is deliberately a short list of what works
 * rather than a long list of exclusions: a storage kind added to the platform tomorrow is not
 * grid-editable until somebody has looked at it, which is the safe direction for a write path.
 *
 * Why each of the others is absent, since "not yet" and "never" are different and only the second
 * would be a reason to stop asking:
 *   • `Select` / `MultiSelect` — a box would take a value the value set does not contain;
 *   • `Reference` — it would take any string as a record id;
 *   • `Boolean` — the control is wrong, not merely plain;
 *   • `Date`, `DateTime`, `Time`, `ZonedDateTime` — the record page draws real pickers, and a line
 *     of text is worse than what already exists rather than merely different (CAOS-1678 is open on
 *     that editor);
 *   • `JSON`, `File`, `Geolocation`, `Overridable`, `Localizable` — compound values with no
 *     one-line form that round-trips.
 *   • `Formula`, `Rollup`, `AutoNumber` never reach here: `isEditable` refuses them upstream, and
 *     naming them here as well would be a second answer to a settled question.
 *
 * Every one of those is "not yet": they need a real control inside a cell, which needs the renderer
 * to be placeable without its own label and a picker that is not clipped by the cell. That is a
 * piece of work with a design question in it, not an omission here.
 */
const GRID_EDITABLE_STORAGE_TYPES: ReadonlySet<string> = new Set(['Text', 'Number']);

/**
 * A table of records of ONE object — the shape both a list tab and a record's attached set take.
 * Extracted (CAOS-361) so an account's contacts read exactly like the Contacts tab reads: same name
 * link, same reference links, same status badges. Two tables of the same records that disagree on
 * how a value looks is how a workspace starts feeling like several products.
 *
 * `tabKey` is the app tab a row LINKS into; for an attached set that is the CHILD's own tab, so
 * clicking a contact under an account opens it on the Contacts tab where it belongs.
 */
function buildRecordTable(
  object: ObjectMeta,
  rows: readonly ApiRecord[],
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  ctx: SurfaceContext,
  columns: readonly string[],
  opts: {
    emptyLabel: string;
    home?: Destination;
    /** CAOS-363 — the headings become sort toggles. A list tab wants them; an attached set does not. */
    sortable?: boolean;
    /** The order currently applied, so the heading shows which column is doing the ordering. */
    sort?: { field: string; dir: 'asc' | 'desc' };
    /**
     * CAOS-407 — a button on every row, and what to do once one of them has changed something.
     *
     * Absent ⇒ no actions column at all, which is every caller before this ticket: a table that
     * grows an empty trailing column because a list declared no buttons has lost a column of room
     * for no reason.
     */
    rowActions?: { readonly actions: readonly RecordActionMeta[]; readonly onChanged: () => void };
  },
): HTMLElement {
  const objectHome = objectHomeMap(ctx.tabs, ctx.appKey, ctx.recordHomes);
  const table = document.createElement('caos-data-table');
  table.setAttribute('label', object.pluralLabel);
  table.setAttribute('empty-label', opts.emptyLabel);
  if (opts.sort) {
    // The table speaks 'asc' | 'desc' — the same words we do. Handing it the aria wording instead
    // made every heading click read as a FIRST click, so a second click never flipped the order.
    (table as unknown as { sort: unknown }).sort = { key: opts.sort.field, direction: opts.sort.dir };
  }

  const rowActionSpec = opts.rowActions;
  const drawsActions = (rowActionSpec?.actions.length ?? 0) > 0;
  (table as unknown as { columns: unknown }).columns = columns.map((apiName) => {
    const f = object.fields.get(apiName);
    const numeric = f?.storageType === 'Number' || f?.storageType === 'Rollup';
    // A formula column is computed on read, so its heading is not offered as a sort. A roll-up column is:
    // the Engine orders a page by its maintained value (CAOS-576).
    const sortable = opts.sortable === true && f !== undefined && f.storageType !== 'Formula';
    return { key: apiName, label: f?.label ?? apiName, align: numeric ? 'end' : 'start', sortable };
  });
  if (drawsActions) {
    (table as unknown as { columns: { push(c: unknown): void } }).columns.push({
      key: ROW_ACTIONS_COLUMN,
      label: 'Actions',
      align: 'end',
      sortable: false,
    });
  }

  (table as unknown as { rows: unknown }).rows = rows.map((record) => {
    const cells: Record<string, unknown> = {};
    for (const apiName of columns) {
      const f = object.fields.get(apiName);
      if (!f) {
        cells[apiName] = '';
        continue;
      }
      const value = formatValue(f, record, refIndex, ctx.meta.valueSets, objectHome);
      if (apiName === object.nameField) {
        // The record's name column always links into the record page.
        const href = recordAddress(opts.home ?? { appKey: ctx.appKey, tabKey: ctx.tabKey }, String(record.id));
        cells[apiName] = { node: recordLink(recordDisplayName(object, record), href, ctx.navigate) };
      } else if (f.storageType === 'Reference' && value.href) {
        cells[apiName] = { node: recordLink(value.text, value.href, ctx.navigate) };
      } else if (value.intent) {
        cells[apiName] = { node: badge(value) };
      } else {
        cells[apiName] = value.text;
      }
    }
    if (drawsActions) {
      // The same bar the record header draws, over THIS row's record: same runner, same checks,
      // same confirm. A row whose buttons all filter out gets an empty cell rather than no cell,
      // because a table whose rows have different numbers of cells is a broken table.
      const bar = recordActionBar(
        object,
        record,
        refIndex,
        ctx,
        rowActionSpec!.onChanged,
        { current: recordVersion(record) },
        { actions: rowActionSpec!.actions, placement: 'row' },
      );
      cells[ROW_ACTIONS_COLUMN] = { node: bar ?? document.createElement('span') };
    }
    return { id: String(record.id), cells };
  });
  return table;
}

/**
 * CAOS-361 — one attached set, READ: what the record page needs to draw it. A set whose read failed
 * carries `status: 'error'` and is SHOWN saying so, because "we could not read your contacts" and
 * "this account has no contacts" are different statements and a record page must never quietly turn
 * the first into the second.
 */
export interface ResolvedRelatedList {
  readonly id: string;
  readonly heading: string;
  readonly object: ObjectMeta;
  /**
   * Where a row links to — the CHILD's own home, so it opens where it belongs. A full destination
   * rather than a bare tab key, because an authored home may live in a DIFFERENT app from the one
   * this related list is being read in.
   */
  readonly home?: Destination;
  readonly columns: readonly string[];
  readonly rows: readonly ApiRecord[];
  readonly refIndex: ReadonlyMap<string, { name: string; object: string }>;
  readonly status: 'ok' | 'error';
  /**
   * CAOS-1726 — the template region the LAYOUT placed this set into, carried through unchanged
   * from the manifest. `undefined` ⇒ the layout named none, and it draws where it always has.
   */
  readonly region?: string;
  /** CAOS-407 — the buttons this list declares on each of its rows. Empty ⇒ no actions column. */
  readonly rowActions: readonly RecordActionMeta[];
  /** CAOS-407 — the buttons this list declares over the rows a person has ticked. */
  readonly selectionActions: readonly Extract<RecordActionMeta, { readonly kind: 'update' }>[];
}

/**
 * Read every attached set a record's layout names: for each, the child records whose
 * `relationshipField` points at THIS record. One query per set, all in flight together.
 *
 * A set naming an object that is not installed is skipped entirely rather than rendered empty — the
 * app asked for something that is not here, and an empty table would claim there are no such records
 * when the truth is there is no such object.
 */
export async function loadRelatedLists(
  baseObject: ObjectMeta,
  record: ApiRecord,
  catalog: ReadonlyMap<string, ObjectMeta>,
  /**
   * Where the reader is standing, which is what decides a child row's address. One object rather
   * than three loose positionals deliberately: the previous shape took `tabs` followed by
   * `fetchImpl`, and adding an app key between them let an existing call keep type-checking with
   * its fetch landing in the new parameter. The sets then came back empty rather than wrong, which
   * is the good version of that failure and not one to leave available.
   */
  where: { readonly tabs: readonly ShellNavTab[]; readonly appKey: string; readonly recordHomes?: ReadonlyMap<string, Destination> },
  fetchImpl: FetchImpl = fetch,
): Promise<ResolvedRelatedList[]> {
  // CAOS-166 — the attached sets are READ here and RENDERED in `renderRecordSurface`, so the record
  // type has to be resolved in both or the two disagree: a type whose layout names its own sets
  // would have them drawn as empty, because the fetch went looking for the base layout's. Same
  // record, same rule, same function.
  const object = objectForRecord(baseObject, record);
  const objectHome = objectHomeMap(where.tabs, where.appKey, where.recordHomes);
  const wanted = object.relatedLists
    .map((rl) => ({ rl, child: catalog.get(rl.object) }))
    .filter((e): e is { rl: RelatedListMeta; child: ObjectMeta } => e.child !== undefined);

  return Promise.all(
    wanted.map(async ({ rl, child }): Promise<ResolvedRelatedList> => {
      const declared = rl.columns?.length
        ? rl.columns
        : child.listColumns.length
          ? child.listColumns
          : [child.nameField];
      // The column that points back at THIS record is dropped: every row would repeat the record
      // you are already looking at.
      const columns = declared.filter((c) => c !== rl.relationshipField);
      const outcome = await queryRecordsOutcome(rl.object, fetchImpl, 200, {
        filters: [{ field: rl.relationshipField, op: 'eq', value: String(record.id) }],
      });
      const childRefIndex =
        outcome.status === 'ok' ? await buildReferenceIndex(child, catalog, fetchImpl) : new Map();
      const home = objectHome.get(rl.object);
      return {
        id: rl.id,
        heading: rl.label ?? child.pluralLabel,
        object: child,
        ...(rl.region ? { region: rl.region } : {}),
        ...(home ? { home } : {}),
        columns,
        rows: outcome.status === 'ok' ? outcome.records : [],
        refIndex: childRefIndex,
        status: outcome.status,
        // CAOS-407 — carried through as authored. Which of them a given person actually SEES is
        // decided per row by the same bar the header uses, because that is where the record and the
        // field permissions are: a button that sets a field this person cannot edit is not drawn.
        rowActions: rl.rowActions ?? [],
        selectionActions: rl.selectionActions ?? [],
      };
    }),
  );
}

/**
 * CAOS-367 — the merged activity timeline for one record, as the record page needs to draw it.
 *
 * THREE STATES, KEPT APART ON PURPOSE, because two of them look identical if you collapse them:
 *   • `ok` with entries — what has been done about this record;
 *   • `ok` with none — nothing has been done yet, which is a true and useful statement;
 *   • `error` / unavailable — we could not read it, or this org has no activity storage at all.
 * A record page that renders the last two as "no activity yet" tells somebody their notes are gone.
 * That is the same rule `ResolvedRelatedList.status` exists for, applied to a different read.
 */
export interface ActivityEntry {
  /**
   * CAOS-1587 — a plain string, not the three literals it used to be. The renderer has no business
   * knowing which kinds exist: every decision it used to make from this value is now read from the
   * matching {@link ActivityKindDescriptor} instead, so narrowing the type here would only let a
   * `switch` on it creep back in.
   */
  readonly kind: string;
  readonly id: string;
  readonly at: string | null;
  readonly record: ApiRecord;
}

/**
 * CAOS-1587 — how one kind is drawn, as the KIND declares it rather than as this file remembers it.
 *
 * Until now three decisions were made here: the headline read `title` for a note and `subject` for
 * anything else, the detail read `body`, `description` or `notes`, and the set of kinds to count was
 * a list in this file. All three were right for the kinds that ship and unreachable for any kind that
 * does not, so a fourth kind could not be drawn without editing this renderer — which lives in a
 * different repository on a different release cadence from the kernel that would define the kind.
 *
 * It arrives on the response rather than being looked up because it CANNOT be looked up from here.
 * The shipped kinds are standard objects defined in kernel TypeScript with no authored metadata rows,
 * so `projectMetadata` has no object body to read a declaration out of — which is exactly why this
 * file held the field names in the first place.
 */
export interface ActivityKindDescriptor {
  readonly kind: string;
  /** The field an entry of this kind is titled by. */
  readonly headlineField: string;
  /** The field holding the prose under the headline, or null for a kind that carries none. */
  readonly detailField: string | null;
}

export interface ResolvedActivity {
  readonly entries: readonly ActivityEntry[];
  readonly counts: Readonly<Record<string, number>>;
  readonly total: number;
  /** Kinds whose storage this org has never had provisioned — reported, never rendered as zero. */
  readonly unavailableKinds: readonly string[];
  /**
   * Every kind the timeline knows about and how to draw it, in the order the counts read.
   *
   * Empty only when the read FAILED. An `ok` timeline always carries every kind, including ones with
   * no entries, because the counts block names them all — so an empty list here and an `ok` status
   * together mean a kernel older than this field, and the renderer draws no counts rather than
   * guessing at a set.
   */
  readonly kinds: readonly ActivityKindDescriptor[];
  readonly status: 'ok' | 'error';
}

/**
 * One field change on this record, as `history list` sends it.
 *
 * This is a SUBSET of the kernel's `DataHistoryEntry` (kernel/src/observability/data-history.ts)
 * named field-for-field against it rather than renamed on the way in, so the two can be compared
 * by eye. The row also carries `id`, `orgId`, `correlationId`, `object`, `recordId` and `bypassKey`;
 * none of those is anything a person reading a record needs, so none is read here.
 */
export interface HistoryChange {
  /**
   * The field that changed, QUALIFIED — `account.amount`, not `amount`.
   *
   * Kept as sent. {@link ObjectMeta.fields} is keyed by the BARE api name, so resolving a label
   * means putting this through {@link bareFieldName} first; storing the bare name here instead
   * would quietly throw away which object the row belongs to.
   */
  readonly fieldKey: string;
  /** The value before. `null` on a create. */
  readonly oldValue: unknown;
  /** The value after. `null` on a delete, which is a tombstone rather than an erasure. */
  readonly newValue: unknown;
  /** Who changed it. `null` when the kernel could not resolve an actor — the wire type, kept. */
  readonly actor: string | null;
  /** When, ISO-8601. */
  readonly at: string;
  /** create | update | delete. */
  readonly changeKind: string;
}

/**
 * CAOS-402 — a record's field-change history, resolved for the record page.
 *
 * `status: 'error'` rather than a throw, for the reason the timeline beside it does the same: this
 * is one of several reads a record page makes in parallel, and a history the platform could not
 * fetch must not take the record down with it.
 *
 * THE EMPTY CASE AND THE FAILED CASE ARE DIFFERENT ANSWERS and the panel draws them differently.
 * An empty history is a claim ABOUT THE RECORD — nothing on it has ever changed — and a person
 * would believe it. A read that did not answer has to say so in words.
 */
export interface ResolvedHistory {
  readonly changes: readonly HistoryChange[];
  readonly status: 'ok' | 'error';
}

/**
 * The most field changes a record page draws before it stops.
 *
 * This bounds the WIRE and the DRAW, not the database read: `readFieldHistory` selects a record's
 * whole ordered history with no `LIMIT`, and the route applies `limit` to what came back. So
 * CAOS-402's third acceptance criterion — a record with a long history opens as fast as one
 * without — is NOT satisfied by this constant, and the ticket says so rather than implying it is.
 */
const HISTORY_PAGE_LIMIT = 50;

/**
 * Read this record's field-change history. Fail-soft, like every other read here.
 *
 * NEWEST FIRST, which is the opposite of the stream's own order: `readFieldHistory` ends
 * `ORDER BY at ASC, id ASC` because an append-only log is read forwards by a chain or an export,
 * and a person opening a record wants the last thing that happened to it. The reversal belongs
 * here, in the one reader that wants it, rather than in the kernel where ascending is correct.
 */
export async function loadRecordHistory(
  objectKey: string,
  recordId: string,
  fetchImpl: FetchImpl = fetch,
): Promise<ResolvedHistory> {
  const failed: ResolvedHistory = { changes: [], status: 'error' };
  try {
    const res = await fetchImpl(
      // `order=desc` is not a preference, it is the only correct request. The route caps with
      // `rows.slice(0, limit)` over an ASCENDING read, so an ascending page of 50 from a record
      // with 80 changes is changes 1-50 — and the change somebody just made, the reason they
      // opened the panel, is the one missing. Sorting that page descending here would have put
      // change 50 at the top and looked entirely correct. CAOS-402 added `desc` to the route for
      // this; at no limit could the newest changes be asked for before it.
      `${RECORD_HISTORY_PATH}?object=${encodeURIComponent(objectKey)}&recordId=${encodeURIComponent(
        recordId,
      )}&limit=${HISTORY_PAGE_LIMIT}&order=desc`,
      { method: 'GET', headers: { accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' },
    );
    // The route answers the standard '{ rows, nextCursor }' page of this file's sibling read
    // nouns -- 'rows', NOT 'entries'. Named off kernel/src/api/observability-routes.ts rather
    // than guessed: a renderer reading a key the route does not send draws an empty history, and
    // an empty history is the one thing this panel must never say by accident.
    const env = (await res.json()) as { ok?: boolean; data?: { rows?: unknown } };
    if (!res.ok || env?.ok !== true || !Array.isArray(env.data?.rows)) return failed;
    const changes: HistoryChange[] = [];
    let dropped = 0;
    for (const row of env.data.rows) {
      if (row === null || typeof row !== 'object') {
        dropped += 1;
        continue;
      }
      const r = row as Record<string, unknown>;
      const fieldKey = r['fieldKey'];
      const at = r['at'];
      // A row naming no field or carrying no timestamp is not a field change. It is DROPPED
      // rather than drawn, because a row with a blank field and a blank date reads as a defect
      // in the page. Every other value is taken as-is: 'oldValue' and 'newValue' are genuinely
      // of any type, and null is MEANINGFUL on both of them.
      if (typeof fieldKey !== 'string' || fieldKey === '' || typeof at !== 'string' || at === '') {
        dropped += 1;
        continue;
      }
      changes.push({
        fieldKey,
        oldValue: r['oldValue'] ?? null,
        newValue: r['newValue'] ?? null,
        actor: typeof r['actor'] === 'string' && r['actor'] !== '' ? r['actor'] : null,
        at,
        changeKind: typeof r['changeKind'] === 'string' && r['changeKind'] !== '' ? r['changeKind'] : 'update',
      });
    }
    // ROWS CAME BACK AND NOT ONE SURVIVED: that is a shape this reader does not understand, not a
    // record with no history. Reporting it as an empty OK would put "No field on this record has
    // changed yet." on a record that has changed — the exact false claim the three states exist to
    // keep apart, arrived at by a different road. A response that was genuinely empty is untouched
    // by this, because nothing was dropped.
    if (changes.length === 0 && dropped > 0) return failed;

    // NEWEST FIRST, in two steps that each have one job. The route already answers newest-first for
    // `order=desc`, so both steps are no-ops on a current kernel; they exist for a kernel older than
    // that parameter, which ignores it and answers ascending.
    //
    // STEP ONE normalises the DIRECTION by reversing the whole array, which is exactly what the
    // kernel does for `desc` — and reversing wholesale is the point. Every field changed by one save
    // shares an `at`, so ties are the normal case, and a whole-array reverse carries each tied group
    // round with it, landing on the same order the kernel's own `desc` produces. An earlier version
    // of this reversed ties inside a comparator instead; that was wrong, and wrong in the live
    // direction — against a `desc` response, whose ties arrive already reversed, it reversed them
    // back and reintroduced exactly the ascending-inside-descending order it was added to remove.
    const first = changes[0];
    const last = changes[changes.length - 1];
    if (first && last && first.at < last.at) changes.reverse();

    // STEP TWO repairs an array that is not ordered by `at` at all, which neither kernel sends but
    // which a proxy concatenating two pages would. Ties compare EQUAL here on purpose: the sort is
    // stable, so a tied group keeps the order step one put it in rather than being rearranged by a
    // rule that cannot know which field of one save came first — because none of them did.
    changes.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
    return { changes, status: 'ok' };
  } catch {
    return failed;
  }
}

/** How the three kinds are labelled on the page. The kernel key is never shown to a person. */
export const ACTIVITY_KIND_LABELS: Readonly<Record<string, { one: string; many: string }>> = {
  note: { one: 'Note', many: 'Notes' },
  task: { one: 'Task', many: 'Tasks' },
  call_log: { one: 'Call', many: 'Calls' },
};

// CAOS-1587 — the list of kinds that used to live here is gone. It is read off the response, because
// a list in this file is a list no org can extend.

/** The most timeline entries a record page draws before it stops. */
const ACTIVITY_PAGE_LIMIT = 50;

/**
 * Read the record's timeline. Fail-soft like every other read here: a transport or parse failure
 * becomes `status: 'error'`, which the panel SHOWS, rather than throwing into the render path.
 */
export async function loadActivityTimeline(
  objectKey: string,
  recordId: string,
  fetchImpl: FetchImpl = fetch,
): Promise<ResolvedActivity> {
  const failed: ResolvedActivity = { entries: [], counts: {}, total: 0, unavailableKinds: [], kinds: [], status: 'error' };
  try {
    const res = await fetchImpl(
      `${ACTIVITY_TIMELINE_PATH}?object=${encodeURIComponent(objectKey)}&recordId=${encodeURIComponent(recordId)}&limit=${ACTIVITY_PAGE_LIMIT}`,
      { method: 'GET', headers: { accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' },
    );
    const env = (await res.json()) as {
      ok?: boolean;
      data?: {
        entries?: ActivityEntry[];
        counts?: Record<string, number>;
        total?: number;
        unavailableKinds?: string[];
        kinds?: ActivityKindDescriptor[];
      };
    };
    if (env?.ok !== true || !Array.isArray(env.data?.entries)) return failed;
    const data = env.data!;
    return {
      entries: data.entries!,
      counts: data.counts ?? {},
      total: typeof data.total === 'number' ? data.total : data.entries!.length,
      unavailableKinds: Array.isArray(data.unavailableKinds) ? data.unavailableKinds : [],
      // Each descriptor is checked rather than trusted: this is a parsed response, and the rest of
      // this function treats every field of it as untrusted for the same reason. A descriptor missing
      // its headline field would render every entry of that kind blank, which is the defect CAOS-1587
      // closes — so a malformed one is dropped and its kind falls to the no-descriptor path.
      kinds: (Array.isArray(data.kinds) ? data.kinds : []).filter(
        (k): k is ActivityKindDescriptor =>
          !!k &&
          typeof k.kind === 'string' &&
          k.kind !== '' &&
          typeof k.headlineField === 'string' &&
          k.headlineField !== '' &&
          (k.detailField === null || typeof k.detailField === 'string'),
      ),
      status: 'ok',
    };
  } catch {
    return failed;
  }
}

/**
 * The headline a timeline entry shows — the field THIS KIND declares, not one this file chose.
 *
 * A kind with no descriptor falls to `(untitled)` rather than to a guess. That is deliberate: the old
 * code guessed `subject` for anything that was not a note, which is how a kind nobody had heard of got
 * drawn blank and looked like a data problem instead of a missing declaration. `(untitled)` for a kind
 * that declared nothing is at least honest about which of the two it is.
 */
function activityHeadline(entry: ActivityEntry, descriptor: ActivityKindDescriptor | undefined): string {
  const value = descriptor ? entry.record[descriptor.headlineField] : undefined;
  return typeof value === 'string' && value.trim() ? value : '(untitled)';
}

/** The secondary line: the prose the person actually wrote, from the field the kind names for it. */
function activityDetail(entry: ActivityEntry, descriptor: ActivityKindDescriptor | undefined): string {
  if (!descriptor || descriptor.detailField === null) return '';
  const value = entry.record[descriptor.detailField];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * A timeline instant, rendered. Falls back to the raw string rather than showing "Invalid Date" —
 * an unparseable stamp is still evidence that something happened, and hiding it would be worse.
 */
function formatActivityInstant(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Build the Activity panel: the per-kind counts, then the merged stream, newest first.
 *
 * The counts come from `counts`, NOT from the entries on screen — the panel draws the newest page
 * and the record may have far more, so counting what is rendered would quietly under-report.
 */
/**
 * CAOS-402 — the History panel: what a field used to be, what it became, who did it and when.
 *
 * Those four are AC1's own words, and each is one span of a row so that none of them can go
 * missing quietly. One row per field change, newest first.
 *
 * The field is named by its authored LABEL where the object declares one and by its key
 * otherwise. The fallback is not defensive padding: the stream outlives the schema, so a field
 * dropped from an object still has a history, and a blank name would make those rows unreadable
 * rather than merely plain.
 *
 * There is deliberately NO visibility branch here. A field the reader may not read is ABSENT
 * from the response rather than redacted in it, and a reader the record plane refused gets no
 * rows at all (CAOS-1658 / CAOS-515) — so the only rows that arrive are rows this person may
 * see, and a second check here would be a second answer to the same question: the one that
 * drifts.
 *
 * THE THREE STATES ARE DRAWN DIFFERENTLY ON PURPOSE. Collapsing the failed read into the empty
 * one would tell somebody their record has never been touched, which is evidence about the past
 * rather than a missing feature — someone could act on it.
 */
function buildHistoryPanel(history: ResolvedHistory, object: ObjectMeta): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'sa-sections sa-history';

  const sec = document.createElement('caos-detail-section');
  sec.setAttribute('heading', 'History');

  if (history.status === 'error') {
    const note = document.createElement('p');
    note.className = 'sa-history__unread';
    note.textContent =
      'The history of this record could not be read just now. Reload the page to try again.';
    sec.append(note);
    panel.append(sec);
    return panel;
  }

  if (history.changes.length === 0) {
    const note = document.createElement('p');
    note.className = 'sa-history__empty';
    // Not "no history": every record has one, and this one is empty. That is a statement about
    // the record, not about whether the platform is keeping track.
    note.textContent = 'No field on this record has changed yet.';
    sec.append(note);
    panel.append(sec);
    return panel;
  }

  // A FULL PAGE MAY NOT BE THE WHOLE HISTORY, and the panel says which it is showing rather than
  // letting the last row imply the record stops there. The wording is true either way: a record
  // with exactly this many changes really is showing its most recent that many. Said plainly here
  // instead of counting, because the route answers `nextCursor: null` and sends no total, so the
  // page being full is the only evidence available.
  if (history.changes.length >= HISTORY_PAGE_LIMIT) {
    const note = document.createElement('p');
    note.className = 'sa-history__capped';
    note.textContent = `Showing the ${HISTORY_PAGE_LIMIT} most recent changes.`;
    sec.append(note);
  }

  const list = document.createElement('ol');
  list.className = 'sa-history__entries';
  for (const change of history.changes) {
    // The response sends a QUALIFIED key and this object's catalog is keyed by the bare api
    // name, so the normalisation is what makes the lookup hit at all -- not a tidy-up.
    const bare = bareFieldName(change.fieldKey);
    const row = document.createElement('li');
    row.className = 'sa-history__entry';
    row.dataset['field'] = bare;
    row.dataset['kind'] = change.changeKind;

    const field = document.createElement('span');
    field.className = 'sa-history__field';
    field.textContent = object.fields.get(bare)?.label ?? change.fieldKey;
    row.append(field);

    const delta = document.createElement('span');
    delta.className = 'sa-history__change';
    delta.append(historyValue(change.oldValue, 'old'));
    const arrow = document.createElement('span');
    arrow.className = 'sa-history__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '\u2192';
    delta.append(arrow);
    delta.append(historyValue(change.newValue, 'new'));
    row.append(delta);

    const who = document.createElement('span');
    who.className = 'sa-history__actor';
    // An actor the kernel could not resolve is NAMED as unknown rather than left blank: a blank
    // cell reads as a rendering fault, and "who did it" is a quarter of what this panel is for.
    who.textContent = change.actor ?? 'Unknown';
    row.append(who);

    const when = document.createElement('time');
    when.className = 'sa-history__at';
    // The machine-readable attribute keeps the value EXACTLY as the route sent it; only the text a
    // person reads is trimmed. CAOS-516 routed this stream's `at` through the shared ISO projection,
    // which carries the microseconds Postgres actually stores, so the raw value went from
    // `...10.729Z` to `...10.729123Z` and this cell — which prints it verbatim — started showing six
    // fractional digits to a reader. The data is more precise and the display should not be: trim
    // the rendered text to whole seconds, and leave `datetime` untouched so nothing machine-side
    // loses precision it was given.
    when.setAttribute('datetime', change.at);
    when.textContent = displayInstant(change.at);
    row.append(when);

    list.append(row);
  }
  sec.append(list);
  panel.append(sec);
  return panel;
}

/**
 * One side of a change, as a cell.
 *
 * `null` is MEANINGFUL on both sides and is not an absence to hide: it is the empty side of a
 * create or a delete. An empty STRING is a different thing again — a field somebody CLEARED —
 * and the two must not be the same row to anything reading this DOM, even though both draw an
 * em-dash to a person.
 */
/**
 * The instant a person reads, from the instant the route sent.
 *
 * Trimmed to whole seconds rather than reformatted: this panel deliberately shows an unambiguous
 * UTC ISO-8601 instant rather than a localised date, and that decision is not being revisited here
 * — the only question is how many fractional digits belong in front of somebody. Sub-second
 * precision is a diagnostic detail, it is still in the `datetime` attribute for anything machine
 * side, and a field change is not an event anybody times to the microsecond.
 *
 * Anything that does not match is passed through untouched. A value this cannot parse is better
 * shown raw than silently blanked, and this must never be the reason a row renders empty.
 */
function displayInstant(at: string): string {
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d+)?(Z|[+-]\d{2}:\d{2})$/.exec(at);
  return m ? `${m[1]}${m[2]}` : at;
}

function historyValue(value: unknown, side: 'old' | 'new'): HTMLElement {
  const cell = document.createElement('span');
  cell.className = `sa-history__value sa-history__value--${side}`;
  if (value === null || value === undefined) {
    cell.dataset['absent'] = '';
    cell.textContent = '\u2014';
    return cell;
  }
  if (value === '') {
    cell.dataset['blank'] = '';
    cell.textContent = '\u2014';
    return cell;
  }
  // A JSON-valued field changing is still a change somebody may need to see, and String() would
  // have drawn "[object Object]" for every one of them.
  cell.textContent = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return cell;
}

/**
 * CAOS-1726 — one attached set as a titled section, extracted so the Related TAB and a set the
 * layout placed in its own region are drawn by the same code.
 *
 * Extracted rather than copied, deliberately. The body carries the CAOS-407 child-permission rule,
 * the empty-label wording and the selection-bar condition; a second copy of that beside it would be
 * two answers to what a related list is allowed to do, and the one nobody edited would be the one
 * that kept rendering. The text below is unchanged from the loop it came out of.
 */
function relatedListSection(
  set: ResolvedRelatedList,
  object: ObjectMeta,
  ctx: SurfaceContext,
  reload: () => void,
): HTMLElement {
      const sec = document.createElement('caos-detail-section');
      sec.setAttribute('heading', set.heading);
      if (set.status === 'error') {
        const note = document.createElement('p');
        note.className = 'sa-related__unread';
        note.textContent = `The ${set.heading.toLowerCase()} on this record could not be read just now. Reload the page to try again.`;
        sec.append(note);
      } else {
        // CAOS-407 — the CHILD's permissions, by the same rule the header's `create` button follows:
        // `actionPerms` deliberately excludes this object because `ctx.perms` already carries it, so
        // a self-referencing list (a task's sub-tasks) has to read the one and every other list the
        // other. An object nothing was read about gets NO_WRITE rather than the parent's rights,
        // because an undeclared permission is not a permissive one.
        const childCtx: SurfaceContext =
          set.object.key === object.key
            ? { ...ctx, perms: ctx.perms ?? NO_WRITE }
            : { ...ctx, perms: ctx.actionPerms?.get(set.object.key) ?? NO_WRITE };
        const childTable = buildRecordTable(set.object, set.rows, set.refIndex, childCtx, set.columns, {
          emptyLabel: `No ${set.heading.toLowerCase()} on this ${object.label.toLowerCase()} yet.`,
          ...(set.home ? { home: set.home } : {}),
          // CAOS-407 — the buttons this list declares. `reload` is the record page's own re-read, so
          // a row button that changes a child re-reads the sets rather than leaving the table
          // showing what the row said before the press.
          ...(set.rowActions.length ? { rowActions: { actions: set.rowActions, onChanged: reload } } : {}),
        });
        // CAOS-407 — rows become tickable only when there is something to do with a selection. A
        // checkbox column on a list with no selection action is a control that does nothing.
        if (set.selectionActions.length > 0) {
          const selectionBar = selectionActionBar(set.object, set.rows, childTable, childCtx, set.selectionActions, reload);
          if (selectionBar) {
            childTable.setAttribute('selectable', '');
            sec.append(selectionBar);
          }
        }
        sec.append(childTable);
      }
  return sec;
}

/**
 * CAOS-1726 — the blueprint a record's own contents are placed into, when there is one.
 *
 * `undefined` means this record draws exactly as it always has, and that is the common case rather
 * than a failure: an object with no record page publishes no region vocabulary, a blueprint with a
 * single region has nowhere else to put anything, and a layout that named no region has not asked
 * for any of this. Each of those is a real state, and none of them is an error.
 */
interface RecordRegionPlan {
  readonly template: string;
  readonly regions: readonly { readonly key: string; readonly label: string }[];
  readonly primary: string;
  readonly geometry?: {
    readonly areas: readonly string[];
    readonly cols: string;
    readonly stack: readonly string[];
  };
}

/**
 * The blueprint's PRIMARY region — `main` when declared, else the first.
 *
 * A local mirror of the kernel's `presentation/place.ts` `primaryRegionKey`, following the
 * convention `shell-frame.ts` already set with `primaryFrameRegionKey` rather than introducing a
 * cross-workspace import for four lines. The rule is stated identically in all three places; if it
 * ever needs to change it has to change in all three, which is why each says where the others are.
 */
function primaryRecordRegionKey(regions: readonly { readonly key: string }[]): string {
  return regions.find((r) => r.key === 'main')?.key ?? regions[0]?.key ?? 'main';
}

/** Resolve the record's blueprint, or `undefined` when nothing publishes regions for it. */
function recordRegionPlan(object: ObjectMeta, ctx: SurfaceContext): RecordRegionPlan | undefined {
  const bound = object.recordTemplate;
  if (!bound) return undefined;
  const entry = ctx.meta.pageTemplates.get(bound.template);
  // A blueprint the org does not hold is a dangling reference the deploy gate refuses; at render
  // time it degrades to today's surface rather than throwing, the same posture the page template
  // fail-safe takes for an unknown template id.
  if (!entry || entry.regions.length < 2) return undefined;
  return {
    template: bound.template,
    regions: entry.regions,
    primary: primaryRecordRegionKey(entry.regions),
    ...(entry.geometry ? { geometry: entry.geometry } : {}),
  };
}

/**
 * Which region a requested key actually resolves to. Mirrors the kernel placement engine's two
 * fail-safes: a thing that named NO region falls to the primary one (the defined default, not a
 * failure), and a thing naming a region the resolved blueprint does not declare also falls to
 * primary rather than vanishing. Nothing is ever dropped for naming the wrong region.
 */
function placedRecordRegion(requested: string | undefined, plan: RecordRegionPlan): string {
  if (requested === undefined) return plan.primary;
  return plan.regions.some((r) => r.key === requested) ? requested : plan.primary;
}

/**
 * CAOS-1726 — lay the record's regions out from the blueprint's OWN geometry, never a hardcoded
 * region set.
 *
 * This deliberately mirrors `renderSectionsByRegion` in `page-surface.ts` rather than inventing a
 * second arrangement, down to the class names (`sa-sections--regions`, `sa-region`) so both
 * surfaces inherit the CAOS-428 gutter and the start-alignment rule that `page-surface.test.ts`
 * pins. Three of its refinements are reproduced because each exists for a reason that applies here
 * identically, and each would have been a plausible thing to leave out:
 *
 *   - ONLY OCCUPIED REGIONS get a cell. A reserved grid cell with nothing in it reads as content
 *     that failed to arrive, not as a region the author left empty.
 *   - A ROW NO OCCUPIED CELL NAMES IS DROPPED. The row collapses but the gutters around it do not,
 *     so a record whose blueprint has a highlights band and whose layout places nothing there
 *     would open with a band of dead space above its content.
 *   - `grid-area` IS SET ONLY WHEN THE MATRIX NAMES THE REGION. A region placed on an area the
 *     matrix never mentions is auto-placed wherever the browser finds room, which is a silently
 *     wrong layout; leaving it unset puts the region in normal flow, which is merely plain.
 *
 * A blueprint with no geometry gets no matrix: its regions stack in declared order, which is the
 * documented meaning of absent geometry rather than an invented arrangement.
 */
function buildRecordRegionGrid(
  plan: RecordRegionPlan,
  nodesByRegion: ReadonlyMap<string, readonly HTMLElement[]>,
): HTMLElement {
  const body = document.createElement('div');
  body.className = 'sa-sections sa-sections--regions';
  body.dataset.template = plan.template;

  const occupied = plan.regions.filter((r) => (nodesByRegion.get(r.key)?.length ?? 0) > 0);
  const occupiedKeys = new Set(occupied.map((r) => r.key));
  const rows = (plan.geometry?.areas ?? []).filter((row) =>
    row
      .trim()
      .split(/\s+/)
      .some((cell) => occupiedKeys.has(cell)),
  );
  if (plan.geometry && rows.length > 0) {
    body.style.gridTemplateAreas = rows.map((row) => `"${row}"`).join(' ');
    body.style.gridTemplateColumns = plan.geometry.cols;
  }
  const named = new Set(
    rows.flatMap((row) => row.trim().split(/\s+/)).filter((cell) => cell !== '.'),
  );

  for (const region of occupied) {
    const el = document.createElement('div');
    el.className = 'sa-region';
    el.dataset['region'] = region.key;
    if (region.key === plan.primary) el.dataset['role'] = 'primary';
    if (rows.length > 0 && named.has(region.key)) el.style.gridArea = region.key;
    for (const node of nodesByRegion.get(region.key) ?? []) el.append(node);
    body.append(el);
  }
  return body;
}

function buildActivityPanel(activity: ResolvedActivity, objectLabel: string): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'sa-sections sa-activity';

  const sec = document.createElement('caos-detail-section');
  sec.setAttribute('heading', 'Activity');

  if (activity.status === 'error') {
    const note = document.createElement('p');
    note.className = 'sa-activity__unread';
    note.textContent = 'The activity on this record could not be read just now. Reload the page to try again.';
    sec.append(note);
    panel.append(sec);
    return panel;
  }

  // The counts per kind — the thing the ticket asks for by name. Iterated over what the response
  // declared, not over a list held here, so a kind this renderer has never heard of is counted.
  const byKind = new Map(activity.kinds.map((d) => [d.kind, d]));

  // The SET still comes from the response and never from a list held here, but it comes from more
  // than one of its fields. The descriptors are canonical and give the order; a kind the response
  // COUNTS without declaring is counted anyway. That second half is not hypothetical: a kernel
  // older than this change serves counts and no descriptors at all, and taking the set from the
  // descriptors alone would have shown an empty block where real numbers had arrived. Drawing a
  // count needs a label and a number, not a field declaration — the label falls back to the key.
  const countedKinds: string[] = [];
  const seenKind = new Set<string>();
  const addKind = (kind: unknown): void => {
    if (typeof kind !== 'string' || kind === '' || seenKind.has(kind)) return;
    seenKind.add(kind);
    countedKinds.push(kind);
  };
  // A count is a whole number of things and cannot be negative, so that — not `typeof number` — is
  // the test. `typeof` alone admits NaN, Infinity and -1, none of which is a count, and each of which
  // reaches the page as a row reading "Tasks: NaN" or "Notes: -1". JSON cannot carry NaN or Infinity,
  // but a negative or fractional count from a kernel computing one wrongly is ordinary enough.
  const isCount = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0;

  for (const descriptor of activity.kinds) addKind(descriptor.kind);
  // counts is parsed from the wire and unvalidated, so a key whose value is not a count does not
  // earn a row of its own — it is not evidence that the kind exists.
  for (const [kind, n] of Object.entries(activity.counts)) if (isCount(n)) addKind(kind);
  for (const kind of activity.unavailableKinds) addKind(kind);

  // A kind that earned its row from a DESCRIPTOR still gets one even if its count is malformed, and
  // then reads 0 — the same fallback the absent case has always had. The check belongs here as well
  // as above: filtering only the set would still draw "Notes: -1" for a kind the descriptors named.
  const countFor = (kind: string): number => {
    const n = (activity.counts as Record<string, unknown>)[kind];
    return isCount(n) ? n : 0;
  };

  const counts = document.createElement('ul');
  counts.className = 'sa-activity__counts';
  for (const kind of countedKinds) {
    // A kind with no label shows its own key rather than nothing. Labels are still held here, and
    // that is a smaller gap than the field names were: an unlabelled kind reads oddly, where an
    // undrawable one could not be read at all. Naming them is its own change.
    const labels = ACTIVITY_KIND_LABELS[kind] ?? { one: kind, many: kind };
    const li = document.createElement('li');
    li.className = 'sa-activity__count';
    li.dataset.kind = kind;
    if (activity.unavailableKinds.includes(kind)) {
      // NOT rendered as "0": this org has no storage for this kind, so zero would be a
      // measurement nobody took. "Unavailable" and "none" are different sentences.
      li.textContent = `${labels.many}: unavailable`;
      li.dataset.unavailable = 'true';
    } else {
      const n = countFor(kind);
      li.textContent = `${n === 1 ? labels.one : labels.many}: ${n}`;
    }
    counts.append(li);
  }
  sec.append(counts);

  if (activity.entries.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'sa-activity__empty';
    empty.textContent =
      activity.unavailableKinds.length > 0
        ? `Activity is not set up for this organization yet, so nothing can be shown for this ${objectLabel.toLowerCase()}.`
        : `Nothing has been done about this ${objectLabel.toLowerCase()} yet.`;
    sec.append(empty);
    panel.append(sec);
    return panel;
  }

  const list = document.createElement('ol');
  list.className = 'sa-activity__list';
  for (const entry of activity.entries) {
    const item = document.createElement('li');
    item.className = 'sa-activity__entry';
    item.dataset.kind = entry.kind;

    const kindTag = document.createElement('span');
    kindTag.className = 'sa-activity__kind';
    kindTag.textContent = ACTIVITY_KIND_LABELS[entry.kind]?.one ?? entry.kind;
    item.append(kindTag);

    const headline = document.createElement('span');
    headline.className = 'sa-activity__headline';
    headline.textContent = activityHeadline(entry, byKind.get(entry.kind));
    item.append(headline);

    if (entry.at) {
      const when = document.createElement('time');
      when.className = 'sa-activity__when';
      when.dateTime = entry.at;
      when.textContent = formatActivityInstant(entry.at);
      item.append(when);
    }

    const detail = activityDetail(entry, byKind.get(entry.kind));
    if (detail) {
      const body = document.createElement('p');
      body.className = 'sa-activity__detail';
      body.textContent = detail;
      item.append(body);
    }
    list.append(item);
  }
  sec.append(list);
  panel.append(sec);
  return panel;
}

/**
 * CAOS-366 — the tag strip: the labels on this record, beside its name, and the control that adds
 * one.
 *
 * WHY IT IS IN THE HEADER. A tag is an attribute of the record the way its name is, not a thing
 * attached to it the way a note is — which is why this sits by the heading rather than becoming a
 * fourth tab. It is also why the strip renders when the record has NO labels: the whole point of
 * the ticket is that anything can be grouped, and a surface that appears only once somebody has
 * already tagged something can never be used to tag the first thing.
 *
 * Every control here is gated on `canCreate` for `tag_assignment`, asked of the Engine rather than
 * assumed — see {@link SurfaceContext.perms}. Absent means not offered, which is CAOS-360's rule.
 */
function renderTagStrip(
  object: ObjectMeta,
  record: ApiRecord,
  tags: ResolvedTags,
  ctx: SurfaceContext,
  objectHome: ReadonlyMap<string, Destination>,
  reload: () => void,
): HTMLElement | null {
  // A failed read renders nothing at all. An org that has not granted `tag` is the ordinary case
  // of this, and a record page that says "tags could not be loaded" to everybody in such an org
  // would be noise about a feature they do not have.
  if (tags.status !== 'ok') return null;

  const strip = document.createElement('div');
  strip.className = 'sa-tags';

  const recordId = String(record.id);

  // Where a label leads: the other records carrying it. Rendered under the strip on demand rather
  // than always, because the answer is a second round of reads and most visits to a record page
  // are not asking it.
  const answer = document.createElement('div');
  answer.className = 'sa-tags__answer';
  answer.hidden = true;

  let openTagId = '';
  const showTagged = (tag: RecordTag): void => {
    if (openTagId === tag.id) {
      openTagId = '';
      answer.hidden = true;
      answer.replaceChildren();
      return;
    }
    openTagId = tag.id;
    answer.hidden = false;
    answer.replaceChildren(document.createTextNode(`Finding what else is tagged ${tag.name}…`));
    void (async () => {
      const found = await loadTaggedRecords(tag.id, recordId, ctx.fetchImpl ?? fetch);
      // The person may have pressed another label while this was in flight; the answer on screen
      // must be the one they last asked for, not whichever read finished last.
      if (openTagId !== tag.id) return;
      answer.replaceChildren();
      const heading = document.createElement('p');
      heading.className = 'sa-tags__answer-heading';
      heading.textContent =
        found.length === 0 ? `Nothing else is tagged ${tag.name}.` : `Also tagged ${tag.name}`;
      answer.append(heading);
      if (found.length === 0) return;

      const list = document.createElement('ul');
      list.className = 'sa-tags__answer-list';
      for (const hit of found) {
        const item = document.createElement('li');
        const tab = objectHome.get(hit.object);
        // A record in an object this app does not show gets named but not linked — the link would
        // lead nowhere, and pretending otherwise is worse than saying plainly that it exists.
        if (tab) {
          const link = document.createElement('a');
          link.href = recordAddress(tab, String(hit.recordId));
          link.textContent = hit.name || hit.recordId;
          link.addEventListener('click', (ev) => {
            ev.preventDefault();
            ctx.navigate?.(link.getAttribute('href')!);
          });
          item.append(link);
        } else {
          item.textContent = hit.name || hit.recordId;
        }
        list.append(item);
      }
      answer.append(list);
    })();
  };

  for (const tag of tags.tags) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'sa-tags__chip';
    chip.textContent = tag.name;
    chip.setAttribute('aria-label', `Show what else is tagged ${tag.name}`);
    chip.addEventListener('click', () => showTagged(tag));
    strip.append(chip);
  }

  if (tags.canAttach) {
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'sa-tags__chip sa-tags__chip--add';
    add.textContent = 'Add tag';
    add.addEventListener('click', () => {
      const typed = window.prompt(`Tag this ${object.label.toLowerCase()} with:`);
      if (typed === null) return;
      const wanted = typed.trim();
      if (!wanted) return;
      // Already on the record: say so and change nothing, rather than writing a second assignment
      // that would render as the same chip.
      if (tags.tags.some((t) => t.name.toLowerCase() === wanted.toLowerCase())) {
        raiseToast(`This ${object.label.toLowerCase()} is already tagged ${wanted}.`, 'success');
        return;
      }
      add.disabled = true;
      void (async () => {
        const fetchImpl = ctx.fetchImpl ?? fetch;
        const label = await findOrCreateTag(wanted, fetchImpl);
        const attached = label ? await attachTag(label.id, object.key, recordId, fetchImpl) : false;
        add.disabled = false;
        if (!attached) {
          raiseToast('That tag could not be added. Check your connection and try again.', 'danger');
          return;
        }
        // Re-read rather than push the chip on locally: the label may have existed already under a
        // different spelling, and the strip must show what the record actually says.
        reload();
      })();
    });
    strip.append(add);
  }

  // Nothing to show and nothing to offer — a strip of one empty row helps nobody.
  if (strip.childElementCount === 0) return null;

  const holder = document.createElement('div');
  holder.className = 'sa-tags__holder';
  holder.append(strip, answer);
  return holder;
}

/**
 * CAOS-405 — how many `compactLayout` entries the highlights band shows, COUNTING the first (the
 * record's title, which the header already shows as its heading). The incumbent's rule: the first
 * seven on a desktop, the first four on a phone. The kernel's assembled page applies the same rule
 * (`HIGHLIGHTS_SHOWN` in kernel/src/presentation/assemble.ts), and a test holds the two numbers equal.
 *
 * The two apply the phone count differently, and on purpose. The kernel is handed a form factor, so
 * it resolves only the phone's band. This screen is one page that resizes, so it draws the desktop's
 * band and marks the cells past the phone count for the stylesheet to hide at the header's phone
 * width. At either width the fields a reader sees are the kernel's set for that form factor.
 */
export const HIGHLIGHTS_SHOWN = { desktop: 7, mobile: 4 } as const;

/**
 * One field drawn read-only, label above value. The record body's cell and the highlights band's cell
 * are the same drawing, so a field reads identically in both: a reference links to its record, a value
 * with an intent shows its badge, and an empty value is left off so the row shows its placeholder.
 */
function readOnlyFieldRow(
  f: FieldMeta,
  record: ApiRecord,
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  ctx: SurfaceContext,
  objectHome: ReadonlyMap<string, Destination>,
): HTMLElement {
  const row = document.createElement('caos-key-value-row');
  row.setAttribute('label', f.label);
  row.setAttribute('stacked', '');
  row.setAttribute('no-border', '');
  const value = formatValue(f, record, refIndex, ctx.meta.valueSets, objectHome);
  if (f.storageType === 'Reference' && value.href && value.text) {
    row.append(recordLink(value.text, value.href, ctx.navigate));
  } else if (value.intent && value.text) {
    row.append(badge(value));
  } else if (value.text) {
    row.setAttribute('value', value.text);
  }
  // Empty value: leave the row with no value → the component shows its em-dash placeholder.
  return row;
}

/**
 * CAOS-405 — the highlights band, for the record header's `fields` slot. It draws the entries of the
 * object's `compactLayout` after the first (the title), in order and each once, until a desktop's band
 * is full. An entry is skipped when the object does not define it or when the record read left it out:
 * the read omits every field the reader cannot read, so an absent key IS an unreadable field, and
 * skipping it lets the next named field take its place. A key that is present with no value is a
 * readable empty field and keeps its place. Cells past what a phone shows are marked, and the
 * stylesheet hides them at the record header's phone width; the band itself always draws.
 * `null` when there is nothing to draw.
 */
function renderHighlightsBand(
  object: ObjectMeta,
  record: ApiRecord,
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  ctx: SurfaceContext,
  objectHome: ReadonlyMap<string, Destination>,
): HTMLElement | null {
  const named = object.compactLayout?.fields ?? [];
  const seen = new Set<string>(named.slice(0, 1));
  const band = document.createElement('div');
  band.className = 'sa-highlights';
  band.slot = 'fields';
  for (const name of named.slice(1)) {
    if (band.childElementCount >= HIGHLIGHTS_SHOWN.desktop - 1) break;
    if (seen.has(name)) continue;
    const f = object.fields.get(name);
    if (!f || !Object.prototype.hasOwnProperty.call(record, name)) continue;
    seen.add(name);
    const row = readOnlyFieldRow(f, record, refIndex, ctx, objectHome);
    if (band.childElementCount >= HIGHLIGHTS_SHOWN.mobile - 1) row.dataset['highlightBeyondPhone'] = '';
    band.append(row);
  }
  return band.childElementCount > 0 ? band : null;
}

/** Render the RECORD surface for one record: a record header + the page-layout sections of fields. */
export function renderRecordSurface(
  baseObject: ObjectMeta,
  record: ApiRecord,
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  ctx: SurfaceContext,
  related: readonly ResolvedRelatedList[] = [],
  // CAOS-367 — what people DID about this record. Optional and defaulted so every existing caller
  // (and every existing test) renders exactly as before; a caller that read a timeline gets the tab.
  activity?: ResolvedActivity,
  // CAOS-366 — the labels on this record. Optional for the same reason as `activity` above: a
  // caller that did not read them gets the page it always got, with no tag strip at all. That is
  // deliberately not the same as an empty read, which DOES render the strip so somebody can add
  // the first label — a record nobody has tagged still has to offer tagging.
  tags?: ResolvedTags,
  // CAOS-402 — this record's field-change history. Optional and LAST for the same reason as
  // `activity` and `tags`: every existing caller and every existing test renders exactly as it
  // did. Absent is not the same as empty — absent draws no tab at all, an empty read draws the
  // tab and says nothing has changed.
  history?: ResolvedHistory,
): HTMLElement {
  // CAOS-166 — resolve which record type THIS record is once, at the top, and draw from the layout
  // that type names. The parameter is deliberately renamed rather than reassigned: every read below
  // goes through `object`, so there is no path left through this function that reaches the base
  // projection by accident, and a later edit cannot reintroduce one without renaming it back.
  const object = objectForRecord(baseObject, record);
  const objectHome = objectHomeMap(ctx.tabs, ctx.appKey, ctx.recordHomes);
  const perms = ctx.perms;
  const wrap = document.createElement('div');
  wrap.className = 'sa-surface sa-surface--record';

  // Breadcrumb: App › Plural (→ list) › this record.
  const crumbs = document.createElement('caos-breadcrumb');
  (crumbs as unknown as { items: unknown }).items = [
    { label: object.pluralLabel, href: listAddress({ appKey: ctx.appKey, tabKey: ctx.tabKey }) },
    { label: recordDisplayName(object, record) },
  ];
  crumbs.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null;
    const anchor = target?.closest('a');
    const href = anchor?.getAttribute('href');
    if (href && href.startsWith('/')) {
      ev.preventDefault();
      ctx.navigate?.(href);
    }
  });
  wrap.append(crumbs);

  const head = document.createElement('caos-record-header');
  head.setAttribute('object-label', object.label);
  head.setAttribute('heading', recordDisplayName(object, record));
  head.setAttribute('no-icon', '');
  // CAOS-369 — the status badge, drawn for the field the record PAGE names and for no other.
  //
  // This used to search the object for whichever field carried a value set and was called `status`
  // or `stage`. That made the badge the one thing in this header an org could neither ask for nor
  // decline — every other element here is authored — so a `phase` or `state` field got nothing and
  // an unrelated picklist named `status` got a badge nobody wanted. The name match is gone rather
  // than kept as a fallback: a fallback would mean the old surface still renders for exactly the
  // orgs that never authored the key, which is the behaviour this ticket exists to remove, and it
  // would leave `statusField` unable to express "no badge".
  //
  // No `valueSet` requirement either. The tint comes from the value's own declared display intent,
  // so a field without one paints as a plain badge rather than not at all; refusing to draw it here
  // would be this renderer overruling what the page asked for.
  const namedStatus = object.statusField?.field;
  const statusField = namedStatus ? object.fields.get(namedStatus) : undefined;
  if (statusField) {
    const value = formatValue(statusField, record, refIndex, ctx.meta.valueSets, objectHome);
    if (value.text) {
      const badgeNode = badge(value);
      const holder =
        badgeNode instanceof HTMLElement ? badgeNode : (() => {
          const s = document.createElement('span');
          s.append(badgeNode);
          return s;
        })();
      holder.setAttribute('slot', 'status');
      head.append(holder);
    }
  }
  // CAOS-1882 — the quiet second line under the record's name: the ONE field the record page names
  // in `subtitleField`, and no other. Resolved exactly as the badge above resolves `statusField`,
  // because it is the same kind of key — one field ref belonging to the page rather than to the
  // object — and a header whose two authored lines were found by two different rules would be a
  // header an author cannot predict.
  //
  // Three things each draw NO LINE rather than a blank one or an em-dash:
  //
  //   * the page names no field                  → `namedSub` is undefined
  //   * the object has no such field              → `fields.get` misses
  //   * the READER cannot see it, or it is empty  → the two guards below
  //
  // The reader's permission is not asked here because nothing on this surface can ask it: field
  // read permission reaches the host as an ABSENT KEY in the record payload, which is why
  // `renderHighlightsBand` above pairs `fields.get` with `hasOwnProperty` and why this does too.
  // Resolving against `object.fields` alone would draw the label of a field whose value was
  // withheld, as an empty line — the one outcome worse than no line, because it would be visible
  // evidence of a field the reader is not allowed to read.
  //
  // The empty check is on the FORMATTED text, not on the raw value, and the boundary that draws is
  // exactly `formatValue`'s own: `null`, `undefined` and `''` come back as `''` and draw nothing,
  // while `0` and `false` come back as "0" and "false" and DO draw. That second half is deliberate —
  // a numeric or boolean field named here is a field somebody chose, and suppressing a legitimate
  // zero would be this renderer deciding the value was not worth saying.
  //
  // What it does NOT do is turn an unresolvable value into no line. A reference whose target is gone,
  // and a value-set code with no matching option, both fall back to `String(raw)` inside
  // `formatValue` — so this line can draw a raw id. That is shared behaviour with the highlights band
  // and the status badge rather than anything this key introduces, it is not what "empty or
  // unreadable" in CAOS-1882 means (a withheld field is an absent KEY, handled above), and making the
  // subtitle alone second-guess `formatValue` would put one line of the header on a different
  // formatting rule from every other value on the surface. Left as it is, said out loud here.
  //
  // In a labelled cell in the body a dash is the honest answer, because the label has already
  // promised the field; here nothing has promised anything, so an empty line is a gap in the header
  // rather than an answer in it. `caos-record-header` holds the other half of that rule structurally
  // — `.sub:empty { display: none }` and a trimmed `textContent` — so neither side relies on the
  // other remembering.
  const namedSub = object.subtitleField?.field;
  const subField = namedSub ? object.fields.get(namedSub) : undefined;
  if (subField && Object.prototype.hasOwnProperty.call(record, namedSub!)) {
    const subText = formatValue(subField, record, refIndex, ctx.meta.valueSets, objectHome).text.trim();
    if (subText) head.setAttribute('sub', subText);
  }
  // CAOS-405 — the highlights band: the fields the object's record page names, under the record name.
  const highlights = renderHighlightsBand(object, record, refIndex, ctx, objectHome);
  if (highlights) head.append(highlights);
  // CAOS-362 — the record's buttons, in the header beside its name. Re-reads the record when one
  // of them changes it, so the badge, the fields and the remaining buttons all agree with what the
  // record now says.
  const reload = (): void => {
    void (async () => {
      const fetchImpl = ctx.fetchImpl ?? fetch;
      // Both reads at once. The attached sets are filtered by this record's id, which an action
      // never changes, so there is nothing to wait for between them — and a record still saying the
      // old thing two seconds after somebody pressed the button reads as a page that did not hear.
      const [outcome, nextRelated, nextActivity, nextTags, nextHistory] = await Promise.all([
        getRecordOutcome(object.key, String(record.id), fetchImpl),
        loadRelatedLists(object, record, ctx.meta.objects, ctx, fetchImpl),
        // CAOS-367 — re-read alongside them. An action that logged a call must not leave the
        // timeline showing the state before it, which is the same reason the record itself is re-read.
        loadActivityTimeline(object.key, String(record.id), fetchImpl),
        // CAOS-366 — and the labels, for the same reason: this is the path a newly attached tag
        // comes back through, so a strip rendered from the pre-write read would omit it.
        tags ? loadRecordTags(object.key, String(record.id), fetchImpl) : Promise.resolve(undefined),
        // CAOS-402 — and the history, which is the read with the STRONGEST claim to be re-run here:
        // an action that changed a field has just written the row this panel exists to show, so a
        // panel rendered from the pre-write read is missing exactly the change you came back to see.
        // Conditional on the caller having had one, like the tags above, so a surface rendered
        // without history does not acquire a tab by pressing a button.
        history ? loadRecordHistory(object.key, String(record.id), fetchImpl) : Promise.resolve(undefined),
      ]);
      if (outcome.status !== 'ok' || outcome.record === null) return;
      wrap.replaceWith(
        renderRecordSurface(object, outcome.record, refIndex, ctx, nextRelated, nextActivity, nextTags, nextHistory),
      );
    })();
  };
  // CAOS-368 — ONE version cell for this record page, declared before the first thing that uses
  // it. Both the action bar and every editable field below close over it, so an inline edit and a
  // row action state the same version instead of each holding its own snapshot of the load.
  const version: RecordVersionHolder = { current: recordVersion(record) };
  // CAOS-561 - and what Edit MEANS here, resolved ONCE and read by both halves of the answer: the
  // button, which resolves the identical question from the identical inputs inside `actionOffer`,
  // and the fields below. `mayEditRecord` is the permission that governs editing a record, and it
  // is what a replacement is allowed to replace the affordance for - never to grant.
  const editVerb = resolveStandardVerb('edit', object, record, refIndex, ctx, reload, mayEditRecord(ctx), version);
  const actionBar = recordActionBar(object, record, refIndex, ctx, reload, version);
  if (actionBar) head.append(actionBar);
  wrap.append(head);

  // CAOS-366 — the labels, directly under the heading. Appended to the surface rather than into
  // `caos-record-header` because the header's own slots are the status badge and the action bar,
  // and a chip row that has to reflow under both belongs outside it.
  if (tags) {
    const strip = renderTagStrip(object, record, tags, ctx, objectHome, reload);
    if (strip) wrap.append(strip);
  }

  // CAOS-361 — Details, and Related when the record has anything attached to it. The second tab is
  // offered only when there is something behind it: a Related tab that is always empty teaches people
  // not to look.
  // CAOS-1726 - what the LAYOUT asked to sit beside the body, resolved against the blueprint the
  // bound record page names. Everything that resolves to the primary region behaves exactly as it
  // did before this ticket; only what was deliberately placed elsewhere is lifted out of the tabs.
  const regionPlan = recordRegionPlan(object, ctx);
  const placedOutOf = <T,>(items: readonly T[], regionOf: (item: T) => string | undefined): T[] =>
    regionPlan === undefined
      ? []
      : items.filter((item) => placedRecordRegion(regionOf(item), regionPlan) !== regionPlan.primary);
  const sectionsPlacedOut = new Set(placedOutOf(object.sections, (sec) => sec.region));
  const listsPlacedOut = new Set(placedOutOf(related, (set) => set.region));
  const panelRegions = new Map<'activity' | 'history', string>();
  if (regionPlan) {
    for (const entry of object.panels ?? []) {
      const region = placedRecordRegion(entry.region, regionPlan);
      if (region !== regionPlan.primary) panelRegions.set(entry.panel, region);
    }
  }
  // The tab strip is assembled exactly as before over whatever REMAINS in the primary region.
  const tabRelated = related.filter((set) => !listsPlacedOut.has(set));
  const activityInTabs = activity !== undefined && !panelRegions.has('activity');
  const historyInTabs = history !== undefined && !panelRegions.has('history');
  const anythingPlacedOut =
    sectionsPlacedOut.size > 0 || listsPlacedOut.size > 0 || panelRegions.size > 0;

  const tabStrip = document.createElement('caos-tab-strip');
  tabStrip.setAttribute('aria-label', 'Record');
  // CAOS-367 — Activity is offered whenever the timeline was READ, including when it came back
  // empty or failed. That is deliberately unlike Related above, which hides itself when there is
  // nothing behind it: an empty Related tab teaches people not to look, but Activity is where a
  // person GOES to add the first note, so hiding it until one exists would hide the way in. A
  // failed read must be reachable for the same reason — the panel says so in words.
  const tabItems: { value: string; label: string }[] = [{ value: 'details', label: 'Details' }];
  if (tabRelated.length > 0) tabItems.push({ value: 'related', label: 'Related' });
  // CAOS-1726 - a panel the layout placed beside the body is NOT also offered as a tab. Drawing it
  // in both places would be the same history twice, and the tab would be the one people kept
  // using - which is the state this ticket exists to end.
  if (activityInTabs) tabItems.push({ value: 'activity', label: 'Activity' });
  // CAOS-402 — History is offered whenever it was READ, on the same rule as Activity above and
  // for the same two reasons: an empty history is a real answer about the record, and a failed
  // read has to be REACHABLE to be reported. Withholding the tab on failure would make an
  // unreadable history look like a platform that does not keep one.
  if (historyInTabs) tabItems.push({ value: 'history', label: 'History' });
  (tabStrip as unknown as { items: unknown }).items = tabItems;
  tabStrip.setAttribute('value', 'details');

  /**
   * CAOS-1726 - one layout section as a titled card of fields.
   *
   * A closure rather than a module function because it reads eight values resolved once per
   * surface (`editVerb`, `version`, `objectHome`, `refIndex` and the rest), and threading those
   * through a free function would be worse than this in every way.
   *
   * EXTRACTED FOR THE REASON `relatedListSection` WAS, and the review that asked for it was right
   * that leaving two copies undermined that argument: the placed-out builder was a second copy of
   * this loop, carrying the CAOS-360/CAOS-561 resolution comment and the `--sa-cols` default, and
   * the first edit to either would have missed the other.
   */
  const drawSection = (section: LayoutSection): HTMLElement | null => {
    const sec = document.createElement('caos-detail-section');
    sec.setAttribute('heading', section.label);
    const grid = document.createElement('div');
    grid.className = 'sa-field-grid';
    // CAOS-1884 — a section says either a COUNT or `'auto'`, and the two are answered in different
    // places on purpose. A count is a number this renderer knows now, so it goes on the element as
    // `--sa-cols`. `'auto'` is not a number at all: how many columns fit is decided by the width
    // at paint time, which only the stylesheet is in a position to know. So the renderer states
    // WHICH RULE APPLIES and says nothing about the count, rather than guessing one here and
    // writing a number the layout never authored.
    if (section.columns === 'auto') grid.classList.add('sa-field-grid--auto');
    else grid.style.setProperty('--sa-cols', String(section.columns || 2));
    for (const placement of section.fields) {
      const f = object.fields.get(placement.name);
      // CAOS-1883 - BOTH halves of the rule the highlights band already applies, which this loop
      // only ever applied the first of. `!f` is "the object does not define it". The second is "the
      // record read left the key out", and an absent key IS A FIELD THIS READER MAY NOT SEE:
      //
      //   serializeContextFor  builds `fields` from shape.typeInfos filtered by canReadField
      //                        (kernel/src/api/data-routes.ts) - unreadable fields are not in it
      //   serializeRecord      writes out[key] for EVERY field in that map, nulls included
      //                        (kernel/src/api/record-serialization.ts)
      //
      // so unreadable means the key is absent, and readable-but-empty means the key is present
      // holding null. Those two are the cases this ticket exists to tell apart, and they are
      // distinguishable only by presence - which is why the test is hasOwnProperty and not a
      // truthiness check on the value. `null`, `''` and `0` are all readable empties and keep
      // their place; the em-dash is theirs and means "nothing here", not "not for you".
      //
      // Without this, an unreadable field reached the grid, found no key, and drew the same
      // em-dash row as a readable empty one - the record telling a person "this is empty" when the
      // truth was "you may not see this".
      //
      // THE SYSTEM FIELDS ARE THE ONE EXCEPTION, and they are exempt because for them absence does
      // NOT mean unreadable. `created_at`, `updated_at` and `owner_id` are serialized OUTSIDE the
      // canReadField loop and each is gated on being non-null:
      //
      //   if (record.created_at != null) out[CREATED_AT_KEY] = record.created_at;
      //   if (record.updated_at != null) out[UPDATED_AT_KEY] = record.updated_at;
      //   if (record.owner_id   != null) out[OWNER_ID_KEY]   = record.owner_id;
      //       - kernel/src/api/record-serialization.ts, CAOS-365 and CAOS-1704
      //
      // So an absent system key means the platform has no value for it, and that is a real and
      // ordinary state rather than a permission one: a configuration-backed object has `owner_id`
      // null "by construction - configuration has no owner" (kernel's MetadataRecordStore), and
      // the audit times are deliberately absent rather than null on the generic substrate, so that
      // a surface can tell "this platform does not keep them" from "it does and they are empty"
      // (CAOS-365, the comment on those two fields).
      //
      // Treating them by presence would therefore silently delete the Owner row from every
      // configuration-backed record for a reader who may read everything, which is the ticket's
      // fourth acceptance criterion pointing the wrong way. They keep the behaviour they had: the
      // row is drawn, and with no value it shows the em-dash, which for these three is the honest
      // answer. `system` is the field's own declaration of this (metadata-source.ts SYSTEM_FIELDS),
      // so a fourth system field added later is exempt without anyone having to remember this.
      if (!f) continue;
      if (f.system !== true && !Object.prototype.hasOwnProperty.call(record, placement.name)) continue;
      // CAOS-360 and CAOS-561 - THREE answers decide whether a field converts in place, and they
      // are written out where they are read, in `detailFieldNode`. Two of them are unchanged: the
      // SURFACE's `offeredForEdit` and the ENGINE's `isEditable`. The third is what this object
      // says editing MEANS here, which is why the resolution is taken as an argument rather than
      // computed inside: a site that draws record fields cannot decide that for itself, and a new
      // one added later either asks the same resolver or does not compile.
      const cell = detailFieldNode(object, record, f, placement, editVerb, ctx, refIndex, objectHome, version);
      // CAOS-1881 - the layout asked for this value to take the whole row. Marked on the cell and
      // spanned by the stylesheet rather than by an inline `gridColumn` here, so a section's span
      // behaviour stays in the same file as the grid it spans and cannot drift from it.
      if (placement.fullWidth) cell.dataset['fullWidth'] = '';
      grid.append(cell);
    }
    // CAOS-1883 - a section every one of whose fields was skipped draws nothing, heading included.
    // A titled card standing empty is its own disclosure: it names a group of fields and says, to
    // someone who may not read any of them, that they exist. The reference drops the group for the
    // same reason (`if (fields.length > 0) groups.push(...)`).
    //
    // Keyed on what the grid ACTUALLY holds rather than on `section.fields.length`, so a section
    // whose placements were all skipped for the OTHER reason - the object no longer defines them -
    // disappears too. That case is a layout naming fields that are gone, and an empty card was
    // never the right way to report it either.
    if (grid.childElementCount === 0) return null;
    sec.append(grid);
    return sec;
  };

  const sectionsWrap = document.createElement('div');
  sectionsWrap.className = 'sa-sections';
  sectionsWrap.slot = 'panel';
  sectionsWrap.dataset.value = 'details';
  for (const section of object.sections) {
    if (sectionsPlacedOut.has(section)) continue; // drawn in its own region, below
    const sec = drawSection(section); // CAOS-1883 — null when nothing in it was drawable
    if (sec) sectionsWrap.append(sec);
  }
  tabStrip.append(sectionsWrap);

  if (tabRelated.length > 0) {
    const relatedWrap = document.createElement('div');
    relatedWrap.className = 'sa-sections sa-related';
    relatedWrap.slot = 'panel';
    relatedWrap.dataset.value = 'related';
    for (const set of tabRelated) {
      relatedWrap.append(relatedListSection(set, object, ctx, reload));
    }
    tabStrip.append(relatedWrap);
  }

  if (activity && activityInTabs) {
    const activityWrap = buildActivityPanel(activity, object.label);
    activityWrap.slot = 'panel';
    activityWrap.dataset.value = 'activity';
    tabStrip.append(activityWrap);
  }

  if (history && historyInTabs) {
    const historyWrap = buildHistoryPanel(history, object);
    historyWrap.slot = 'panel';
    historyWrap.dataset.value = 'history';
    tabStrip.append(historyWrap);
  }

  // CAOS-1726 — place the surface. With nothing placed out, the tab strip goes straight onto the
  // record exactly as it did before this ticket: no grid, no wrapper, no changed markup for the
  // layouts nobody has touched. That is the whole compatibility story and it is structural rather
  // than asserted - this is the only `wrap.append(tabStrip)` on that path.
  if (regionPlan === undefined || !anythingPlacedOut) {
    wrap.append(tabStrip);
    return wrap;
  }

  // Everything that goes in a region, gathered before any of it is laid out, so the grid is built
  // from what is actually occupied rather than from the blueprint's full region list.
  const nodesByRegion = new Map<string, HTMLElement[]>();
  const into = (region: string, node: HTMLElement) => {
    const bucket = nodesByRegion.get(region);
    if (bucket) bucket.push(node);
    else nodesByRegion.set(region, [node]);
  };
  // The tab strip holds everything that stayed in the primary region, and is itself placed there.
  into(regionPlan.primary, tabStrip);
  for (const section of object.sections) {
    if (!sectionsPlacedOut.has(section)) continue;
    // CAOS-1883 — a placed-out section that drew nothing must not occupy a region either. Feeding
    // `into` a null would be worse here than in the tab strip: an occupied region is what the
    // region grid counts to decide whether there is more than one column to lay out, so an empty
    // card would not merely show, it would change the page's shape around itself.
    const sec = drawSection(section);
    if (sec) into(placedRecordRegion(section.region, regionPlan), sec);
  }
  for (const set of related) {
    if (!listsPlacedOut.has(set)) continue;
    into(placedRecordRegion(set.region, regionPlan), relatedListSection(set, object, ctx, reload));
  }
  // CAOS-367 / CAOS-402 — a placed panel is drawn by the SAME builder the tab used, so the panel
  // beside the body is the same panel and not a second rendering of the same data that could drift.
  for (const [panel, region] of panelRegions) {
    if (panel === 'activity' && activity) into(region, buildActivityPanel(activity, object.label));
    else if (panel === 'history' && history) into(region, buildHistoryPanel(history, object));
  }
  // FEWER THAN TWO OCCUPIED REGIONS MEANS THERE IS NOTHING TO TELL APART, so the DOM stays exactly
  // as it was rather than gaining a wrapper that changes the surface to no visible end. The same
  // rule `page-surface.ts` applies, for the same reason.
  //
  // Nothing can be lost on this path. A section, list or panel only reaches `*PlacedOut` when its
  // region resolved to something OTHER than primary, so anything lifted out of the tabs also puts
  // a node in a non-primary region and makes a second region occupied. The case this really serves
  // is a placement whose CONTENT turned out to be nothing on this request - the placement stands,
  // the region is simply empty, and an empty sidebar is not drawn to honour it.
  //
  // CAOS-1883 - that second sentence used to read "one occupied region implies nothing was lifted
  // out of the tabs", and it stopped being true when `drawSection` gained the ability to return
  // null: a placed-out section every one of whose fields this reader may not read is lifted out
  // and then contributes no node, so the count can be 1 with something genuinely placed out.
  // Nothing is lost by it - the section had nothing to draw, which is the whole point of the null -
  // and the behaviour here is still the one we want, because a lone region holding one card is
  // exactly the case a grid adds nothing to. The count is taken from `nodesByRegion`, which is what
  // was actually placed rather than what was planned, so it was already measuring the right thing.
  const occupiedCount = regionPlan.regions.filter(
    (r) => (nodesByRegion.get(r.key)?.length ?? 0) > 0,
  ).length;
  if (occupiedCount < 2) {
    wrap.append(tabStrip);
    return wrap;
  }

  wrap.append(buildRecordRegionGrid(regionPlan, nodesByRegion));
  return wrap;
}

/**
 * CAOS-360 — the form for a NEW record: the fields of the object's own layout that this person may
 * fill in, in the order the layout puts them, inside the one overlay every modal in the workspace
 * uses. It deliberately does NOT invent a form of its own — the layout is where somebody already
 * said what this object's fields are and what order they read in, and a create form that disagreed
 * with the record page would be a second answer to a question already answered.
 */
export function openCreateForm(object: ObjectMeta, ctx: SurfaceContext, seed?: CreateSeed): HTMLElement {
  const perms = ctx.perms ?? NO_WRITE;
  const overlay = document.createElement('caos-overlay');
  overlay.setAttribute('label', `New ${object.label}`);
  // The workspace's default overlay is sized for a short confirmation. A record form is two columns
  // of fields, and a panel that clips them hides half the form and the Create button with it.
  overlay.style.setProperty('--caos-overlay-width', 'min(820px, 92vw)');

  const form = document.createElement('form');
  form.className = 'sa-create';
  form.noValidate = true;

  const title = document.createElement('h2');
  title.className = 'sa-create__title';
  title.textContent = `New ${object.label}`;
  form.append(title);

  // The fields scroll; the title and the buttons do not. An object with thirty fields must not push
  // Create off the bottom of the panel.
  const body = document.createElement('div');
  body.className = 'sa-create__body';
  const grid = document.createElement('div');
  grid.className = 'sa-field-grid';
  grid.style.setProperty('--sa-cols', '2');
  const values: Record<string, unknown> = {};
  const fields = editableFields(object, perms);
  for (const f of fields) {
    const holder = document.createElement('div');
    const el = document.createElement('caos-field-renderer');
    el.setAttribute('mode', 'edit');
    if (f.required) el.setAttribute('required', '');
    const api = el as unknown as {
      field: unknown;
      value: unknown;
      options: unknown;
      optionsPresentation: string;
      search: unknown;
    };
    api.field = rendererField(f);
    // CAOS-362 — a clone opens this same form with the original's values already in it, so the
    // form starts from what somebody already decided rather than from nothing. The value is put
    // into the control AND into `values`, because nothing has been typed yet and an untouched
    // control raises no change event to record it.
    const seeded = seed?.values?.[f.apiName];
    api.value = seeded ?? null;
    if (seeded !== undefined && seeded !== null) values[f.apiName] = seeded;
    if (f.storageType === 'Select' || f.storageType === 'MultiSelect') {
      api.options = selectOptions(f, ctx.meta);
      // CAOS-640 — the create form draws the same declared presentation the record page does.
      api.optionsPresentation = valueSetPresentation(f, ctx.meta);
    }
    const toolbar = richTextToolbarFor(f, ctx.meta);
    if (toolbar) (el as unknown as { richTextToolbar: unknown }).richTextToolbar = toolbar;
    if (f.storageType === 'Reference') {
      const search = referenceSearch(f, ctx);
      if (search) api.search = search;
      // A seeded reference must read as the record's NAME. Left as the raw id it would show a
      // string nobody recognises in a box that is supposed to say who this belongs to.
      const label = seed?.refLabels?.get(f.apiName);
      if (label) (el as unknown as { refDisplay: string }).refDisplay = label;
    }
    el.addEventListener('caos-field-change', (ev) => {
      values[f.apiName] = (ev as CustomEvent<{ value: unknown }>).detail?.value;
    });
    holder.append(el);
    grid.append(holder);
  }
  body.append(grid);
  form.append(body);

  const actions = document.createElement('div');
  actions.className = 'sa-create__actions';
  const cancel = document.createElement('caos-button');
  cancel.setAttribute('variant', 'ghost');
  cancel.textContent = 'Cancel';
  cancel.addEventListener('click', () => overlay.remove());
  const save = document.createElement('caos-button');
  save.setAttribute('variant', 'primary');
  save.textContent = `Create ${object.label}`;
  actions.append(cancel, save);
  form.append(actions);

  const submit = async (): Promise<void> => {
    // A required field left empty is refused HERE, by name, rather than sent and bounced with a
    // message about a column.
    const missing = fields.filter((f) => f.required && (values[f.apiName] == null || values[f.apiName] === ''));
    if (missing.length > 0) {
      raiseToast(`${missing.map((f) => f.label).join(', ')} ${missing.length === 1 ? 'is' : 'are'} required.`, 'danger');
      return;
    }
    save.setAttribute('disabled', '');
    const payload: Record<string, unknown> = {};
    for (const f of fields) {
      const v = wireValue(f, values[f.apiName]);
      if (v !== null && v !== undefined) payload[f.apiName] = v;
    }
    // CAOS-1706 — the attachment travels OUTSIDE `payload`, deliberately. `payload` is built from
    // `fields`, the object's authored columns, and `data/create` refuses a name that is not one of
    // them; `parent_id` is a system column and would be rejected rather than stamped. Passing it
    // as its own argument also means an attachment cannot be overwritten by a field of the same
    // name, and that an unattached create sends exactly the body it sent before.
    const outcome = await createRecord(object.key, payload, ctx.fetchImpl ?? fetch, seed?.attachTo);
    save.removeAttribute('disabled');
    if (outcome.status !== 'ok') {
      raiseToast(outcome.message, 'danger');
      return;
    }
    overlay.remove();
    raiseToast(`${object.label} created.`, 'success');
    // Land on what was just made: the point of creating it was to have it.
    ctx.navigate?.(recordAddress({ appKey: ctx.appKey, tabKey: ctx.tabKey }, String(outcome.record.id)));
  };
  save.addEventListener('click', () => void submit());
  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    void submit();
  });

  overlay.append(form);
  overlay.addEventListener('caos-overlay-close', () => overlay.remove());
  document.body.append(overlay);
  return overlay;
}


/**
 * CAOS-362 — values to open the create form with, and the names to show for any reference among
 * them. Used by a `clone` action; absent for an ordinary New.
 */
export interface CreateSeed {
  readonly values: Record<string, unknown>;
  readonly refLabels?: ReadonlyMap<string, string>;
  /**
   * CAOS-1706 — the id of the record to ATTACH the new one to, sent as the Engine's `parentId` and
   * stamped on the system `parent_id` column.
   *
   * A separate channel from `values` because it is not a form field and must not become one. The
   * form draws a control for every entry in `values`, so putting an attachment there would render
   * a box holding a raw uuid that the person could edit or clear — and `parent_id` has no
   * `FieldMeta`, so the control would have no label, no type and no permission behind it. Keeping
   * it out of `values` is what makes attachment invisible in the form and still true on the wire.
   */
  readonly attachTo?: string;
}

/** An action's tint, in the button vocabulary. No intent ⇒ secondary: a header of primaries has no emphasis left. */
function actionVariant(intent: string | undefined): string {
  if (intent === 'danger') return 'destructive';
  if (intent === 'success' || intent === 'info') return 'primary';
  return 'secondary';
}

/**
 * CAOS-362 — ask before doing it. Resolves true when the person says go ahead, false on cancel or
 * on dismissing the panel, so an action with a `confirm` can never happen by a stray press.
 */
function askToConfirm(
  question: string,
  confirmLabel: string,
  detail: { changes?: readonly string[] } = {},
): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement('caos-overlay');
    overlay.setAttribute('label', confirmLabel);
    const box = document.createElement('div');
    box.className = 'sa-confirm';
    const text = document.createElement('p');
    text.className = 'sa-confirm__question';
    text.textContent = question;
    // CAOS-406 — the change itself, when the caller can state it. A confirm that asks "Are you
    // sure?" over an edit it does not show is a yes/no about something the person has to already
    // know, which is not a review of anything. Listed in the authored order the write applies in,
    // so what is read is what happens.
    let review: HTMLElement | null = null;
    if (detail.changes && detail.changes.length > 0) {
      review = document.createElement('ul');
      review.className = 'sa-confirm__changes';
      for (const line of detail.changes) {
        const item = document.createElement('li');
        item.textContent = line;
        review.append(item);
      }
    }
    const actions = document.createElement('div');
    actions.className = 'sa-confirm__actions';
    const no = document.createElement('caos-button');
    no.setAttribute('variant', 'ghost');
    no.textContent = 'Cancel';
    const yes = document.createElement('caos-button');
    yes.setAttribute('variant', 'primary');
    yes.textContent = confirmLabel;
    let settled = false;
    const close = (answer: boolean): void => {
      if (settled) return;
      settled = true;
      overlay.remove();
      resolve(answer);
    };
    no.addEventListener('click', () => close(false));
    yes.addEventListener('click', () => close(true));
    overlay.addEventListener('caos-overlay-close', () => close(false));
    actions.append(no, yes);
    box.append(text, ...(review ? [review] : []), actions);
    overlay.append(box);
    document.body.append(overlay);
  });
}

/**
 * CAOS-1861 — ask for a NAME. Resolves the trimmed text when the person confirms, and `null` on
 * cancel, on dismissing the panel, or on an empty box.
 *
 * Built beside {@link askToConfirm} and deliberately in its image — same `caos-overlay`, same
 * single-settle `close`, same "dismiss means no" rule — because the two are the same interaction
 * with one more field, and a second dialog discipline in one file is how one of them comes to leak
 * a promise that never settles.
 *
 * EMPTY IS CANCEL, not an empty name. A view labelled "" draws a blank option in the chooser and
 * cannot be told from the others, and the Engine would refuse it anyway (`label` is a required
 * string param); treating blank as cancel means the refusal never has to be explained.
 *
 * The box is not a `caos-input`: the search field a few hundred lines up is a plain `input` for the
 * same reason, and matching the surrounding code matters more here than reaching for the component.
 */
function askForName(
  question: string,
  confirmLabel: string,
  initial = '',
): Promise<string | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement('caos-overlay');
    overlay.setAttribute('label', confirmLabel);
    const box = document.createElement('div');
    box.className = 'sa-confirm';
    const text = document.createElement('label');
    text.className = 'sa-confirm__question';
    text.textContent = question;
    const field = document.createElement('input');
    field.type = 'text';
    field.className = 'sa-confirm__name';
    field.value = initial;
    field.setAttribute('aria-label', question);
    text.append(field);
    const actions = document.createElement('div');
    actions.className = 'sa-confirm__actions';
    const no = document.createElement('caos-button');
    no.setAttribute('variant', 'ghost');
    no.textContent = 'Cancel';
    const yes = document.createElement('caos-button');
    yes.setAttribute('variant', 'primary');
    yes.textContent = confirmLabel;
    let settled = false;
    const close = (answer: string | null): void => {
      if (settled) return;
      settled = true;
      overlay.remove();
      resolve(answer);
    };
    const accept = (): void => {
      const value = field.value.trim();
      close(value === '' ? null : value);
    };
    no.addEventListener('click', () => close(null));
    yes.addEventListener('click', accept);
    // Enter in the box confirms. Naming a thing is a one-field form, and reaching for the mouse to
    // commit a word you have just typed is the kind of friction a person feels on every view.
    field.addEventListener('keydown', (ev) => {
      if ((ev as KeyboardEvent).key === 'Enter') {
        ev.preventDefault();
        accept();
      }
    });
    overlay.addEventListener('caos-overlay-close', () => close(null));
    actions.append(no, yes);
    box.append(text, actions);
    overlay.append(box);
    document.body.append(overlay);
    field.focus();
    field.select();
  });
}

/**
 * CAOS-407 — what happened to ONE record in a selection action, kept per record rather than summed.
 *
 * The acceptance criterion is "says what happened to each", and a count cannot answer it: eleven of
 * twelve applied is not a report if the twelfth is the one somebody needed. `name` rather than id,
 * because the person picked rows by name and an id names nothing to them.
 */
interface SelectionOutcome {
  readonly name: string;
  readonly ok: boolean;
  readonly detail: string;
}

/**
 * CAOS-407 — show what happened to each record, and re-read once it has been read.
 *
 * In an overlay rather than under the table, and that is forced rather than chosen: the re-read
 * re-renders the whole surface, so a report drawn into the table's own section is destroyed by the
 * refresh that follows it. Reloading only after the person dismisses this keeps both — they see the
 * outcome per record, and the table they return to agrees with what was written.
 */
function reportSelectionOutcomes(label: string, outcomes: readonly SelectionOutcome[], done: () => void): void {
  const overlay = document.createElement('caos-overlay');
  const failed = outcomes.filter((o) => !o.ok).length;
  overlay.setAttribute('label', label);
  const box = document.createElement('div');
  box.className = 'sa-bulk-report';
  const summary = document.createElement('p');
  summary.className = 'sa-bulk-report__summary';
  summary.textContent =
    failed === 0
      ? `${label} applied to ${outcomes.length} ${outcomes.length === 1 ? 'record' : 'records'}.`
      : `${outcomes.length - failed} of ${outcomes.length} applied. ${failed} did not.`;
  const list = document.createElement('ul');
  list.className = 'sa-bulk-report__list';
  for (const outcome of outcomes) {
    const item = document.createElement('li');
    item.dataset['outcome'] = outcome.ok ? 'ok' : 'failed';
    item.textContent = `${outcome.name} — ${outcome.detail}`;
    list.append(item);
  }
  const actions = document.createElement('div');
  actions.className = 'sa-confirm__actions';
  const close = document.createElement('caos-button');
  close.setAttribute('variant', 'primary');
  close.textContent = 'Close';
  let settled = false;
  const finish = (): void => {
    if (settled) return;
    settled = true;
    overlay.remove();
    done();
  };
  close.addEventListener('click', finish);
  overlay.addEventListener('caos-overlay-close', finish);
  actions.append(close);
  box.append(summary, list, actions);
  overlay.append(box);
  document.body.append(overlay);
}

/**
 * CAOS-407 — the buttons that act on the rows a person has TICKED.
 *
 * Drawn above the table rather than on a row, because that is what they are about: a different
 * control, in a different place, over a different subject. The same two rules as a record's own
 * buttons decide whether one is drawn at all — the person must be able to edit the field it sets,
 * and an object nothing is known about answers no.
 *
 * Each record is written with the version IT was read at, not one version for the batch: they are
 * separate records and a stale one among them is refused on its own, reported on its own, and does
 * not take the other eleven down with it.
 */
export function selectionActionBar(
  object: ObjectMeta,
  rows: readonly ApiRecord[],
  table: HTMLElement,
  ctx: SurfaceContext,
  actions: readonly Extract<RecordActionMeta, { readonly kind: 'update' }>[],
  reload: () => void,
): HTMLElement | null {
  const perms = ctx.perms ?? NO_WRITE;
  // CAOS-406 — an update action's assignments are a LIST (`set`), whichever spelling was authored;
  // the projection collapses the single `field`/`value` pair into a list of one before anything
  // here sees it. So the permission rule is the same all-or-nothing rule a record's own buttons
  // use, applied across the whole list: the assignments were authored as ONE change, and a button
  // that quietly wrote the two fields this person may edit and skipped the third would leave every
  // ticked record in a state nobody described — worse over a selection of twelve than over one.
  const targetsOf = (action: Extract<RecordActionMeta, { readonly kind: 'update' }>) =>
    action.set.map((assignment) => ({ assignment, field: object.fields.get(assignment.field) }));
  const drawable = actions.filter((action) => {
    const targets = targetsOf(action);
    return targets.length > 0 && !targets.some(({ field }) => !field || !isEditable(field, perms));
  });
  if (drawable.length === 0) return null;

  const bar = document.createElement('div');
  bar.className = 'sa-selection-actions';
  const count = document.createElement('span');
  count.className = 'sa-selection-actions__count';
  bar.append(count);

  const selectedIds = (): string[] => (table as unknown as { selectedIds?: string[] }).selectedIds ?? [];
  const buttons: HTMLElement[] = [];
  const sync = (): void => {
    const n = selectedIds().length;
    count.textContent = n === 0 ? 'None selected' : `${n} selected`;
    for (const button of buttons) {
      if (n === 0) button.setAttribute('disabled', '');
      else button.removeAttribute('disabled');
    }
  };

  const run = async (action: Extract<RecordActionMeta, { readonly kind: 'update' }>, button: HTMLElement): Promise<void> => {
    const ids = selectedIds();
    if (ids.length === 0) return;
    const targets = targetsOf(action);
    if (action.confirm) {
      // The count is stated in the question. "Are you sure?" is a different question when it means
      // twelve records than when it means one, and the person pressing it should be told which.
      const asked = `${action.confirm} This applies to ${ids.length} ${ids.length === 1 ? 'record' : 'records'}.`;
      // CAOS-406 — and WHAT is about to change, not only how many records it lands on. Over a
      // selection this matters more rather than less: an unstated edit applied to one record can be
      // inspected afterwards, and the same edit applied to twelve is twelve things to go and check.
      if (
        !(await askToConfirm(asked, action.label, {
          changes: targets.map(({ assignment, field }) => `${field!.label} → ${String(assignment.value)}`),
        }))
      ) {
        return;
      }
    }
    button.setAttribute('loading', '');
    button.setAttribute('disabled', '');
    const byId = new Map(rows.map((r) => [String(r.id), r]));
    const outcomes: SelectionOutcome[] = [];
    // One at a time, in the order they appear. Firing twelve writes at once would report them in
    // whatever order they happened to finish, which is not an order anybody can check against the
    // list they were looking at.
    for (const id of ids) {
      const record = byId.get(id);
      if (!record) {
        outcomes.push({ name: id, ok: false, detail: 'no longer in this list.' });
        continue;
      }
      const name = recordDisplayName(object, record);
      // CAOS-406 — every assignment in ONE write per record, in authored order. Not one write per
      // field per record: that would turn twelve records into thirty-six writes, each its own
      // chance to fail halfway, and a record left with its stage moved and its reason blank is the
      // state authoring them as a single change exists to make impossible. Each record still
      // carries the version IT was read at, which is CAOS-407's rule and is unchanged.
      const changes: Record<string, unknown> = {};
      for (const { assignment } of targets) changes[assignment.field] = assignment.value;
      const outcome = await updateRecord(
        object.key,
        id,
        changes,
        recordVersion(record),
        ctx.fetchImpl ?? fetch,
      );
      outcomes.push(
        outcome.status === 'ok'
          ? { name, ok: true, detail: `${action.label.toLowerCase()} applied.` }
          : { name, ok: false, detail: outcome.message },
      );
    }
    button.removeAttribute('loading');
    sync();
    reportSelectionOutcomes(action.label, outcomes, reload);
  };

  for (const action of drawable) {
    const button = document.createElement('caos-button');
    button.setAttribute('variant', actionVariant(action.intent));
    button.dataset.actionId = action.id;
    button.dataset.actionKind = action.kind;
    button.textContent = action.label;
    button.addEventListener('click', () => void run(action, button));
    buttons.push(button);
    bar.append(button);
  }

  table.addEventListener('caos-row-select', sync);
  table.addEventListener('caos-select-all', sync);
  sync();
  return bar;
}

/**
 * CAOS-362 — the buttons in a record's header.
 *
 * Two rules decide whether an action is DRAWN at all, and both of them are about not lying:
 *
 *   • PERMISSION. An `update` whose field this person may not change, and a `clone` on an object
 *     they may not create, are not offered. CAOS-560 extends the same rule to the two kinds that
 *     reach BEYOND this object: a `create` is offered only to somebody who may create the target,
 *     and a `component` action only when every requirement it DECLARES is met. A component's needs
 *     are opaque, which is exactly why it has to declare them — the alternative is a button that
 *     fails when pressed, which is the thing this rule exists to prevent. The same rule the editable fields follow (CAOS-360):
 *     an affordance that ends in a refusal is worse than no affordance.
 *   • APPLICABILITY. `appliesWhen` is read against this record, so Approve is on the request that is
 *     waiting for approval and nowhere else. A navigate whose path still has an unfilled
 *     placeholder is dropped too — half an address lands somewhere arbitrary.
 *
 * An `update` goes through the ordinary write boundary, so it is validated and refused exactly as
 * typing the value would be; a button is not a second way in. When it lands, the surface RE-READS:
 * the status badge, the field and the action set itself all describe the record as it now is, and
 * an Approve button still sitting there after approving would be the surface disagreeing with the
 * record it is showing.
 */
/**
 * CAOS-560 — the OTHER objects this object's actions reach, deduplicated: the target of every
 * `create`, and every object named by a `component` action's declared requirements. The surface's
 * own object is excluded because `SurfaceContext.perms` already carries it.
 *
 * Exported so the content host can resolve their permissions BEFORE rendering; the action bar is
 * synchronous, and asking inside it would mean drawing buttons and then taking them away.
 */
export function objectsActionsReach(object: ObjectMeta): string[] {
  const keys = new Set<string>();
  for (const action of object.actions) {
    if (action.kind === 'create') keys.add(action.object);
    else if (action.kind === 'component') for (const need of action.requires) keys.add(need.object);
  }
  // CAOS-407 — a related list's buttons act on the CHILD's records, so whether this person may press
  // one is a question about the CHILD's permissions and not about this record's. Without this the
  // child's are never read, and every row button would be judged against the parent's — wrong in
  // both directions at once: a button offered that writes a field the person cannot edit, and a
  // button hidden on a child they can.
  for (const list of object.relatedLists) {
    if ((list.rowActions?.length ?? 0) > 0 || (list.selectionActions?.length ?? 0) > 0) keys.add(list.object);
    // A create button on a row makes a record of an object of its own, exactly as one in the header does.
    for (const action of list.rowActions ?? []) if (action.kind === 'create') keys.add(action.object);
  }
  keys.delete(object.key);
  return [...keys];
}

/**
 * CAOS-560 — can this person do the thing this requirement names? An object nothing is known about
 * answers NO: an undeclared permission is not a permissive one.
 */
function requirementMet(
  need: ActionRequirementMeta,
  object: ObjectMeta,
  ctx: SurfaceContext,
): boolean {
  const perms = need.object === object.key ? ctx.perms : ctx.actionPerms?.get(need.object);
  if (!perms) return false;
  if (need.can === 'create') return perms.canCreate;
  if (!need.field) return perms.canUpdate;
  // A field-level need is answered by the same editability rule the record's own controls use, so
  // a button and the field it would change never disagree about who may change it.
  const field = (need.object === object.key ? object : ctx.meta.objects.get(need.object))?.fields.get(need.field);
  return field !== undefined && isEditable(field, perms);
}

/**
 * CAOS-560 — open a `component` action: the authored piece, in the workspace's one overlay, with
 * this record in context.
 *
 * THE RECORD IS PASSED AS CONTEXT, NOT AS A CAPABILITY. The element is given the record, its id and
 * its object so it knows what it was opened about; it is given no write path, no token and no
 * privileged handle. Whatever it goes on to do, it does through the ordinary API the rest of the
 * workspace uses — the same boundary, the same validation, the same permissions, the same audit. An
 * action that could write in a way an ordinary save cannot would make every guarantee on the write
 * path conditional, which is why this function hands over data and nothing else.
 *
 * Drawn through {@link renderInstalledComponent}, the same path a page places a piece by, so a
 * component that is missing, or whose code cannot be loaded, says exactly what it says on a page
 * instead of an empty overlay.
 */
function openComponentAction(
  action: Extract<RecordActionMeta, { kind: 'component' }>,
  object: ObjectMeta,
  record: ApiRecord | null,
  ctx: SurfaceContext,
  onUndrawable?: () => void,
): void {
  const overlay = document.createElement('caos-overlay');
  overlay.setAttribute('label', action.label);
  overlay.style.setProperty('--caos-overlay-width', 'min(820px, 92vw)');

  const el = renderInstalledComponent(action.component, action.inputs, drawOptions(ctx), {
    // Before the props, for the same reason a placement stamps its item id first: a piece whose
    // property setters run during `applyProps` can already see what it was opened about.
    beforeProps: (node) => {
      node.dataset['actionId'] = action.id;
      node.setAttribute('object', object.key);
      // CAOS-561 — a piece opened as the replacement for New is opened with NO record, because
      // there is not one yet. It is told which object it is about and nothing else. Setting an
      // empty `record-id` would be worse than setting none: a piece cannot tell "" from "not yet".
      if (record !== null) {
        node.setAttribute('record-id', String(record.id));
        (node as unknown as Record<string, unknown>)['record'] = record;
      }
    },
    // CAOS-561 — only a caller that DISPLACED something to make room for this piece passes one.
    // The ordinary action bar does not: a button whose piece cannot be drawn says so in the overlay
    // and leaves the rest of the surface exactly as it was, which is what CAOS-560 settled.
    ...(onUndrawable ? { onUndrawable } : {}),
  });

  // The piece asks to be closed the way every other overlay content does.
  el.addEventListener('caos-action-done', () => overlay.remove());
  overlay.addEventListener('caos-overlay-close', () => overlay.remove());
  overlay.append(el);
  document.body.append(overlay);
}

/**
 * CAOS-561 — what a STANDARD VERB means on this surface. The one place that answers it.
 *
 * The incumbent configures an override once per place the action can appear, so one object can
 * behave one way from its record page and another from a list row, and keeping them consistent is
 * somebody remembering to. Here the declaration is on the OBJECT and every site asks this function,
 * so two surfaces cannot disagree — not because anybody kept them in step, but because there is
 * only one answer and neither of them computed it.
 *
 * `mayPerformVerb` is the permission that governs the STANDARD verb, and it is a required argument
 * rather than something resolved in here on purpose: each verb's permission lives with its own
 * affordance (`canCreate` for the New a list draws, `canUpdate` for editing a record), and asking a
 * caller to hand it over is what makes it impossible to offer a replacement as a way AROUND the
 * permission. A replacement replaces the affordance; it never grants the verb.
 *
 * The five outcomes:
 *
 *   none      — this person may not perform the verb at all. Nothing is offered, replaced or not.
 *   standard  — the platform's own affordance. Either nothing replaces this verb, or a replacement
 *               exists and its predicate says it is not for THIS record — which is the author
 *               saying "here, Edit is ordinary", and is why record-type scoping needs no new
 *               vocabulary: the object's record-type field is a real field a predicate can read.
 *   replaced  — the replacement, in place of the standard affordance.
 *   withheld  — a replacement applies and this person may not run it, so the verb is offered NOT AT
 *               ALL. See {@link brokenReplacement} for why this does not fall back.
 *   fallback  — the replacement is broken. The standard affordance is offered and says so.
 */
export type VerbResolution =
  | { readonly verb: StandardVerb; readonly outcome: 'none' }
  | { readonly verb: StandardVerb; readonly outcome: 'standard' }
  | { readonly verb: StandardVerb; readonly outcome: 'replaced'; readonly action: RecordActionMeta; readonly run: () => void | Promise<void> }
  | { readonly verb: StandardVerb; readonly outcome: 'withheld'; readonly action: RecordActionMeta }
  | { readonly verb: StandardVerb; readonly outcome: 'fallback'; readonly action: RecordActionMeta; readonly why: string };
/**
 * CAOS-561 — the permission that governs EDITING a record, named once because two call sites read
 * it ({@link renderRecordSurface} for the fields, {@link recordActionBar} for the button) and they
 * must never be able to drift: a surface that locked its fields on one answer and drew its button on
 * another would be the two halves of a replacement disagreeing.
 */
function mayEditRecord(ctx: SurfaceContext): boolean {
  return (ctx.perms ?? NO_WRITE).canUpdate;
}
/**
 * CAOS-561 — how a piece is drawn from this surface, built once so the two readers of that answer
 * cannot disagree: the one that draws it and the one that asks in advance whether it could be.
 */
function drawOptions(ctx: SurfaceContext): PageRenderOptions {
  // CAOS-1809 — the viewer travels with the draw. A record ACTION has no route, so `routeParams` is
  // absent here and that is right (CAOS-798); who pressed the button is a different question and is
  // just as answerable on this path as on a page's.
  return { meta: ctx.meta, ...(ctx.viewer ? { viewer: ctx.viewer } : {}), ...ctx.componentDrawing };
}
/**
 * CAOS-561 — the reason a replacement names SOMETHING THAT IS NOT THERE, or `null` when it does not.
 *
 * This is the distinction the whole fallback rule turns on, and conflating it with "withheld" would
 * put a hole in the mechanism in one direction and a record nobody can edit in the other:
 *
 *   • WITHHELD — this person may not run the replacement. The verb is then offered NOT AT ALL.
 *     Falling back to the standard action here would be the hole the ticket names: the author said
 *     editing this record goes through the wizard, and we would hand them the plain editor instead.
 *   • BROKEN — the replacement names a piece the org does not have, a field the object lost, a
 *     destination that cannot be reached. The author's intent is now unknowable, and a record
 *     nobody can edit is worse than an override that did not take, so the standard action is
 *     offered and SAYS SO.
 *
 * Note what is deliberately NOT broken: a `component` action whose component is missing is still
 * OFFERED on the ordinary action bar, because CAOS-560 made pressing it say which of the two
 * failures it is, on screen. As a replacement it cannot do that, because the standard affordance it
 * displaced would already be gone by the time anybody pressed anything.
 */
function brokenReplacement(
  action: RecordActionMeta,
  object: ObjectMeta,
  record: ApiRecord | null,
  ctx: SurfaceContext,
): string | null {
  const named = `“${action.label}”`;
  if (record === null && action.kind !== 'navigate' && action.kind !== 'component') {
    return `${named} reads values off a record, and there is none to read here.`;
  }
  if (action.kind === 'navigate' && action.to === undefined) {
    // CAOS-1616 — a destination has two ways to be unusable and they are not the same fact, so they
    // do not get the same sentence. An empty field on this record is about THIS RECORD: the next
    // one along may be fine, and calling that a misconfiguration sends somebody to the wrong place.
    // An object with no reachable home IS a configuration fact — nothing about this record will
    // change it — and it reads as one.
    const targetId = ((record ?? {}) as Record<string, unknown>)[action.destination.field];
    if (targetId === null || targetId === undefined || String(targetId) === '') {
      return `${named} needs a value this record does not have.`;
    }
    const home = objectHomeMap(ctx.tabs, ctx.appKey, ctx.recordHomes).get(action.destination.object);
    return home === undefined
      ? `${named} opens a record of “${action.destination.object}”, and there is nowhere in this workspace those records are shown.`
      : null;
  }
  if (action.kind === 'navigate') {
    const { complete } = fillPath(action.to, (record ?? {}) as Record<string, unknown>);
    // Worded as what it is. Unlike the three below, this one is not a metadata defect: the path is
    // filled from THIS record, so an optional field nobody has filled in makes it incomplete on one
    // record and fine on the next. It still falls back — the verb has to stay performable — but
    // telling somebody their object is misconfigured when their record simply has a blank field
    // sends them to the wrong place to fix it.
    return complete ? null : `${named} needs a value this record does not have.`;
  }
  if (action.kind === 'update') {
    // CAOS-406 made an update a LIST of assignments, so the question is whether EVERY field it
    // names exists. Naming the missing ones rather than counting them: "changes a field this
    // object does not have" sends somebody looking through all of them.
    const missing = action.set.filter((a) => !object.fields.get(a.field)).map((a) => a.field);
    return missing.length === 0
      ? null
      : `${named} changes ${missing.length === 1 ? 'a field' : 'fields'} this object does not have: ${missing.join(', ')}.`;
  }
  if (action.kind === 'create') {
    return ctx.meta.objects.get(action.object) ? null : `${named} makes a record of something this workspace does not have.`;
  }
  if (action.kind === 'component') {
    // The SAME ladder `renderInstalledComponent` draws by, asked in advance. Asking a narrower
    // question here — "is it in the installed list" — was the hole: a piece that is installed and
    // whose code cannot be loaded passed that check, the verb was replaced by it, and the person
    // found out by pressing the button and reading an overlay, with the standard affordance already
    // gone. Its one asynchronous rung cannot be answered in advance; `openComponentAction` is told
    // instead, and the surface re-draws.
    const failure = componentDrawFailure(action.component, drawOptions(ctx));
    return failure === null ? null : `${named} opens a piece that cannot be drawn: ${failure}.`;
  }
  return null;
}
/**
 * CAOS-561 — the LOUD half of the fallback. A broken replacement leaves the standard affordance in
 * place, and this says why it is the standard one, beside it.
 *
 * It is a rendered sentence rather than a toast because the condition is a property of the screen
 * and not an event: a toast fires once, on a paint nobody may have been watching, and a person who
 * arrives afterwards sees an ordinary Edit with no indication that the object asked for something
 * else. This is also why it is not silent — an override that quietly did not take is exactly the
 * state somebody spends an afternoon on.
 */
function standardFallbackNotice(resolution: Extract<VerbResolution, { outcome: 'fallback' }>): HTMLElement {
  const note = document.createElement('p');
  note.className = 'sa-verb-fallback';
  note.setAttribute('role', 'status');
  note.dataset['verb'] = resolution.verb;
  note.dataset['fallbackFrom'] = resolution.action.id;
  note.textContent = `${resolution.why} The standard ${resolution.verb === 'create' ? 'New' : 'Edit'} is offered instead.`;
  return note;
}
export function resolveStandardVerb(
  verb: StandardVerb,
  object: ObjectMeta,
  record: ApiRecord | null,
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  ctx: SurfaceContext,
  reload: () => void,
  mayPerformVerb: boolean,
  /**
   * CAOS-368 - the page's shared version cell, when the caller has one. A replacement for Edit
   * WRITES, and it has to state the same version the inline fields beside it are stating: edit a
   * field, then press the replacement, and without this the press carries the version from before
   * that edit and is refused for a conflict with nobody but the same person's own save. Optional
   * for the same reason `actionOffer`'s is - a verb resolved without a surface has no cell to
   * share - and the default there is the record's own.
   */
  version?: RecordVersionHolder,
): VerbResolution {
  if (!mayPerformVerb) return { verb, outcome: 'none' };
  const action = object.actions.find((a) => a.replaces === verb);
  if (!action) return { verb, outcome: 'standard' };

  if (action.appliesWhen !== undefined) {
    // A predicate is read against the record the verb is about. Offered with no record there is
    // nothing to read, and a condition nobody can evaluate must not silently decide a verb — the
    // kernel refuses this at deploy, and if one reaches here anyway it is broken, not ignored.
    if (record === null) {
      return { verb, outcome: 'fallback', action, why: `“${action.label}” applies only to some records, and this is offered without one.` };
    }
    if (!rowMatches(action.appliesWhen, record as unknown as Record<string, unknown>)) {
      return { verb, outcome: 'standard' };
    }
  }

  const why = brokenReplacement(action, object, record, ctx);
  if (why) return { verb, outcome: 'fallback', action, why };

  const offer = actionOffer(action, object, record, refIndex, ctx, reload, version);
  if (offer.status === 'withheld') return { verb, outcome: 'withheld', action };
  return { verb, outcome: 'replaced', action, run: offer.run };
}

/**
 * CAOS-561 - what an action WOULD do here, and whether it can be offered at all.
 *
 * Lifted out of {@link recordActionBar} so the ONE question "can this action be offered" has one
 * answer, asked both by the bar drawing its buttons and by {@link resolveStandardVerb} deciding
 * whether a replacement can stand in for a standard verb. Two copies of this ladder would drift the
 * way the reference grammars did before `outboundRefsOf` (CAOS-100/CAOS-560): a button offered on
 * the bar and silently withheld as a replacement, for a reason nobody could see.
 *
 * `withheld` covers every reason THIS PERSON may not run it - a predicate that does not match, a
 * permission they do not hold, a field they may not write. It deliberately does NOT cover an action
 * that names something which does not exist; see {@link brokenReplacement} for why those two have
 * to be told apart.
 *
 * The ladder below is `recordActionBar`'s own, unchanged: every `continue` that skipped drawing a
 * button is the refusal itself now, and the component arm gained the one line CAOS-561 adds.
 */
type ActionOffer =
  | { readonly status: 'offer'; readonly run: () => void | Promise<void> }
  | { readonly status: 'withheld' };

const WITHHELD: ActionOffer = { status: 'withheld' };

function actionOffer(
  action: RecordActionMeta,
  object: ObjectMeta,
  record: ApiRecord | null,
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  ctx: SurfaceContext,
  reload: () => void,
  /**
   * CAOS-368 - the page's shared version cell, when the caller has one. Defaulted to the record's
   * own, which is what a caller with no page cell had before that ticket and what a verb resolved
   * without a surface gets.
   */
  version: RecordVersionHolder = { current: record === null ? undefined : recordVersion(record) },
): ActionOffer {
  const perms = ctx.perms ?? NO_WRITE;
  const row = (record ?? {}) as Record<string, unknown>;
  // Every kind below `navigate` and `component` reads values off the record the button was pressed
  // from. Offered with none, there is nothing for them to read.
  if (record === null && action.kind !== 'navigate' && action.kind !== 'component') return WITHHELD;

  // The same guard, as a binding: the arms below reach the record's id and its values, and the
  // check above is not something the compiler can carry into them.
  const onRecord = record as ApiRecord;
  if (action.appliesWhen && !rowMatches(action.appliesWhen, row)) return WITHHELD;

  let run: (() => void | Promise<void>) | null = null;

  if (action.kind === 'navigate') {
    if (action.to !== undefined) {
      const { path, complete } = fillPath(action.to, row);
      if (!complete) return WITHHELD;
      run = () => ctx.navigate?.(path);
    } else {
      // CAOS-1616 — a destination resolves through the same home map every other link on this
      // surface resolves through, so the app and tab it lands on are the ones THIS workspace
      // installed rather than the ones whoever authored the action happened to have.
      //
      // Withheld rather than drawn-and-dead in both the ways it can fail to resolve: the row has no
      // id in the field (an optional reference nobody has filled in, which is a fact about this row
      // and not a defect), or the target object has no reachable home here (it is not installed, or
      // no tab shows it). A button that navigates nowhere is the thing this ticket exists to stop.
      const targetId = row[action.destination.field];
      const home = objectHomeMap(ctx.tabs, ctx.appKey, ctx.recordHomes).get(action.destination.object);
      if (home === undefined || targetId === null || targetId === undefined || String(targetId) === '') {
        return WITHHELD;
      }
      const path = recordAddress(home, String(targetId));
      run = () => ctx.navigate?.(path);
    }
  } else if (action.kind === 'update') {
    // CAOS-406 — every assignment must be one this person may actually make. A partially
    // applicable update is NOT applied in part: the assignments were authored as one change, and
    // writing some of them would leave the record in a state the author never described and
    // nobody asked for. Hidden entirely instead, on the same rule a single un-editable field has
    // always hidden a one-field update.
    const targets = action.set.map((a) => ({ assignment: a, field: object.fields.get(a.field) }));
    if (targets.some(({ field }) => !field || !isEditable(field, perms))) return WITHHELD;
    run = async () => {
      if (
        action.confirm &&
        !(await askToConfirm(action.confirm, action.label, {
          // "Offer the change for review before it commits": the question alone says a change is
          // coming, not what it is. With the fields named, a person can tell "Mark lost" setting
          // three fields from one that also closes the record.
          changes: targets.map(({ assignment, field }) => `${field!.label} → ${String(assignment.value)}`),
        }))
      ) {
        return;
      }
      // CAOS-368 — a row action states the version it was offered against, the same as an inline
      // edit. `appliesWhen` decided this button should exist by reading THIS copy of the record,
      // so a button drawn from a stale read is precisely the case worth refusing: "Mark approved"
      // is not a safe thing to apply to a record somebody else has moved on since it was drawn.
      //
      // CAOS-406 — ONE write for every assignment, not one per field. Three writes would be
      // three chances to fail halfway and three separate version checks, and a record left with
      // its stage moved and its reason blank is exactly the state authoring them as one change
      // was meant to make impossible. Built in authored order, so a field the author somehow
      // named twice resolves the way the deploy gate says it would.
      const changes: Record<string, unknown> = {};
      for (const { assignment } of targets) changes[assignment.field] = assignment.value;
      const outcome = await updateRecord(
        object.key,
        String(onRecord.id),
        changes,
        version.current,
        ctx.fetchImpl ?? fetch,
      );
      if (outcome.status === 'conflict') {
        // Reload rather than leave the old record on screen: the action did not apply, and what
        // the record now says is the only honest thing to show — including whether this button
        // still belongs on it at all.
        raiseToast(outcome.message, 'warning');
        reload();
        return;
      }
      if (outcome.status !== 'ok') {
        raiseToast(outcome.message, 'danger');
        return;
      }
      // One field reads as it always did; several are listed, because "3 fields updated" tells
      // the person a number and not what happened to their record.
      raiseToast(
        targets.map(({ assignment, field }) => `${field!.label} is now ${String(assignment.value)}`).join(', ') + '.',
        'success',
      );
      reload();
    };
  } else if (action.kind === 'clone') {
    if (!perms.canCreate) return WITHHELD;
    run = () => {
      const values: Record<string, unknown> = {};
      const refLabels = new Map<string, string>();
      for (const f of editableFields(object, perms)) {
        const v = controlValue(f, onRecord);
        if (v === null || v === undefined || v === '') continue;
        values[f.apiName] = v;
        if (f.storageType === 'Reference') {
          const name = refIndex.get(String(v))?.name;
          if (name) refLabels.set(f.apiName, name);
        }
      }
      openCreateForm(object, ctx, { values, refLabels });
    };
  } else if (action.kind === 'create') {
    // The TARGET's permissions, not this object's: the person may well be able to edit the
    // account and not to create a contact.
    //
    // The `=== object.key` fallback is not a special case, it is the SAME rule `requirementMet`
    // follows, and it is load-bearing: `objectsActionsReach` deliberately leaves this object out
    // of `actionPerms` because `ctx.perms` already carries it, so reading only the map would
    // find nothing and silently hide a create that targets the object it was authored on —
    // "New Task from a Task" being the obvious one. The two halves of that exclusion have to be
    // read together.
    const targetPerms = action.object === object.key ? perms : ctx.actionPerms?.get(action.object);
    const target = ctx.meta.objects.get(action.object);
    if (!target || !targetPerms?.canCreate) return WITHHELD;
    run = () => {
      const values: Record<string, unknown> = {};
      const refLabels = new Map<string, string>();
      for (const entry of action.prefill) {
        // Only fields of the target the person may actually fill. A prefill that seeded a field
        // they cannot edit would put a value in a control they cannot see, which is a write they
        // never made and cannot inspect.
        const field = target.fields.get(entry.field);
        if (!field || !isEditable(field, targetPerms)) continue;
        const value = 'from' in entry ? (record as Record<string, unknown>)[entry.from] : entry.value;
        if (value === null || value === undefined || value === '') continue;
        values[entry.field] = value;
        if (field.storageType === 'Reference') {
          const name = refIndex.get(String(value))?.name;
          if (name) refLabels.set(entry.field, name);
        }
      }
      // CAOS-1706 — "this record" is the record the button was pressed from, which on a header
      // button is the record on the page and on a related list's row button is that ROW's record
      // (`rowActions` reuses this same action vocabulary, and the rule written on it is that an
      // action applies to the row it is drawn on). Both arrive here the same way, so this reads
      // correctly in either placement without asking which one it is in.
      //
      // `onRecord` rather than `record`: a `create` offered with no record is already WITHHELD
      // above, and that binding is how the arms below carry the guard the compiler cannot. There
      // is no recordless path to attach from, so this needs no second answer for one.
      //
      // Still narrowed to a non-empty string instead of sent as-is. `data/create` accepts a string
      // or `null` and throws `validation.request` on any other non-string, and it treats an empty
      // string as absent. So a record whose id did not survive the projection as a string would
      // turn a declared attachment into a REJECTED create — a number or an object would throw —
      // while `undefined` here makes it a silent unattached create instead, which is exactly the
      // create that happened before this change. Degrading to the old behaviour beats failing the
      // write over an attachment the person never asked for by name.
      const id = (onRecord as Record<string, unknown>).id;
      const attachTo =
        action.attachToRecord && typeof id === 'string' && id.length > 0 ? id : undefined;
      openCreateForm(target, { ...ctx, perms: targetPerms }, { values, refLabels, attachTo });
    };
  } else {
    // CAOS-560 — the component kind. Every DECLARED requirement must be met; one that is not
    // means this person could not finish what the button starts.
    if (!action.requires.every((need) => requirementMet(need, object, ctx))) return WITHHELD;
    // CAOS-561 - a REPLACEMENT displaced the standard affordance, so it is the one case where a
    // piece that turns out to be undrawable has to reach back: `reload` re-draws the surface,
    // which asks `brokenReplacement` again and now gets the failure the ladder has just
    // remembered, so the verb comes back carrying the notice that says why. An ordinary button
    // displaced nothing and keeps CAOS-560's behaviour exactly - it says so in the overlay and
    // changes nothing else.
    const onUndrawable = action.replaces !== undefined ? reload : undefined;
    run = () => openComponentAction(action, object, record, ctx, onUndrawable);
  }
  return run === null ? WITHHELD : { status: 'offer', run };
}

export function recordActionBar(
  object: ObjectMeta,
  record: ApiRecord,
  refIndex: ReadonlyMap<string, { name: string; object: string }>,
  ctx: SurfaceContext,
  reload: () => void,
  /**
   * CAOS-368 — the record page's shared version cell, when there is one.
   *
   * A record page draws this bar AND its editable fields over the same record, and an inline edit
   * moves the version. Reading the version off `record` here instead would make this bar a second,
   * unshared snapshot: edit a field, then press a button, and the button states the version from
   * before that edit and is refused for a conflict with nobody but the same person's own save. So
   * the page hands its cell in, and the two stay in step.
   *
   * Defaulted, because this bar is also drawn on its own: a caller with no page cell gets the
   * record's own version, which is exactly what it had before this ticket.
   */
  version: RecordVersionHolder = { current: recordVersion(record) },
  /**
   * CAOS-407 — WHICH actions to draw, and where they are being drawn.
   *
   * Defaulted to the object's own header actions, which is what every caller before this ticket
   * means, so they are unchanged. A related list hands in the buttons its own declaration carries
   * and the row's record, and gets back the same buttons running the same code — the same
   * `appliesWhen`, the same editable-field check, the same `confirm`, the same version the button
   * was offered against, the same conflict refusal, the same message afterwards.
   *
   * That reuse is the whole point of the ticket rather than an implementation convenience. A second
   * runner written for rows is how `confirm` comes to mean "ask" in the header and "ask, unless the
   * row was filtered" on a row, and how one of the two quietly stops checking whether the person may
   * edit the field at all.
   */
  opts: { readonly actions?: readonly RecordActionMeta[]; readonly placement?: 'header' | 'row' } = {},
): HTMLElement | null {
  const perms = ctx.perms ?? NO_WRITE;
  const row = record as Record<string, unknown>;
  const bar = document.createElement('div');
  bar.className = opts.placement === 'row' ? 'sa-record-actions sa-record-actions--row' : 'sa-record-actions';
  // A row's buttons go in a table cell, which has no `actions` slot to be assigned to; a slot name
  // that names nothing on the host element hides the buttons entirely.
  if (opts.placement !== 'row') bar.slot = 'actions';

  // CAOS-561 - the button, lifted out so the verb replacement below and the ordinary actions draw
  // the identical control. Two builders is how a replacement ends up without the loading state
  // that stops a write being pressed twice.
  const button = (forAction: RecordActionMeta, run: () => void | Promise<void>): HTMLElement => {
    const btn = document.createElement('caos-button');
    btn.setAttribute('variant', actionVariant(forAction.intent));
    btn.dataset.actionId = forAction.id;
    btn.dataset.actionKind = forAction.kind;
    btn.textContent = forAction.label;
    btn.addEventListener('click', () => {
      const result = run();
      if (!(result instanceof Promise)) return;
      // A write in flight must not be pressed twice: the second press would be a second write.
      btn.setAttribute('loading', '');
      btn.setAttribute('disabled', '');
      void result.finally(() => {
        btn.removeAttribute('loading');
        btn.removeAttribute('disabled');
      });
    });
    return btn;
  };

  // CAOS-561 - Edit, when this object says its Edit means something of its own. It sits with the
  // record's other buttons because that is where a person looks for what they can do to a record,
  // and IN THE POSITION IT WAS AUTHORED IN rather than appended after everything: an author who put
  // it second meant it second, and a platform that quietly moved it would be one more thing to
  // discover. The fields it displaces stop being directly editable in the same paint - see
  // `detailFieldNode` - so the two halves of "replacing Edit" always agree.
  const edit = resolveStandardVerb('edit', object, record, refIndex, ctx, reload, mayEditRecord(ctx), version);

  for (const action of opts.actions ?? object.actions) {
    // An action that stands in for a standard verb is NOT an extra button. It is drawn AS the verb
    // or not at all; drawing it here as well would put two of it on the surface, which is the
    // "hide the standard one and add a second button beside it" shape this ticket exists to remove.
    //
    // A related list's buttons never reach this branch, and not by a check written here: `replaces`
    // is read from an OBJECT definition and from nowhere else, so an action a surface placed does
    // not carry one. That is enforced in `recordActionsFrom`, which takes the origin as an
    // argument precisely so it cannot be forgotten at a call site.
    if (action.replaces !== undefined) {
      if (edit.outcome === 'replaced' && edit.action.id === action.id) {
        const replacement = button(edit.action, edit.run);
        replacement.dataset['standardVerb'] = 'edit';
        bar.append(replacement);
      } else if (edit.outcome === 'fallback' && edit.action.id === action.id) {
        bar.append(standardFallbackNotice(edit));
      }
      continue;
    }
    const offer = actionOffer(action, object, record, refIndex, ctx, reload, version);
    if (offer.status !== 'offer') continue;
    bar.append(button(action, offer.run));
  }

  return bar.childElementCount > 0 ? bar : null;
}

/**
 * CAOS-503 — a tab pointing at an EXTERNAL ADDRESS. It always opens in a new browser tab, never in
 * place (the platform's content-security policy already refuses a frame from any provider — see
 * the `TabSchema` comment in `kernel/src/meta/metatypes.ts`): fires `window.open` on paint and ALSO
 * renders a real, always-present link, so a blocked pop-up (`window.open` returning `null` — the
 * one failure this seam can actually observe from here), a browser that throws on the call, and a
 * keyboard/screen-reader visit that never performs the synthetic click some browsers require to
 * treat the open as user-initiated, all leave something visible to act on rather than nothing. The
 * manual link is the real mechanism about half the time, not a defensive extra bolted on afterward.
 */
function renderExternalDestination(label: string, url: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'sa-surface sa-surface--external';

  let blocked = false;
  try {
    blocked = !window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    blocked = true;
  }

  const h = document.createElement('h1');
  h.className = 'sa-fallback-title';
  h.textContent = blocked ? `“${label}” could not open automatically` : `“${label}” opened in a new tab`;
  const p = document.createElement('p');
  p.className = 'sa-fallback-body';
  p.textContent = blocked
    ? 'Your browser blocked the pop-up. Open it directly:'
    : 'If a new tab did not appear, your browser may have blocked it. Open it directly:';
  const a = document.createElement('a');
  a.className = 'sa-link';
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.textContent = url;

  wrap.append(h, p, a);
  return wrap;
}

/** A loud, honest fallback node (missing record / no data) — never a blank region. Exported so
 *  callers outside this module's own route resolution (e.g. `main.ts`'s app-Settings route,
 *  CAOS-694/CAOS-342) can report the same "named a piece that isn't there" shape consistently. */
export function fallbackNode(title: string, body: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'sa-surface sa-surface--fallback';
  const h = document.createElement('h1');
  h.className = 'sa-fallback-title';
  h.textContent = title;
  const p = document.createElement('p');
  p.className = 'sa-fallback-body';
  p.textContent = body;
  wrap.append(h, p);
  return wrap;
}

/** The list page header (object plural label + optional row count) — reused by the loading/error states. */
function listHeader(object: ObjectMeta, ctx: SurfaceContext): HTMLElement {
  const header = document.createElement('caos-page-header');
  header.setAttribute('eyebrow', ctx.appLabel);
  header.setAttribute('heading', object.pluralLabel);
  return header;
}

/**
 * CAOS-331 — the transient LOADING state for a content surface. A list keeps its page header (so the
 * object identity is stable while the rows arrive) and shows an honest status line; a record shows a
 * status line. This is what the tab paints the instant it opens, before the Data API read settles —
 * never a blank content region.
 */
function renderContentLoading(object: ObjectMeta, ctx: SurfaceContext, kind: 'list' | 'record'): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = `sa-surface sa-surface--${kind} sa-surface--loading`;
  if (kind === 'list') wrap.append(listHeader(object, ctx));
  const line = document.createElement('p');
  line.className = 'sa-status-line';
  line.setAttribute('role', 'status');
  line.setAttribute('aria-live', 'polite');
  const what = kind === 'list' ? object.pluralLabel.toLowerCase() : object.label.toLowerCase();
  line.textContent = `Loading ${what}…`;
  wrap.append(line);
  return wrap;
}

/**
 * CAOS-331 — the ERROR state for a content surface: a distinct, honest failure line, NEVER a silent
 * empty table. A read that failed (transport/parse/permission) is not the same as an object with zero
 * rows; conflating them hides real breakage behind "No records yet".
 */
function renderContentError(object: ObjectMeta, ctx: SurfaceContext, kind: 'list' | 'record'): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = `sa-surface sa-surface--${kind} sa-surface--error`;
  if (kind === 'list') wrap.append(listHeader(object, ctx));
  const line = document.createElement('p');
  line.className = 'sa-status-line sa-status-line--error';
  line.setAttribute('role', 'alert');
  const what = kind === 'list' ? object.pluralLabel.toLowerCase() : object.label.toLowerCase();
  line.textContent = `Couldn't load ${what}. Check your connection and try again.`;
  wrap.append(line);
  return wrap;
}

/** The key under which a streaming content host carries its "populated" promise (callers/tests may await it). */
const CONTENT_SETTLED = '__caosContentSettled';

/**
 * Await a streaming content node's data population (loading → rows / record / error). Resolves
 * immediately for a static node (e.g. the record-not-found fallback), so callers can always await.
 */
export function whenContentSettled(node: Node | undefined): Promise<void> {
  const p = node && (node as unknown as Record<string, unknown>)[CONTENT_SETTLED];
  return p instanceof Promise ? (p as Promise<void>) : Promise.resolve();
}

/** The parsed route: which app/tab, and (for a record page) which record id. */
export interface ContentRoute {
  readonly appKey?: string;
  readonly tabKey?: string;
  readonly recordId?: string;
  /**
   * CAOS-1492 K7 — the first path segment, when it names an app this workspace does not have.
   * Present ⇒ the route resolved to NOTHING and the caller must say so; it is never set together
   * with `appKey`.
   */
  readonly unresolvedAppKey?: string;
}

/**
 * Parse `/sales/accounts/:id` into app/tab/record against the plan's installed apps.
 *
 * CAOS-1492 K7 — an unmatched first segment used to resolve to `apps[0]`, the first installed app.
 * A route naming an app that is not installed therefore opened a DIFFERENT app's landing, looking
 * for all the world like the destination had simply been reached; nothing anywhere reported that
 * the address had not resolved. That is the mechanism behind the pre-existing CAOS-358 symptom, and
 * it is what made a mis-addressed contribution (K7's claim-shape half) impossible to diagnose from
 * the screen — the wrong answer and the right answer look identical.
 *
 * An ABSENT first segment is a different thing, and CAOS-291 changes what it does. `/` is the
 * workspace root, and this used to resolve it to the FIRST INSTALLED APP — described here as "a real
 * default rather than a guess", which was fair as far as it went: the alternative on offer at the
 * time was guessing at an unmatched word, and array position at least never surprises.
 *
 * It was still the founder's original complaint, and it had outlived the fix for the other half of
 * it. CAOS-302 stopped the CHROME auto-selecting an app at root; the content region went on filling
 * that same home with the first app's landing, so the screen still opened on Sales Atlas and showed
 * its tabs. The chrome and the content disagreed about root, which is precisely the drift the three
 * resolvers are pinned together to prevent — the pin just did not cover the root path.
 *
 * Root now asks the ORG where it lands, through `plan.defaultApp` (`org.defaultApp`), and falls back
 * to the workspace home rather than to array position. "The first app in the list" was never a
 * designation anybody made; it was whichever package happened to sort first.
 *
 * ── How this sits with CAOS-291 ──────────────────────────────────────────────
 * The other two resolvers — `resolveActiveRoute` (shell-wiring.ts, the initial paint) and
 * `resolveFromPath` (shell-frame.ts, an in-place navigation) — have ALWAYS answered "no app" for an
 * unmatched segment, deliberately, so that a stale link lands on the workspace home rather than
 * fabricating a selection. That decision is unchanged and this change agrees with it: after this,
 * all three resolvers say the same thing about WHICH app, namely none.
 *
 * What changes is only who explains it. The CHROME still lands on home, exactly as CAOS-291 says it
 * should. The CONTENT region no longer fills that home with some other app's landing as though the
 * address had been reached — it says the address resolved to nothing, and names it. Landing
 * somewhere sensible and being told why are not in tension; before this, the person got the first
 * and never the second.
 */
export function parseContentRoute(pathname: string, plan: ShellFramePlan): ContentRoute {
  const apps = plan.injections['navigation-entries'] ?? [];
  if (apps.length === 0) return {};
  const segments = pathname.split('/').filter(Boolean);
  const requested = segments[0];
  const matched = requested ? apps.find((a) => a.sourceKey === requested) : undefined;
  if (requested && !matched) return { unresolvedAppKey: requested };
  if (!requested) {
    // Root: the org's designation, or the home. A designation naming an app this org does not have
    // is reported the same way an unmatched address is, rather than swallowed — an administrator
    // whose designated app has been uninstalled should be told that, and this is already the field
    // that means "the address resolved to nothing, and here is what it named".
    if (!plan.defaultApp) return {};
    const designated = apps.find((a) => a.sourceKey === plan.defaultApp);
    if (!designated) return { unresolvedAppKey: plan.defaultApp };
    return { appKey: designated.sourceKey };
  }
  const app = matched!;
  const tabs = app.tabs ?? [];
  // CAOS-1649 — an address naming an app and NO tab resolves the app's landing tab, through the one
  // function that decides which tab an app opens on ({@link landingTabKey}). `/feedback` is where
  // the Home screen's app card goes, so this is the ordinary way in, and it used to resolve to no
  // tab at all: the chrome highlighted the landing tab while the content region drew a bare object
  // roster of every field — describe's columns, with none of the authored list view's columns,
  // presentation, search, sort or filters. The two halves disagreed silently because each worked
  // the answer out for itself.
  //
  // ONLY WHEN THE SEGMENT IS ABSENT. An address that NAMES a tab this app does not carry still
  // resolves to no tab, exactly as before — falling back to the landing tab there would turn a
  // wrong address into a plausible-looking right one, which is the silent-resolution class the long
  // comment above this function exists to refuse. Absent is a question nobody asked; present and
  // unmatched is a question with a wrong answer, and they must not behave the same way.
  const tab = segments[1] === undefined ? tabs.find((t) => t.key === landingTabKey(app)) : tabs.find((t) => t.key === segments[1]);
  return { appKey: app.sourceKey, tabKey: tab?.key, recordId: tab ? segments[2] : undefined };
}

/**
 * CAOS-608 — where the Page Layouts editor lives, as the Setup package declares it.
 *
 * The host has to name this route because the host is what navigates to it, and the projection it
 * reads (`OrgMetadata`) carries pages but not roster pages, so there is nowhere to read the
 * package's own `detailRoute` from at runtime. It is therefore a constant that must AGREE with a
 * declaration in another repository — `src/rosterPages/page_layouts_list.json` in caos-pkg-setup,
 * whose `detailRoute` is `/setup/page-layouts/{id}`. A test pins it against the generated standard
 * registry, which carries that declaration, so the two cannot drift apart silently.
 */
export const PAGE_LAYOUTS_DETAIL_PATH = '/setup/page-layouts';

/**
 * CAOS-608 — the layout editor's address for the record page at `pathname`, or `undefined` when
 * there is nothing editable to open.
 *
 * "Edit this page" has been in the account menu since CAOS-581 and has never been offered: nothing
 * set `show-edit-page` and nothing listened for `topbar-edit-page`. This resolves the single fact
 * that decides both halves, so the entry appears exactly where it leads somewhere.
 *
 * Three conditions, each ruling out a different way the entry could lie:
 *
 *   - `recordId` is present, so this is a RECORD page rather than a list, an app landing or a page
 *     tab. Offered on a list, "Edit this page" would open a layout the person is not looking at.
 *   - the active tab names an OBJECT. A tab carrying a `page` or an external `url` has no layout
 *     behind it at all (the same two branches `resolveShellContent` checks before the record one).
 *   - that object has an AUTHORED layout. This is the condition that is easy to miss and the one
 *     that matters most: an object nobody has laid out still draws a perfectly good record page,
 *     from the sections `layout-definition.ts` synthesises for it — its own fields under "Details",
 *     the system fields under their own heading. That fallback is not a `layout` component and has
 *     no key (`layoutKey` is `undefined`, `layoutIsDefault` is true), and all three editing actions
 *     on the Page Layouts screen name their target through
 *     `paramsFrom: { apiName: 'objects.0.layoutKey' }`. Offering the entry there would open a
 *     screen whose every action cannot resolve a target — a control that appears to work and
 *     quietly does nothing, which is the exact class of control CAOS-581 removed this entry to
 *     avoid. Hiding it is the honest answer until somebody authors a layout.
 *
 * ON "THE LAYOUT ALREADY SELECTED", which the ticket asks for. The route is addressed by OBJECT
 * (`page_layouts_detail` declares `idParam: 'object'`), not by layout key. That is sufficient here
 * rather than a near miss: describe resolves exactly ONE layout per object
 * (`layout-definition.ts` returns a single `layoutKey`/`sections` pair per object, and
 * `recordTypes` comes back empty), and the detail page reads it at `objects.0`. So naming the
 * object names the layout, and there is no second layout for the screen to land on instead. If
 * record types ever select among several layouts for one object, this is the line that has to
 * change, and it will need the route to take a layout key.
 */
export function resolveEditPageHref(input: {
  readonly plan: ShellFramePlan;
  readonly meta: OrgMetadata;
  readonly pathname: string;
}): string | undefined {
  const route = parseContentRoute(input.pathname, input.plan);
  if (route.recordId === undefined) return undefined;
  const apps = input.plan.injections['navigation-entries'] ?? [];
  const tab = apps
    .find((a) => a.sourceKey === route.appKey)
    ?.tabs?.find((t) => t.key === route.tabKey);
  const objectKey = tab?.object;
  // ONE condition, written as two lines. A tab carrying a `page` or an external `url` names no
  // object, and an object this workspace has no metadata for has no layout either — both arrive
  // here as "no authored layout", because `objects.get(undefined)?.layoutKey` is undefined too.
  // The first line therefore narrows the type for the address below rather than adding a case, and
  // removing it changes no behaviour (only `tsc`). Said plainly because a reader who assumed the
  // two lines were independent guards would write a test for the first that cannot fail.
  if (objectKey === undefined) return undefined;
  if (input.meta.objects.get(objectKey)?.layoutKey === undefined) return undefined;
  return `${PAGE_LAYOUTS_DETAIL_PATH}/${encodeURIComponent(objectKey)}`;
}

/**
 * Resolve the content region for the current route: a record page, a list view, or `undefined` (the
 * app landing — the frame then renders its object-tab cards).
 *
 * CAOS-331 — for a list/record route this returns a content HOST **synchronously in a loading state**
 * and streams the real data in: the tab paints `Loading …` the instant it opens, then swaps in the
 * live rows / record once the same-origin Data API read settles, or a distinct ERROR state if the
 * read fails (never a silent empty table). The columns come from the list-view metadata, the rows
 * from the data plane (org_id + RLS enforced server-side). `whenContentSettled(node)` awaits the swap.
 */
export function resolveShellContent(input: {
  plan: ShellFramePlan;
  meta: OrgMetadata;
  pathname: string;
  navigate?: (href: string) => void;
  fetchImpl?: FetchImpl;
  /** CAOS-345 — the address's query string, which may name a saved list view. */
  search?: string;
  /**
   * CAOS-1624 — the system permissions the viewer holds, as `auth whoami` resolved them. Reaches
   * the surface context so a list view's `appliesWhen` filter can be scoped to them. Absent ⇒ not
   * known, which keeps every scoped filter applied.
   */
  heldPermissions?: readonly string[];
  /**
   * CAOS-1809 — who is reading, keyed as a placed component's inputs name them. Threaded from the
   * caller rather than read here for the same reason `heldPermissions` is: the session is resolved
   * once at boot, and a surface that re-derived it could disagree with the account block on screen.
   */
  viewer?: Readonly<Record<string, string>>;
  /** CAOS-438 — how an app landing page is drawn; see {@link resolveShellContentNode}. */
  appLanding?: (appKey: string) => Node;
}): Promise<Node | undefined> {
  return Promise.resolve(resolveShellContentNode(input));
}

/**
 * CAOS-346 — the announcement that a record is now OPEN. It lives in `open-record-event.ts` since
 * CAOS-695, beside the Setup pages that announce themselves the same way; re-exported here for the
 * callers that already read it from this module.
 */
export { OPEN_RECORD_EVENT };

/**
 * CAOS-345 — the saved view a route asks for, from `?view=`. Reads the live address when the caller
 * does not pass one, so an in-place navigation picks up the view without threading the query string
 * through every seam; tests pass it explicitly.
 */
function readViewParam(search?: string): string | undefined {
  const raw = search ?? (typeof location !== 'undefined' ? location.search : '');
  if (!raw) return undefined;
  try {
    return new URLSearchParams(raw).get('view') ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * CAOS-1861 — one of THIS person's saved views, projected into the same shape an installed one
 * arrives in.
 *
 * It goes through {@link projectOneListView} rather than reading the body directly, and that is the
 * whole point: a personal view and a shipped view are the same metatype, so the columns, the
 * presentation, the row buttons and every other reading of a `listView` body must be derived by the
 * same code or a personal view would quietly support a different subset of the vocabulary. The
 * alternative — a second, smaller projection for views that happen to be stored per person — is how
 * you end up with a feature that works on a shipped view and silently does nothing on a saved one.
 *
 * `source === 'personal'` is checked even though the installed set was already consulted: the route
 * answers shipped views too, and a shipped view that `meta.listViews` does not hold is a stale
 * metadata read, not something to draw from a different source behind the reader's back.
 */
async function personalListView(
  objectKey: string,
  key: string,
  fetchImpl?: typeof fetch,
): Promise<ListViewMeta | undefined> {
  const mine = (await listViewsFor(objectKey, fetchImpl)).find(
    (v) => v.key === key && v.source === 'personal',
  );
  if (!mine || mine.body == null || typeof mine.body !== 'object') return undefined;
  return projectOneListView(objectKey, mine.key, mine.body as Record<string, unknown>);
}

/** The synchronous core of {@link resolveShellContent} — builds the loading host + starts the stream. */
function resolveShellContentNode(input: {
  plan: ShellFramePlan;
  meta: OrgMetadata;
  pathname: string;
  navigate?: (href: string) => void;
  fetchImpl?: FetchImpl;
  /** CAOS-345 — the address's query string, which may name a saved list view. */
  search?: string;
  /**
   * CAOS-1624 — the system permissions the viewer holds, as `auth whoami` resolved them. Reaches
   * the surface context so a list view's `appliesWhen` filter can be scoped to them. Absent ⇒ not
   * known, which keeps every scoped filter applied.
   */
  heldPermissions?: readonly string[];
  /**
   * CAOS-1809 — who is reading, keyed as a placed component's inputs name them. Threaded from the
   * caller rather than read here for the same reason `heldPermissions` is: the session is resolved
   * once at boot, and a surface that re-derived it could disagree with the account block on screen.
   */
  viewer?: Readonly<Record<string, string>>;
  /**
   * CAOS-438 — how an APP LANDING page is drawn, given the app it was reached in.
   *
   * INJECTED rather than imported, and the reason is a cycle rather than taste: the surface that
   * draws it reads this module (`queryRecordsOutcome`, `recordDisplayName`), so importing it back
   * would put the two files in a loop whose behaviour depends on which one Node happens to
   * evaluate first. The composition root owns both and hands one to the other, which is the same
   * shape `fetchImpl` already has here.
   *
   * ABSENT ⇒ nothing draws it, and the tab says so in words. Deliberately a named refusal rather
   * than falling through to the record-type branch below: falling through would land an app
   * landing on "no content", which is the silent blank every branch around it exists to stop.
   */
  appLanding?: (appKey: string) => Node;
}): Node | undefined {
  const { plan, meta, pathname, navigate } = input;
  const fetchImpl = input.fetchImpl ?? fetch;
  const route = parseContentRoute(pathname, plan);
  // CAOS-1492 K7 — an address naming an app that is not installed SAYS SO, in the content region,
  // instead of quietly opening the first installed app. Checked before the landing branch below:
  // an unresolved route has no appKey, so it would otherwise be indistinguishable from `/`.
  if (route.unresolvedAppKey) {
    return fallbackNode(
      `There is nothing at “/${route.unresolvedAppKey}”`,
      `This workspace has no app called “${route.unresolvedAppKey}”. If you followed a link from a menu entry, the package that added it is pointing somewhere this workspace does not have — install it, or correct the entry's destination.`,
    );
  }
  if (!route.appKey || !route.tabKey) return undefined; // app landing → frame's tab cards

  const apps = plan.injections['navigation-entries'] ?? [];
  const app = apps.find((a) => a.sourceKey === route.appKey);
  const tab = app?.tabs?.find((t) => t.key === route.tabKey);
  const objectKey = tab?.object;

  // CAOS-503 — a tab points at a record type's list, a PAGE, or an EXTERNAL ADDRESS. The url half
  // is checked first, for the identical reason CAOS-388 checks the page half before the record-type
  // branch below: a tab holding a url carries neither a record type nor a page, and would otherwise
  // fall through all the way to "no content" — an authored, installed destination arriving at
  // nothing, which is precisely what AC4 of CAOS-503 rules out.
  if (!objectKey && !tab?.page && !tab?.rosterPage && !tab?.rosterDetailPage && tab?.url) {
    return renderExternalDestination(tab.label, tab.url);
  }

  // CAOS-1683 — a ROSTER destination, checked for the same reason the url half is checked above and
  // the page half below: a tab holding a roster carries neither a record type nor a page, so without
  // this branch it falls through every one of them to 'no content'. A roster could be authored,
  // installed, and unreachable all at once - which is the exact sentence CAOS-388 wrote about pages
  // one branch down, and the condition it describes was true of rosters until this ticket.
  //
  // The arm is trusted rather than re-derived. The kernel refuses a tab whose declared arm disagrees
  // with the target's real kind (`validation.tab_target_kind`), and refuses a detail-page target that
  // is addressed by a record (`validation.tab_target_addressed_detail_page`), so an installed tab's arm
  // is already the kind and a singleton is already the only detail page that can be here.
  if (!objectKey && (tab?.rosterPage || tab?.rosterDetailPage)) {
    const kind = tab.rosterPage ? 'rosterPage' : 'rosterDetailPage';
    const key = tab.rosterPage ?? tab.rosterDetailPage!;
    // CAOS-1764 — an ADDRESSED row under an app tab's roster: `/<app>/<tab>/<id>`.
    //
    // `recordId` was already parsed for an app tab and then read by nothing on this path, so a
    // roster's `detailRoute` could be authored, installed, and arrive back at the list it came from
    // — the same "authored, installed and unreachable" shape the two branches around this one were
    // written against, one level deeper. The Setup console has answered this since CAOS-1567
    // (`contributedRecord`); this is that answer at an app tab, reading the SAME map, so the two
    // consoles resolve a row's record page by one rule rather than two.
    //
    // The detail page is found by the address rather than named here: an addressed `rosterDetailPage`
    // declares the list it hangs under as `breadcrumbRootHref`, which the schema requires whenever
    // `idParam` is set. So neither component key appears in host code, and a contributed roster's
    // rows open with nothing here knowing either one.
    if (route.recordId !== undefined && tab.rosterPage) {
      const listHref = `/${route.appKey}/${route.tabKey}`;
      const claimed = meta.rosterDetailPagesByListHref.get(listHref) ?? [];
      // Each way of having no answer says which it is, because they call for different fixes —
      // nothing claims the address, or several do and the package has to say which is meant. The
      // wording follows `contributedRecord`'s for the same two cases.
      if (claimed.length === 0) {
        return fallbackNode(
          'Page not found',
          `This list has no record screen, so there is nothing at “${listHref}/${route.recordId}”. ` +
            'A list opens its rows when the package also ships a record screen that names the list.',
        );
      }
      if (claimed.length > 1) {
        return fallbackNode(
          'More than one record screen',
          `${claimed.length} record screens name “${listHref}” as the list they belong to, so which ` +
            'one this address opens is not decided. The package that ships them has to say.',
        );
      }
      return renderRosterDestination('rosterDetailPage', claimed[0]!, tab.label, {
        navigate,
        fetch: fetchImpl,
        renderFallback: fallbackNode,
        recordId: route.recordId,
      }).root;
    }
    // `fallbackNode` is passed in rather than imported there, so an absent roster reads exactly as
    // an absent page or record type does in the two branches around this one.
    return renderRosterDestination(kind, key, tab.label, {
      navigate,
      fetch: fetchImpl,
      renderFallback: fallbackNode,
    }).root;
  }

  // CAOS-388 — a tab points at a record type's list OR at a PAGE. The page half arrives here now,
  // and the screen is drawn from what the page says it contains. It is deliberately checked BEFORE
  // the record-type branch: a tab holding a page carries no record type, and every such tab used to
  // fall through to "no content", which is how a page could be authored, installed and unreachable
  // all at once.
  if (!objectKey && tab?.page) {
    const page = meta.pages.get(tab.page);
    if (!page) {
      return fallbackNode(
        `“${tab.label}” has nothing behind it`,
        `This tab opens a page called “${tab.page}”, and this workspace does not have it. Install the package that provides it, or point the tab somewhere else.`,
      );
    }
    // CAOS-438 — an APP LANDING is not assembled from what the page places; it is derived from the
    // application this address is in. So it is answered here, before the placement renderer, and it
    // is handed the app key from the ROUTE. The page named no app and cannot: that is what keeps
    // one application's landing from being authored to report another's work.
    if (page.kind === 'appLanding') {
      if (!input.appLanding) {
        return fallbackNode(
          `“${tab.label}” cannot be drawn here`,
          `This tab opens “${page.label}”, which is an app landing — a surface built from what this app contains rather than from anything written on the page. Nothing in this workspace is set up to draw one, so there is nothing to show. That is a wiring fault rather than something an administrator can correct from Setup.`,
        );
      }
      return input.appLanding(route.appKey);
    }
    // CAOS-1616 — a roster row action may OPEN a record, and the address it opens at is a fact
    // about the whole installed plan. Built from the same two values the surface context below uses
    // (this app's tabs as the fallback, every app's authored homes winning) so the two cannot give
    // one answer here and a different one there.
    // CAOS-1767 — `renderPage` and not `renderPageSurface`, like every other caller: it is the one
    // place the list drawing is supplied, and a tab may point at a `list` page as readily as at any
    // other kind.
    return renderPage(page, {
      meta,
      appLabel: app?.label ?? route.appKey,
      pathname,
      objectHomes: objectHomeMap(app?.tabs ?? [], route.appKey, recordHomesOf(apps)),
      // CAOS-1809 — a page placing a piece that declares `source: "userContext"` can now be told
      // who is reading it. This is the path a tab-bound page takes, so it is the one that matters
      // most; the Setup consoles below pass the same value off their own surface context.
      ...(input.viewer ? { viewer: input.viewer } : {}),
    });
  }

  if (!objectKey) return undefined;
  const object = meta.objects.get(objectKey);
  // CAOS-388 — a tab pointing at a record type this workspace has not got used to fall through to
  // the app's landing cards with nothing said, which is the same silent blank the page branch above
  // was just fixed for. If the rule is that nothing is quietly empty, it applies here too: the tab
  // names what is missing, exactly as the missing-page case does one branch up.
  if (!object) {
    return fallbackNode(
      `“${tab?.label ?? objectKey}” has nothing behind it`,
      `This tab opens a record type called “${objectKey}”, and this workspace does not have it. ` +
        'Install the package that provides it, or point the tab somewhere else.',
    );
  }

  // CAOS-345 — which saved view are we looking at? A `view` in the address wins (that is what the
  // list-views menu links to); otherwise the tab's declared default opens. A name that matches
  // nothing falls back to the object's own columns rather than showing an empty table.
  const askedView = readViewParam(input.search);
  const objectViews = meta.listViews.get(objectKey) ?? [];
  const installedView =
    objectViews.find((v) => v.key === askedView) ??
    objectViews.find((v) => v.key === tab?.defaultListView);
  const listView = installedView;

  const ctx: SurfaceContext = {
    meta,
    appKey: route.appKey,
    appLabel: app?.label ?? route.appKey,
    tabKey: route.tabKey,
    tabs: app?.tabs ?? [],
    // Built from EVERY installed app, not from `app` above: an authored home is a statement about
    // where an object's records live full stop, not about where they live when the reader happens
    // to be in the app that declared it.
    recordHomes: recordHomesOf(apps),
    navigate,
    fetchImpl,
    pathname,
    ...(listView ? { listView } : {}),
    ...(input.heldPermissions ? { heldPermissions: input.heldPermissions } : {}),
    ...(input.viewer ? { viewer: input.viewer } : {}),
  };

  // A thin content HOST that mounts a loading state immediately, then swaps in the resolved surface
  // (record / list / not-found) or an honest error once the Data API read settles. Mutating this live
  // node in place is safe: the shell frame appends the content region verbatim.
  const kind: 'list' | 'record' = route.recordId ? 'record' : 'list';
  const host = document.createElement('div');
  host.className = 'sa-content-host';
  host.append(renderContentLoading(object, ctx, kind));

  const settled = (async (): Promise<void> => {
    try {
      if (route.recordId) {
        // CAOS-560 — this object's permissions AND those of every other object its actions reach
        // (a create's target, a component action's declared needs), all in one round. They are
        // needed BEFORE the surface renders because the action bar is built synchronously: asking
        // afterwards would mean drawing a button and then taking it away, which is a worse answer
        // than never offering it. `objectsActionsReach` is usually empty, and then this is exactly
        // the read it always was.
        const reached = objectsActionsReach(object);
        const [outcome, refIndex, perms, reachedPerms] = await Promise.all([
          getRecordOutcome(objectKey, route.recordId, fetchImpl),
          buildReferenceIndex(object, meta.objects, fetchImpl),
          loadObjectPermissions(objectKey, fetchImpl),
          Promise.all(reached.map((key) => loadObjectPermissions(key, fetchImpl))),
        ]);
        const actionPerms = new Map(reached.map((key, i) => [key, reachedPerms[i]!] as const));
        const recordCtx: SurfaceContext = { ...ctx, perms, actionPerms };
        if (outcome.status === 'error') {
          host.replaceChildren(renderContentError(object, ctx, 'record'));
          return;
        }
        if (outcome.record === null) {
          host.replaceChildren(
            fallbackNode('Record not found', `This ${object.label.toLowerCase()} could not be opened. It may have been removed.`),
          );
          return;
        }
        // CAOS-346 — the record is genuinely open now (read, allowed, found), so it earns a place
        // in the open-records strip. Announced only on success: a record that failed to load or does
        // not exist must never leave a tab behind pointing at nothing.
        announceOpenRecord({
          href: recordAddress({ appKey: ctx.appKey, tabKey: ctx.tabKey }, String(outcome.record.id)),
          label: `${object.tabCode ?? fallbackTabCode(object.label)} · ${recordTabIdentifier(object, outcome.record)}`,
        });
        // The attached sets are read only once the record is known to exist and be readable: there
        // is nothing to attach them to otherwise, and asking would spend reads on a page that is
        // about to say it could not find anything.
        const [related, activity, tags, history] = await Promise.all([
          loadRelatedLists(object, outcome.record, meta.objects, ctx, fetchImpl),
          // CAOS-367 — read with the attached sets, not after them: both are about this one record
          // and neither depends on the other, so a serial read would just make the page slower.
          loadActivityTimeline(object.key, String(outcome.record.id), fetchImpl),
          // CAOS-366 — the labels on this record, read the same way and for the same reason.
          loadRecordTags(object.key, String(outcome.record.id), fetchImpl),
          // CAOS-402 — and the field-change history, in the same breath as the three above. This is
          // the read that makes the History tab appear at all, and it is UNCONDITIONAL: AC2 asks for
          // nothing to be configured for this to be true of a brand-new object, so every record page
          // asks for its history and no object, package or administrator opts in.
          loadRecordHistory(object.key, String(outcome.record.id), fetchImpl),
        ]);
        host.replaceChildren(
          renderRecordSurface(object, outcome.record, refIndex, recordCtx, related, activity, tags, history),
        );
        return;
      }
      /*
       * CAOS-1861 — a view the INSTALLED set does not hold may still be one this person made.
       *
       * `meta.listViews` is the package set, and until this ticket it was the only set: a person's
       * own views were unreachable from the screen even though the Engine has offered them since
       * CAOS-1579. So an address naming a view nobody shipped fell through to the object's own
       * columns, which is exactly what somebody would see the instant after creating a view and
       * being sent to it.
       *
       * IT IS RESOLVED HERE, not beside `installedView` above, and the reason is this function's
       * contract rather than tidiness: `resolveShellContentNode` is SYNCHRONOUS so that a tab
       * paints `Loading …` the moment it opens, and an `await` up there would hold the first paint
       * of every list screen on the platform behind a read that serves one case. Down here the
       * screen is already up and the await costs what the record read already costs.
       *
       * LAZILY, AND ONLY THEN. The read happens when the address names a view AND the installed set
       * does not hold it — so the common cases cost nothing at all: no `view` parameter, or one
       * naming a shipped view. It also lands BEFORE the record query on purpose, because a view
       * narrows and orders that query: resolving it afterwards would mean reading every record and
       * then filtering a view's worth out of them in the browser.
       *
       * Fail-soft: `listViewsFor` answers `[]` on any trouble and a body the projection refuses
       * yields `undefined`, both of which land on the fallback a misspelt view key has always
       * landed on — the object's own columns. A list screen that could not resolve its view is
       * still a list screen.
       */
      const chosenView = listView ?? (askedView ? await personalListView(objectKey, askedView, fetchImpl) : undefined);
      const listCtx: SurfaceContext = { ...ctx, ...(chosenView ? { listView: chosenView } : {}) };
      // CAOS-349 — a chosen saved view narrows and orders the read itself, so what you see is what
      // the view says it is (not every record under a narrowed heading).
      const [outcome, refIndex, perms] = await Promise.all([
        queryRecordsOutcome(objectKey, fetchImpl, 200, {
          ...(chosenView?.filters ? { filters: chosenView.filters } : {}),
          ...(chosenView?.sort ? { sort: chosenView.sort } : {}),
        }),
        buildReferenceIndex(object, meta.objects, fetchImpl),
        loadObjectPermissions(objectKey, fetchImpl),
      ]);
      if (outcome.status === 'error') {
        host.replaceChildren(renderContentError(object, listCtx, 'list'));
        return;
      }
      host.replaceChildren(
        renderListSurface(object, outcome.records, refIndex, { ...listCtx, perms }, outcome.nextCursor),
      );
    } catch {
      host.replaceChildren(renderContentError(object, ctx, kind));
    }
  })();
  Object.defineProperty(host, CONTENT_SETTLED, { value: settled, enumerable: false });
  return host;
}

/**
 * CAOS-1767 — a `list`-kind page: MANY records of one object, drawn through that object's own list
 * view.
 *
 * ── WHY THIS LIVES IN THIS FILE, which is already long ───────────────────────────────────────────
 * It was a module of its own for one draft, `surfaces/list-page-surface.ts`, and the full host suite
 * refused it — correctly. The list surface lives here, and this file already imports
 * `renderPageSurface`, so a separate module importing both would have put `page-surface.ts` and this
 * one in a cycle; the draft dodged that with a lazy `import()` inside `page-surface.ts`, and
 * `caos-757-component-code-trust-boundary` failed with "EXACTLY ONE module performs a dynamic
 * import, and it is the component loader".
 *
 * That guard is not incidental tidiness, and its own docblock says what a failure means: the trust
 * boundary CAOS-757 watches has moved, and the decision it defers — trusted origins, an allowlist,
 * subresource integrity — is now due. Spending a security boundary to avoid an import cycle is a bad
 * trade twice over. The structurally right answer is to lift {@link renderListSurface} and its dozen
 * local helpers out of this file into a list module of their own, which is several hundred lines of
 * movement and a different change from this one. Until that happens the list drawing belongs beside
 * the list surface, and that is here.
 *
 * ── NOTHING HERE IS SILENTLY EMPTY ───────────────────────────────────────────────────────────────
 * The page-surface rule applies unchanged, and the states are genuinely different statements about
 * the page rather than one shrug worded four ways:
 *
 *   • the page names no object                     — it is not a list page the renderer can draw;
 *   • the object is not installed here             — names it, and says the page is ahead of what
 *                                                    this workspace holds;
 *   • the saved view it opens on is not installed  — names the VIEW, not the object, because the
 *                                                    object is fine and one of its views is missing;
 *   • the records could not be read                — says so, and is not "there are none".
 *
 * The fourth is the one worth being careful about: a read that failed and an object with no records
 * look identical on screen unless something insists otherwise. {@link queryRecordsOutcome} exists to
 * tell those apart (CAOS-363's reasoning, one level down), and this is the surface honouring it. An
 * object that genuinely holds nothing falls through to the list surface's own empty wording, which is
 * worded for WHY it is empty.
 */

/**
 * How many rows one read brings back. The same 200 the app-tab list reads, deliberately: the list
 * surface's own controls re-read the Engine rather than filtering what is on screen, and they ask for
 * this many too. A different number here would make the same list behave differently depending on
 * which door it was reached through.
 */
const LIST_PAGE_SIZE = 200;

/**
 * Draw a `list`-kind page's body.
 *
 * Returns the node to place in the page body. It is `async` because the records are read before
 * anything can be drawn — the list surface takes the rows it is to show rather than fetching them —
 * so {@link renderPageSurface} stamps the returned promise where {@link whenContentSettled} can
 * await it.
 */
export const drawListPage: NonNullable<PageRenderOptions['listDraw']> = async (page, options) => {
  const objectKey = page.listObject;
  if (objectKey === undefined || objectKey === '') {
    // Unreachable through a deploy — the schema requires `object` on a `list` data source — and drawn
    // anyway, because the body this renderer is handed can also come from an org whose metadata was
    // installed by an older kernel. Says which of the two it is rather than drawing an empty table,
    // which would read as "this object has no records".
    return pageNotice(
      `“${page.label}” does not say what it lists`,
      `“${page.label}” shows a list of records, but does not name the kind of record it lists. ` +
        'Name one on the page, and the list will appear here.',
    );
  }

  const object = options.meta.objects.get(objectKey);
  if (object === undefined) {
    return pageNotice(
      `“${page.label}” lists something this workspace has not got`,
      `“${page.label}” shows a list of “${objectKey}”, which is not installed here. ` +
        'Install the app that provides it, and this screen will fill in.',
    );
  }

  // The saved view, when the page names one. NAMED-AND-MISSING is its own state and not a fallback:
  // falling back to the object's default columns would draw a list that looks finished and is not the
  // one the page asked for, with nothing on screen saying the view had gone.
  const installedViews = options.meta.listViews.get(objectKey) ?? [];
  const listView = page.listView === undefined ? undefined : installedViews.find((v) => v.key === page.listView);
  if (page.listView !== undefined && listView === undefined) {
    return pageNotice(
      `“${page.label}” opens on a list this workspace has not got`,
      `“${page.label}” shows “${object.pluralLabel}” through the saved list “${page.listView}”, ` +
        'which is not installed here. Install the app that provides it, or point the screen at a ' +
        'list this workspace holds.',
    );
  }

  /*
   * WHERE A ROW LEADS, and why it is resolved from the AUTHORED record homes and nothing else.
   *
   * `renderListSurface` builds a row's address from the app and tab it is being drawn in, and a page
   * is drawn in neither — a Setup destination has no tab, and the same page can be reached from more
   * than one place. CAOS-1586 built the answer to exactly this: a tab declares itself an object's
   * `recordHome`, and the host resolves a record's address from that map wherever it is standing.
   * `PageRenderOptions.objectHomes` is that map, already injected for a roster row action's
   * `destination` (CAOS-1616), so this needs no new seam and no second opinion about addresses.
   *
   * Absent is a real answer: an object with no authored home has no address reachable from here, and
   * every other caller renders that as a NAME WITHOUT A LINK rather than a link that leads nowhere
   * ({@link recordDestination}'s own note). The list surface already does precisely that — its
   * `hrefOf` returns no address when there is no tab key — so passing the empty destination through
   * gets the honest rendering for free rather than by a rule written again here.
   *
   * The tab-guess half of `recordDestination` is deliberately given nothing to work with (`tabs: []`
   * and an empty `appKey`): guessing an address from the tabs of the app a page happens to be drawn
   * inside is how a Setup screen would come to link into Sales. Only an authored home counts here.
   */
  const home = recordDestination(objectKey, { appKey: '', tabs: [] }, options.objectHomes);

  const fetchImpl = options.listFetch ?? fetch;
  const [outcome, refIndex, perms] = await Promise.all([
    queryRecordsOutcome(objectKey, fetchImpl, LIST_PAGE_SIZE, {
      ...(listView?.filters ? { filters: listView.filters } : {}),
      ...(listView?.sort ? { sort: listView.sort } : {}),
    }),
    buildReferenceIndex(object, options.meta.objects, fetchImpl),
    loadObjectPermissions(objectKey, fetchImpl),
  ]);

  if (outcome.status !== 'ok') {
    return pageNotice(
      `Couldn’t load “${page.label}”`,
      `Those ${object.pluralLabel.toLowerCase()} could not be read just now. Check your connection ` +
        'and try again.',
    );
  }

  const ctx: SurfaceContext = {
    meta: options.meta,
    appKey: home?.appKey ?? '',
    appLabel: options.appLabel ?? '',
    tabKey: home?.tabKey ?? '',
    tabs: [],
    ...(options.objectHomes ? { recordHomes: options.objectHomes } : {}),
    ...(listView ? { listView } : {}),
    // A PAGE HAS NO LIST ADDRESS, so it must not offer the saved-view chooser — picking a view is a
    // navigation to `listAddress`, and the `appKey`/`tabKey` above are the listed object's RECORD
    // home rather than this surface's own address. Said explicitly rather than inferred from those
    // two fields; see {@link SurfaceContext.savedViewsWithheld} for the defect that taught the
    // difference.
    savedViewsWithheld: true,
    // No `navigate` seam is passed, because `PageRenderOptions` has none to pass and inventing one
    // here would be a second router. The list surface already handles its absence: it assigns
    // `location` instead, which is an ordinary full navigation to an ordinary address.
    ...(options.pathname ? { pathname: options.pathname } : {}),
    perms,
    fetchImpl,
  };

  return renderListSurface(object, outcome.records, refIndex, ctx, outcome.nextCursor);
};

/**
 * CAOS-1767 — DRAW A PAGE, whatever kind it is. This is what a surface should call.
 *
 * {@link renderPageSurface} draws every kind except a list, and takes the list drawing as a seam
 * ({@link PageRenderOptions.listDraw}) rather than importing it — `page-surface.ts` cannot reach this
 * file, which already imports it. This wrapper is the ONE place the real drawing is supplied, so no
 * caller holds a second description of how a list page is drawn and none of them has to know a list
 * page exists.
 *
 * A caller that reaches `renderPageSurface` directly with a list page does NOT draw a list — it draws
 * a notice saying this screen does not draw one, the same shape the `intake` branch has always had.
 * That is deliberately a named, searchable sentence rather than a silent fallback, because the
 * alternative is the page being told it "contains nothing" while declaring a complete list, which is
 * the exact false statement CAOS-388 added that notice to stop.
 *
 * `options.listDraw` still wins when it is supplied, which is how a test substitutes the drawing.
 */
/**
 * CAOS-492 — draw a ROSTER embedded in a page's section.
 *
 * THE SAME RENDERER THE OTHER THREE ROUTES USE, reached through `renderRosterDestination`. That
 * module's own opening note is the reason: it was written to be "deliberately thin" because
 * "nothing about how a roster draws is re-decided here, because a second implementation of that is
 * how two routes to one surface start disagreeing." An embed is the fourth route to a roster, and it
 * gets the same treatment — including the absent-component sentence, which is answered there in the
 * same words a missing page or record type is answered in (CAOS-388 / CAOS-503).
 *
 * `.root` is taken and `.done` is DISCARDED, exactly as the `rosterPage` tab branch does: the node
 * is returned synchronously and fills itself in, and that module is explicit that nothing in it may
 * escape, so the promise carries no failure a caller could act on.
 *
 * NAVIGATION FALLS TO A FULL PAGE LOAD, which is the incumbent answer rather than a gap. A roster
 * row opens an address, and this seam is handed no router — `PageRenderOptions` carries none, by
 * design, because a page renderer is not a router. {@link drawListPage} above records the same
 * decision for the same reason: "the list surface already handles its absence: it assigns `location`
 * instead, which is an ordinary full navigation to an ordinary address."
 */
export const drawEmbeddedRoster: NonNullable<PageRenderOptions['embedRosterDraw']> = (rosterPageKey) =>
  renderRosterDestination('rosterPage', rosterPageKey, rosterPageKey, {
    navigate: (href) => {
      if (typeof location !== 'undefined') location.assign(href);
    },
    renderFallback: fallbackNode,
  }).root;

export function renderPage(page: PageMeta, options: PageRenderOptions): HTMLElement {
  return renderPageSurface(page, { listDraw: drawListPage, embedRosterDraw: drawEmbeddedRoster, ...options });
}
